import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { salesOrderPalletLink } from "../utils/scanCodes";
import config from "../config.js";
import LeadTimeSelector from "../components/LeadTimeSelector.jsx";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { OverlayTrigger, Tooltip as RbTooltip } from "react-bootstrap";
import pinPrompt from '../utils/pinPrompt';
import { emailSenderHeaders } from '../utils/emailSender';
import openPdf from '../utils/openPdf';
import SurplusAllocationModal from '../components/SurplusAllocationModal.jsx';
import SurplusRackMatchBanner from '../components/SurplusRackMatchBanner.jsx';
import { desktopUi } from '../features/desktopUi';
import { shopToday } from '../utils/shopDate';
import {
  loadSalesOrders, listRiskLevel, customersFromOrders, stagesFromOrders, filterOrders,
  loadOrderTruth, loadOrderExtras,
  QUICK_EDIT_FAILURE, promisedPayload, promisedEditStart, propagationNote, saveOrderFields, shipmentBatches, batchManifestPath,
  loadActionItems, AC_SECTION_META, AC_LATE_VERDICT, lateOnHandText, latePoText, activeDefectsByPart, defectsByLine, shippedTotals,
} from '../features/salesOrders/soData';
import { outsideChip } from '../features/salesOrders/lineSteps';
import { useLineSteps } from '../features/salesOrders/useLineSteps';
import { overCoverageText, surplusByLastLine } from '../features/salesOrders/surplus';
import PodPanel from '../components/PodPanel.jsx';
import { ProductIdLink, CatalogItemLink, CatalogItemDetailModal } from '../components/PartDetailModal.jsx';
import RoutingStrip from '../components/RoutingStrip.jsx';
import CutSheetSwitchModal from '../components/CutSheetSwitchModal.jsx';
import { canOfferCutSheetSwitch } from '../features/jobs/cutSheetSwitch';
import { effectiveDeliveryRisk } from '../features/salesOrders/deliveryRisk';
import { groupJobTasks, assemblyInfoByPart, soLinesByPart, setLabel, orderScope, scopeJobToOrder } from '../utils/groupJobTasks';
import { detailToString } from '../utils/apiError';
import { fmtMoney as fmtMoneyShared, fmtPct, fmtDelta } from '../utils/priceMath';
import {
  lineFinancials,
  linesTotals,
  groupFinancials,
  headerTotals,
  summaryBases,
  accruedFinancials,
  estimateSplitRows,
  notYetRealizableRows,
  priceSourceMeta,
  LOCKED_PRICE_TOOLTIP,
  lineEstimateRows,
  fmtUnitCost,
  engineCallsSummary,
  coatingRowSplit,
  stationRowsForTable,
  soDeletedAlertText,
} from '../utils/salesOrderMath';
// Time & Materials (slice 1d): the logic lives in these files; this page only wires them in.
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import TmPricingModal from '../components/tm/TmPricingModal.jsx';
import TmReopenDialog from '../components/tm/TmReopenDialog.jsx';
import {
  TmSoBanner, TmSoStatusBadge, TmOrderChip, TmProductionNote, TmLinesLockNote, TmNewLinePrice,
} from '../components/tm/TmSalesOrderParts.jsx';
import { tmReadyToPriceSection, TmPendingSummaryLine } from '../components/tm/tmActionCenter.jsx';
import { isTm, isTmPending, isTmFinal, soInvoicePayload, tmInvoiceGate } from '../utils/tmPricing';
import TmDefectsPanel, { useTmDefects, TmDecisionChip } from '../components/tm/TmDefectsPanel.jsx';
import { decisionsByDefectId, jobNumsFrom } from '../utils/tmDefects';
import SOPackagingPlanCard from '../features/packageGuide/PackageGuideCard.jsx';
import { allLinesShipped, loadSurplusJobs, voidSurplusJobs } from '../features/salesOrders/voidSurplus';

/**
 * --------------------
 * Shared helpers
 * --------------------
 */

// Safety helper to prevent .toFixed crashes on null/undefined/NaN
const safeNum = (val) => {
  const n = parseFloat(val);
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (val) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(safeNum(val));

// Tooltip wrapper for the Production Financial Summary card.
//
// Why this exists: the old pattern (data-bs-toggle="tooltip" data-bs-html="true"
// title="<b>…</b><br/>…") relied on a useEffect calling new bootstrap.Tooltip(el)
// after summaryData landed. Two bugs collided to print literal "<br/>" text in
// the cost tooltips (caught 2026-06-06 on SO-1080):
//   1. Bootstrap moves `title` → `data-bs-original-title` on init and reads from
//      that on show. When React re-renders with a new `title` prop (e.g. when
//      live material cost climbs from $680 → $906 → $2,947 as sheets cut), the
//      old `data-bs-original-title` keeps showing AND the new `title` re-arms
//      the browser's native tooltip — which strips HTML, so `<br/>` prints as
//      text.
//   2. The init useEffect only re-fired on [summaryData]. Other state changes
//      that re-render tooltip elements (yieldPolicies arriving, conditional
//      rows toggling on/off) created tooltip-marker elements that never got
//      bound to a Tooltip instance → native fallback again.
// react-bootstrap's OverlayTrigger sidesteps both: the tooltip body is JSX
// (no HTML strings, no attribute round-trip), it re-renders with the parent,
// and it disposes cleanly when the child unmounts.
const InfoTooltip = ({ id, content, placement = "top", children }) => (
  <OverlayTrigger placement={placement} overlay={<RbTooltip id={id}>{content}</RbTooltip>}>
    {children}
  </OverlayTrigger>
);

// Flatten grouped truth lines from /sales_orders/{id}/truth into actual line objects
const flattenTruthLines = (truthLines) => {
  const out = [];
  (truthLines || []).forEach((entry) => {
    if (!entry) return;

    if (entry.type === "assembly_group") {
      (entry.components || []).forEach((c) => c && out.push(c));
      return;
    }

    if (entry.type === "item" && entry.data) {
      out.push(entry.data);
      return;
    }

    if (entry.sales_order_line_id) {
      out.push(entry);
    }
  });
  return out;
};

// Normalize consumable health items
const normalizeIssues = (issues) => {
  const raw = Array.isArray(issues) ? issues : [];
  return raw
    .map((issue) => ({
      ...issue,
      consumable_id: issue.consumable_id ?? null,
      name: issue.name ?? issue.cons_name ?? "N/A",
      needed: safeNum(issue.needed ?? issue.needed_for_order ?? 0),
      on_hand: safeNum(issue.on_hand ?? 0),
      inbound_qty: safeNum(issue.inbound_qty ?? 0),
      status: issue.status ?? "OK",
    }))
    .filter((i) => i.status === "Critical" || i.status === "On Order");
};

const issueKey = (i) =>
  `${i?.consumable_id ?? "na"}|${i?.status ?? "na"}|${safeNum(i?.needed)}|${safeNum(i?.on_hand)}|${safeNum(i?.inbound_qty)}`;

const dedupeIssues = (issues) => {
  const seen = new Set();
  const out = [];
  (issues || []).forEach((i) => {
    const k = issueKey(i);
    if (seen.has(k)) return;
    seen.add(k);
    out.push(i);
  });
  return out;
};

const LifecycleBadge = ({ stage, percent, productionProgress, totalShipped, totalOrdered }) => {
  const configMap = {
    "Draft":             { color: "bg-secondary", icon: "bi-file-earmark-plus", showBar: false },
    "Released":          { color: "bg-primary",   icon: "bi-clipboard-check",   showBar: true },
    "In Production":     { color: "bg-warning text-dark", icon: "bi-gear-wide-connected", showBar: true },
    "Needs More Jobs":   { color: "bg-danger",            icon: "bi-exclamation-triangle-fill", showBar: true },
    "Production Done":   { color: "bg-info text-dark",    icon: "bi-box-seam",          showBar: true },
    "Partially Shipped": { color: "bg-warning text-dark", icon: "bi-truck",             showBar: true },
    "Shipped":           { color: "text-white",    icon: "bi-check-circle",      showBar: true, customBg: "#7952b3" },
    "Invoiced":          { color: "bg-success",   icon: "bi-receipt",           showBar: true },
    "Completed":         { color: "bg-dark",      icon: "bi-check-circle-fill", showBar: true }
  };

  const config = configMap[stage] || configMap["Draft"];

  // Production progress bar: real task completion for production stages,
  // 100% for closed-out stages (shipped/invoiced/completed). Partially
  // Shipped deliberately keeps the real production bar — forcing it to
  // 100% hid the fact that more work remains.
  const postProductionStages = ["Shipped", "Invoiced", "Completed"];

  // Shipped-progress line: whenever some but not all of the ordered qty
  // has left the floor, say so explicitly under the bar. This is what
  // distinguishes "picked up the pallets that were ready" from a truly
  // finished order regardless of which stage label wins.
  const shippedQty = safeNum(totalShipped);
  const orderedQty = safeNum(totalOrdered);
  const isPartialShipment = orderedQty > 0 && shippedQty > 0 && shippedQty < orderedQty;
  const shippedPct = safeNum(percent) || (orderedQty > 0 ? (shippedQty / orderedQty) * 100 : 0);
  const barWidth = postProductionStages.includes(stage)
    ? 100
    : Math.min(safeNum(productionProgress), 100);

  const barColor = barWidth >= 100 ? "bg-success" : "bg-warning";
  const barStyle = stage === "Shipped" ? { backgroundColor: "#7952b3" } : {};

  return (
    <div className="d-flex flex-column w-100 mt-2">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className={`badge ${config.color} rounded-pill`} style={config.customBg ? { backgroundColor: config.customBg } : undefined}>
          <i className={`bi ${config.icon} me-1`}></i> {stage}
        </span>

        {config.showBar && (
           <span className="small fw-bold opacity-75" style={{fontSize: "0.7rem"}}>
             {barWidth}%
           </span>
        )}
      </div>

      {config.showBar && (
        <div className="progress" style={{ height: "4px", backgroundColor: "rgba(0,0,0,0.1)" }}>
          <div
            className={`progress-bar ${barColor}`}
            role="progressbar"
            style={{ width: `${barWidth}%`, ...barStyle }}
            aria-valuenow={barWidth}
            aria-valuemin="0"
            aria-valuemax="100"
          ></div>
        </div>
      )}

      {isPartialShipment && (
        <div className="small mt-1 opacity-75" style={{ fontSize: "0.7rem" }}>
          <i className="bi bi-truck me-1"></i>
          {+shippedQty.toFixed(1)} of {+orderedQty.toFixed(1)} shipped ({+Number(shippedPct).toFixed(1)}%)
        </div>
      )}
    </div>
  );
};

/**
 * --------------------
 * Components
 * --------------------
 */

const StatusBadge = ({ status }) => {
  const map = {
    Open: "primary",
    "In Progress": "warning text-dark",
    "Awaiting Shipment": "info",
    Completed: "success",
    Shipped: "success",
    Cancelled: "secondary",
    Paid: "success",
    Ordered: "info text-dark",
    Draft: "secondary",
  };
  return <span className={`badge bg-${map[status] || "secondary"}`}>{status}</span>;
};

const ConsumableStatusBadge = ({ issues }) => {
  if (!issues || issues.length === 0) return null;

  const hasCritical = issues.some((i) => i.status === "Critical");
  const badgeClass = hasCritical ? "bg-danger" : "bg-warning text-dark";
  const iconClass = hasCritical ? "bi-exclamation-octagon-fill" : "bi-truck";
  const label = hasCritical ? "Shortage" : "On Order";

  const titleText = issues
    .map(
      (i) =>
        `${i.name}: Need ${safeNum(i.needed)}, Have ${safeNum(i.on_hand)}, Inbound ${safeNum(
          i.inbound_qty
        )} (${i.status})`
    )
    .join("\n");

  return (
    <span className={`badge ${badgeClass} cursor-pointer`} title={titleText} style={{ cursor: "help" }}>
      <i className={`bi ${iconClass} me-1`}></i>
      {label}
    </span>
  );
};

const TimeDisplay = ({ seconds }) => {
  const s = safeNum(seconds);
  // Under ten minutes, whole minutes hide the number the floor cares about: a
  // 48-second brake and a 54-second assembly estimate both read "0h 0m".
  if (s < 600) {
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  }
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

// --- Documents Card Component ---
const DocumentsCard = ({ salesOrderId, API_BASE }) => {
  const [documents, setDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocuments = async () => {
    try {
      const res = await fetch(`${API_BASE}/sales_orders/${salesOrderId}/documents`);
      if (res.ok) setDocuments(await res.json());
    } catch (error) {
      console.error("Error loading docs:", error);
    }
  };

  useEffect(() => {
    if (salesOrderId) fetchDocuments();
  }, [salesOrderId]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const pin = await pinPrompt("Enter your PIN to upload:");
    if (!pin) return;

    const docType = file.type === "application/pdf" ? "Customer PO" : "General";
    const displayName = prompt("Enter display name:", file.name) || file.name;

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("pin", pin);
    formData.append("display_name", displayName);
    formData.append("doc_type", docType);

    try {
      const res = await fetch(`${API_BASE}/sales_orders/${salesOrderId}/documents`, {
        method: "POST",
        body: formData,
      });

      if (res.ok) {
        await fetchDocuments();
        alert("Upload successful.");
      } else {
        const err = await res.json();
        alert(`Upload failed: ${detailToString(err.detail)}`);
      }
    } catch (error) {
      alert("Network error uploading file.");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      const res = await fetch(`${API_BASE}/sales_orders/${salesOrderId}/documents/${docId}`, { method: "DELETE" });
      if (res.ok) setDocuments((prev) => prev.filter((d) => d.document_id !== docId));
      else alert("Failed to delete document.");
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="card shadow-sm border-top-0 border-end-0 border-bottom-0 border-4 border-secondary h-100">
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <h6 className="mb-0 fw-bold">
          <i className="bi bi-paperclip me-2"></i>Attachments & POs
        </h6>
        <div>
          <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileUpload} />
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-cloud-upload"></i>}{" "}
            Upload
          </button>
        </div>
      </div>

      <ul className="list-group list-group-flush">
        {documents.length === 0 ? (
          <li className="list-group-item text-muted fst-italic small">No documents attached.</li>
        ) : (
          documents.map((doc) => (
            <li key={doc.document_id} className="list-group-item d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center text-truncate" style={{ maxWidth: "80%" }}>
                <i
                  className={`bi ${
                    doc.doc_type === "Customer PO"
                      ? "bi-file-earmark-text text-primary"
                      : "bi-file-earmark text-secondary"
                  } me-2 fs-5`}
                ></i>
                <div className="text-truncate">
                  <a
                    href={doc.s3_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fw-bold text-decoration-none text-dark"
                  >
                    {doc.display_name}
                  </a>
                  <div className="small text-muted" style={{ fontSize: "0.75rem" }}>
                    {new Date(doc.created_at).toLocaleDateString()} • {doc.uploaded_by_name}
                  </div>
                </div>
              </div>
              <button className="btn btn-sm btn-link text-danger p-0" onClick={() => handleDelete(doc.document_id)}>
                <i className="bi bi-trash"></i>
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

// --- SO Email History Modal ---
// --- Email type + status rendering helpers (shared by card + modal) ---
const _SO_EMAIL_TYPE_LABELS = {
  so_pdf:                 { label: 'SO PDF',      variant: 'primary'   },
  shipping_notification:  { label: 'Ship notice', variant: 'info'      },
  so_finalize_shipping:   { label: 'SO Finalize', variant: 'success'   },
};
const _SO_EMAIL_STATUS_VARIANT = {
  sent:    'success',
  pending: 'warning',
  failed:  'danger',
};

const formatEmailCentralTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    timeZone: 'America/Chicago',
    year:   'numeric',
    month:  'numeric',
    day:    'numeric',
    hour:   'numeric',
    minute: '2-digit',
  });
};

const SOEmailTypeBadge = ({ type }) => {
  const cfg = _SO_EMAIL_TYPE_LABELS[type] || { label: type || 'email', variant: 'secondary' };
  return (
    <span className={`badge bg-${cfg.variant}`} style={{ fontSize: '0.7rem' }}>
      {cfg.label}
    </span>
  );
};

const SOEmailStatusCell = ({ status, errorMessage }) => {
  const variant = _SO_EMAIL_STATUS_VARIANT[status] || 'secondary';
  return (
    <span
      className={`badge bg-${variant}`}
      style={{ fontSize: '0.7rem', cursor: errorMessage ? 'help' : 'default' }}
      title={errorMessage || ''}
    >
      {status}
    </span>
  );
};

// Small button that triggers a server-side resend for pending/failed
// shipping emails, then tells the parent to re-fetch so the row flips to
// sent/failed. Resendable types: shipping_notification, so_finalize_shipping,
// so_finalize_pickup (a refused finalize email says "see Email History" — this
// is what the office finds there). Disabled for so_pdf (its own endpoint).
const SOEmailResendButton = ({ apiBase, salesOrderId, email, onResent }) => {
  const [busy, setBusy] = useState(false);
  const resendable = (
    (email.status === 'pending' || email.status === 'failed')
    && ['shipping_notification', 'so_finalize_shipping', 'so_finalize_pickup'].includes(email.email_type)
  );
  if (!resendable) return null;
  const click = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Resend shipping email to ${email.to_email}?\n\nThis rebuilds the manifest PDF and calls SES again. Safe to retry — does NOT create a duplicate audit row.`)) return;
    setBusy(true);
    try {
      const res = await fetch(
        `${apiBase}/sales_orders/${salesOrderId}/email_history/${email.email_id}/resend`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', ...emailSenderHeaders() } },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(`Resend failed: ${detailToString(body.detail) || res.status}`);
      } else if (body.status === 'failed') {
        alert(`SES rejected the resend: ${body.error_message || 'unknown'}`);
      }
    } catch (err) {
      alert(`Resend error: ${err.message || err}`);
    } finally {
      setBusy(false);
      if (typeof onResent === 'function') onResent();
    }
  };
  return (
    <button
      type="button"
      className={`btn btn-sm ${email.status === 'failed' ? 'btn-warning' : 'btn-outline-warning'}`}
      onClick={click}
      disabled={busy}
      title={`Retry sending this ${email.email_type === 'so_finalize_shipping' ? 'SO-level' : 'per-pallet'} email`}
      style={{ fontSize: '0.75rem', padding: '2px 8px' }}
    >
      {busy
        ? <><span className="spinner-border spinner-border-sm me-1"></span>Sending…</>
        : <><i className="bi bi-arrow-clockwise me-1"></i>Resend</>}
    </button>
  );
};

const SOEmailHistoryModal = ({ show, salesOrderId, apiBase, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = React.useCallback(() => {
    if (!salesOrderId) return;
    setLoading(true);
    fetch(`${apiBase}/sales_orders/${salesOrderId}/email_history`)
      .then(res => res.json())
      .then(data => setHistory(Array.isArray(data) ? data : []))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [apiBase, salesOrderId]);

  useEffect(() => { if (show) refresh(); }, [show, refresh]);

  if (!show) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Sales Order Email History</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            {loading ? (
              <div className="text-center"><div className="spinner-border text-primary"></div></div>
            ) : history.length === 0 ? (
              <p className="text-muted text-center">No emails sent for this Sales Order.</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Date (CST)</th>
                      <th>Type</th>
                      <th>Recipient</th>
                      <th>Subject</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(email => (
                      <React.Fragment key={email.email_id}>
                        <tr>
                          <td>{formatEmailCentralTime(email.sent_at)}</td>
                          <td><SOEmailTypeBadge type={email.email_type} /></td>
                          <td>{email.to_email}</td>
                          <td style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {email.subject}
                          </td>
                          <td>
                            <SOEmailStatusCell status={email.status} errorMessage={email.error_message} />
                          </td>
                          <td>
                            <SOEmailResendButton
                              apiBase={apiBase}
                              salesOrderId={salesOrderId}
                              email={email}
                              onResent={refresh}
                            />
                          </td>
                        </tr>
                        {email.status === 'failed' && email.error_message && (
                          <tr className="table-danger">
                            <td colSpan={6} className="small text-danger">
                              <strong>Send failed:</strong> {email.error_message}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- SO Email History Inline Card ---
// Void surplus jobs (an office tool; moved from the Shipping page's Build Shipment tab, 2026-10-27): once every line
// has shipped, the order's surplus jobs are read and, when there are any, a banner offers to void them
// (features/salesOrders/voidSurplus.js). Nothing is read while a line still has pieces to ship.
const SOVoidSurplusBanner = ({ salesOrderId, lines, allowOverShip, onVoided }) => {
  const shipped = allLinesShipped(lines);
  const [jobCount, setJobCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    setJobCount(0);
    if (!salesOrderId || !shipped) return undefined;
    let live = true;
    loadSurplusJobs(salesOrderId)
      .then((jobs) => { if (live) setJobCount(jobs.length); })
      .catch(() => { /* no banner when the read fails */ });
    return () => { live = false; };
  }, [salesOrderId, shipped, tick]);
  if (!shipped || jobCount < 1) return null;
  const handleVoid = async () => {
    setBusy(true);
    try {
      const res = await voidSurplusJobs({ salesOrderId, ui: desktopUi });
      if (res.done) {
        setTick((t) => t + 1);
        if (onVoided) onVoided();
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="row g-3 mt-1"><div className="col-12">
    <div className="alert alert-warning d-flex justify-content-between align-items-center mb-0" data-testid="void-surplus-banner">
      <div>
        {allowOverShip === false ? (
          <><strong>All lines fulfilled.</strong> Surplus jobs exist with parts that cannot be shipped (yield policy forbids over-shipment).</>
        ) : (
          <><strong>All lines fulfilled.</strong> Surplus jobs exist with extra parts. You can ship these extras or void the surplus jobs.</>
        )}
      </div>
      <button className="btn btn-dark btn-sm" onClick={handleVoid} disabled={busy}>
        Void Surplus Jobs
      </button>
    </div>
    </div></div>
  );
};

const SOEmailHistoryCard = ({ salesOrderId, apiBase }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const refresh = React.useCallback(() => {
    if (!salesOrderId) return;
    setLoading(true);
    fetch(`${apiBase}/sales_orders/${salesOrderId}/email_history`)
      .then(res => res.json())
      .then(data => setHistory(Array.isArray(data) ? data : []))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [apiBase, salesOrderId]);

  useEffect(() => { refresh(); }, [refresh]);

  const failedCount = history.filter(e => e.status === 'failed').length;
  const pendingCount = history.filter(e => e.status === 'pending').length;

  return (
    <div className={`card shadow-sm border-top-0 border-end-0 border-bottom-0 border-4 ${failedCount > 0 ? 'border-danger' : 'border-primary'}`}>
      <div
        className="card-header bg-white fw-bold d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <span>
          <i className={`bi bi-envelope-check me-2 ${failedCount > 0 ? 'text-danger' : 'text-primary'}`}></i>
          Email History
          {history.length > 0 && (
            <span className="badge bg-primary ms-2">{history.length}</span>
          )}
          {failedCount > 0 && (
            <span className="badge bg-danger ms-2" title="Emails that failed to send">
              {failedCount} failed
            </span>
          )}
          {pendingCount > 0 && (
            <span className="badge bg-warning text-dark ms-2" title="Emails queued but not yet delivered">
              {pendingCount} pending
            </span>
          )}
        </span>
        <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`}></i>
      </div>
      {expanded && (
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center p-3"><div className="spinner-border spinner-border-sm text-primary"></div></div>
          ) : history.length === 0 ? (
            <p className="text-muted fst-italic small p-3 mb-0">No emails sent for this order.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Date (CST)</th>
                    <th>Type</th>
                    <th>Recipient</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(email => (
                    <React.Fragment key={email.email_id}>
                      <tr>
                        <td className="small">{formatEmailCentralTime(email.sent_at)}</td>
                        <td><SOEmailTypeBadge type={email.email_type} /></td>
                        <td className="small">{email.to_email}</td>
                        <td className="small" style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {email.subject}
                        </td>
                        <td>
                          <SOEmailStatusCell status={email.status} errorMessage={email.error_message} />
                        </td>
                        <td>
                          <SOEmailResendButton
                            apiBase={apiBase}
                            salesOrderId={salesOrderId}
                            email={email}
                            onResent={refresh}
                          />
                        </td>
                      </tr>
                      {email.status === 'failed' && email.error_message && (
                        <tr className="table-danger">
                          <td colSpan={6} className="small text-danger">
                            <strong>Send failed:</strong> {email.error_message}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Part Search Component ---
const PartSearch = ({ value, initialDisplay, onSelect, disabled, API_BASE }) => {
  const [searchTerm, setSearchTerm] = useState(initialDisplay || "");
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    setSearchTerm(initialDisplay || "");
  }, [initialDisplay]);

  useEffect(() => {
    if (!searchTerm || disabled || !showDropdown) return;

    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/parts/search?query=${encodeURIComponent(searchTerm)}`);
        if (res.ok) setResults(await res.json());
      } catch (error) {
        console.error("Search error", error);
      }
    }, 300);

    return () => clearTimeout(t);
  }, [searchTerm, API_BASE, disabled, showDropdown]);

  const handleSelect = (part) => {
    setSearchTerm(part.product_id);
    setShowDropdown(false);
    onSelect(part);
  };

  return (
    <div className="position-relative">
      <input
        type="text"
        className="form-control form-control-sm"
        placeholder="Type SKU or Desc..."
        value={searchTerm}
        disabled={disabled}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setShowDropdown(true);
        }}
        onFocus={() => setShowDropdown(true)}
        onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
      />
      {showDropdown && results.length > 0 && (
        <ul
          className="list-group position-absolute w-100 shadow-sm"
          style={{ zIndex: 1000, maxHeight: "200px", overflowY: "auto" }}
        >
          {results.map((part) => (
            <button
              key={part.part_id}
              className="list-group-item list-group-item-action text-start p-2 small"
              onClick={() => handleSelect(part)}
            >
              <strong>{part.product_id}</strong>
              <br />
              <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                {part.description?.substring(0, 40)}
              </span>
            </button>
          ))}
        </ul>
      )}
    </div>
  );
};

const TimeComparison = ({ estimated, actual }) => {
  const parseSeconds = (val) => {
    if (typeof val === "number") return val;
    if (!val) return 0;
    const parts = String(val).split(":").map(Number);
    if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    return 0;
  };

  const estSec = parseSeconds(estimated);
  const actSec = parseSeconds(actual);
  const isOver = actSec > estSec && estSec > 0;

  return (
    <div className="d-flex flex-column" style={{ fontSize: "0.85rem" }}>
      <span className="text-muted">
        Est: {estSec > 0 ? <TimeDisplay seconds={estSec} /> : "—"}
      </span>
      <span className={isOver ? "text-danger fw-bold" : "text-success"}>
        Act: {actSec > 0 ? <TimeDisplay seconds={actSec} /> : "—"}
      </span>
    </div>
  );
};

/**
 * --------------------------------------------------------------------------
 * Action Center — the dashboard that fills the right detail panel when no
 * order is selected. All category math lives server-side in
 * GET /sales_orders/action_items (which reuses the SO list's lifecycle
 * logic, so the dashboard can never disagree with the list). This component
 * is pure presentation: sections of click-to-open rows, one per SO.
 * --------------------------------------------------------------------------
 */
const acDate = (v) => {
  if (!v) return null;
  const d = new Date(String(v).slice(0, 10) + "T00:00:00");
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

const AcPill = ({ tone = "secondary", icon, children, title }) => (
  <span
    className={`badge rounded-pill text-bg-${tone} fw-normal`}
    style={{ fontSize: "0.72rem" }}
    title={title || undefined}
  >
    {icon && <i className={`bi ${icon} me-1`}></i>}
    {children}
  </span>
);

// Compact material/consumable availability line ("0 on hand · 20 inbound,
// ETA 7/26 — 12d overdue · PO 1250"). Used by both the shortage card and the
// needs-more-jobs material hints.
const AcSupplyLine = ({ m }) => (
  <div className="d-flex flex-wrap align-items-center gap-1 small">
    <span className="font-monospace">{m.name || m.material_code}</span>
    {m.needed != null && <span className="text-body-secondary">need {safeNum(m.needed)}</span>}
    <span className={safeNum(m.on_hand) <= 0 ? "text-danger fw-bold" : "text-body"}>
      {safeNum(m.on_hand)} on hand
    </span>
    {safeNum(m.inbound_qty) > 0 ? (
      <span className="text-body-secondary">
        · {safeNum(m.inbound_qty)} inbound
        {m.earliest_eta ? `, ETA ${acDate(m.earliest_eta)}` : ", no ETA"}
        {m.eta_overdue_days > 0 && (
          <strong className="text-danger"> — {m.eta_overdue_days}d overdue</strong>
        )}
        {(m.po_numbers || []).length > 0 && (
          <span> · PO {(m.po_numbers || []).join(", ")}</span>
        )}
      </span>
    ) : (
      <span className="text-danger">· nothing inbound</span>
    )}
    {m.status && (
      <AcPill tone={m.status === "Critical" ? "danger" : "warning"}>{m.status}</AcPill>
    )}
  </div>
);

// Material Arriving Late (jobq-api material_timing.py). One block per material an order
// waits on. "Lands" = the day the PO that covers it can be cut (an ETA that has passed
// counts from the next business day); "needed by" = the latest day it can land and still
// finish by the promised date; "earliest finish" assumes no wait at any station, so a
// "Late" here is late on material alone.
// AC_LATE_VERDICT (labels, titles, tone): features/salesOrders/soData.js, shared with the phone.

const AcMaterialLateLine = ({ m }) => {
  const pos = m.pos || [];
  const unit = m.unit || "sheets";
  return (
    <div className="small" data-testid="ac-material-late-line">
      <div className="d-flex flex-wrap align-items-center gap-1">
        <span className="font-monospace fw-semibold">{m.material_code}</span>
        <span className="text-body-secondary">
          need {safeNum(m.needed)} {unit} · {lateOnHandText(m)}
        </span>
        {pos.length > 0 && (
          <span>
            · {latePoText(pos)} {pos.length > 1 ? "all in by" : "lands"}{" "}
            <strong className={m.verdict === "at_risk" ? "text-warning-emphasis" : "text-danger"}>
              {acDate(m.ready_date)}
            </strong>
            {pos.some((p) => p.eta_overdue) && <span className="text-danger"> (ETA passed)</span>}
          </span>
        )}
      </div>
      <div className="text-body-secondary">
        {m.need_by_date && (
          <>needed by <strong className="text-body">{acDate(m.need_by_date)}</strong> · </>
        )}
        earliest finish <strong className="text-body">{acDate(m.earliest_finish)}</strong>
        {(m.route || []).length > 0 && <> · after the cut: {m.route.join(" → ")}</>}
        {m.route_known === false && <> · no routing on file for the part</>}
      </div>
      {(m.jobs || []).map((j) => (
        <div key={j.task_id}
          className={j.verdict === "cut_before_material" ? "text-danger" : "text-body-secondary"}>
          Job #{j.master_job_num} {j.workstation === "Laser" ? "cut" : "saw cut"}{" "}
          {j.scheduled_date ? acDate(j.scheduled_date) : "not scheduled"}
          {j.pinned && " (pinned)"}
          {j.verdict === "cut_before_material" && ": before its material lands"}
        </div>
      ))}
      {(m.unlaunched || []).map((u) => (
        <div key={u.part_id} className="text-body-secondary">
          <span className="font-monospace">{u.product_id}</span> short {safeNum(u.short_qty)}: no job yet
          ({safeNum(u.units)} {unit})
        </div>
      ))}
    </div>
  );
};

const AC_FULFILLMENT = {
  pickup: { icon: "bi-person-walking", label: "Pickup", tone: "warning" },
  delivery: { icon: "bi-truck", label: "Delivery", tone: "info" },
  shipping: { icon: "bi-box-seam", label: "Ship", tone: "secondary" },
};

function AcRow({ item, onSelect, chips, detail }) {
  const f = AC_FULFILLMENT[item.fulfillment_type] || AC_FULFILLMENT.shipping;
  return (
    <button
      type="button"
      className="list-group-item list-group-item-action py-2 px-3"
      onClick={() => onSelect(item.sales_order_id)}
      title={`Open ${item.order_number}`}
    >
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-1">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="fw-bold">{item.order_number}</span>
          <span className="text-body-secondary small">{item.customer_name}</span>
          <AcPill tone={f.tone} icon={f.icon}>{f.label}</AcPill>
        </div>
        <div className="d-flex align-items-center gap-1 flex-wrap justify-content-end">
          {chips}
        </div>
      </div>
      {detail && <div className="small mt-1">{detail}</div>}
    </button>
  );
}

function AcSection({ section, items, onSelect, renderRow }) {
  const [open, setOpen] = useState(true);
  if (!items || items.length === 0) return null;
  return (
    <div className="card mb-3 shadow-sm" style={{ borderLeft: `4px solid ${section.accent}` }}>
      <div
        className="card-header d-flex justify-content-between align-items-center py-2"
        style={{ cursor: "pointer" }}
        onClick={() => setOpen((o) => !o)}
      >
        <div className="d-flex align-items-center gap-2">
          <i className={`bi ${section.icon}`} style={{ color: section.accent }}></i>
          <strong>{section.title}</strong>
          <span className="badge rounded-pill" style={{ backgroundColor: section.accent }}>
            {items.length}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="text-body-secondary small d-none d-lg-inline">{section.blurb}</span>
          <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-down"} text-body-secondary`}></i>
        </div>
      </div>
      {open && (
        <div className="list-group list-group-flush">
          {items.map((item) => renderRow(item, onSelect))}
        </div>
      )}
    </div>
  );
}

function ActionItemsDashboard({ apiBase, onSelectOrder }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  const fetchItems = React.useCallback(async () => {
    try {
      setError(null);
      setData(await loadActionItems()); // features/salesOrders/soData.js (shared with the phone)
      setLastFetched(new Date());
    } catch (e) {
      console.error("Action items fetch failed:", e);
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    const t = setInterval(fetchItems, 60000);
    return () => clearInterval(t);
  }, [fetchItems]);

  const cats = data?.categories || {};
  const summary = data?.summary || {};

  // Section order = triage priority. Rendering skips empty sections.
  const sections = [
    {
      ...AC_SECTION_META.behind_schedule,
      render: (item, onSelect) => {
        const riskChip =
          item.risk_level === "overdue" ? (
            <AcPill tone="danger" icon="bi-alarm-fill">Overdue {item.days_late}d</AcPill>
          ) : item.risk_level === "behind" ? (
            <AcPill tone="danger" icon="bi-x-circle">Est {item.days_late}d late</AcPill>
          ) : (
            <AcPill tone="warning" icon="bi-exclamation-triangle">At Risk</AcPill>
          );
        return (
          <AcRow key={`bs-${item.sales_order_id}`} item={item} onSelect={onSelect}
            chips={<>
              {riskChip}
              {item.blocked_tasks > 0 && (
                <AcPill tone="dark" icon="bi-sign-stop" title="Tasks waiting on an upstream station">
                  {item.blocked_tasks} blocked
                </AcPill>
              )}
              {item.material_late && (
                <AcPill tone="danger" icon="bi-truck"
                  title={`Waiting on ${item.material_late.material_code}`
                    + ((item.material_late.po_numbers || []).length
                      ? `: PO ${item.material_late.po_numbers.join(", ")}` : "")
                    + ` lands ${acDate(item.material_late.ready_date)}`}>
                  Material lands {acDate(item.material_late.ready_date)}
                </AcPill>
              )}
            </>}
            detail={<>
              <div className="d-flex flex-wrap gap-3 text-body-secondary">
                <span>Promised: <strong className="text-body">{acDate(item.promised_date) || "—"}</strong></span>
                <span>Est: <strong className="text-body">{acDate(item.est_complete_date) || "—"}</strong></span>
                {item.current_station && (
                  <span>Now at: <strong className="text-body">{item.current_station}</strong></span>
                )}
                <span>{fmtMoney(item.commitment)}</span>
              </div>
              <div className="d-flex align-items-center gap-2 mt-1">
                <div className="progress flex-grow-1" style={{ height: 5, maxWidth: 220 }}>
                  <div
                    className={`progress-bar ${safeNum(item.production_progress_pct) >= 100 ? "bg-success" : "bg-warning"}`}
                    style={{ width: `${Math.min(100, safeNum(item.production_progress_pct))}%` }}
                  ></div>
                </div>
                <span className="text-body-secondary" style={{ fontSize: "0.72rem" }}>
                  {safeNum(item.production_progress_pct)}% produced
                  {(item.station_breakdown || []).length > 0 &&
                    " · open: " + item.station_breakdown.map((s) => `${s.workstation} ${s.open_tasks}`).join(", ")}
                </span>
              </div>
            </>}
          />
        );
      },
    },
    {
      ...AC_SECTION_META.material_late,
      render: (item, onSelect) => {
        const v = AC_LATE_VERDICT[item.verdict] || AC_LATE_VERDICT.at_risk;
        return (
          <AcRow key={`ml-${item.sales_order_id}`} item={item} onSelect={onSelect}
            chips={<>
              <AcPill tone={v.tone} icon={v.icon} title={v.title}>{v.label(item)}</AcPill>
              {item.promised_date && (
                <AcPill tone="secondary" icon="bi-calendar-event" title="Promised date">
                  {acDate(item.promised_date)}
                </AcPill>
              )}
            </>}
            detail={
              <div className="vstack gap-2">
                {(item.items || []).slice(0, 4).map((m) => (
                  <AcMaterialLateLine key={m.material_id} m={m} />
                ))}
                {(item.items || []).length > 4 && (
                  <span className="text-body-secondary">+{item.items.length - 4} more materials…</span>
                )}
              </div>
            }
          />
        );
      },
    },
    {
      ...AC_SECTION_META.needs_more_jobs,
      render: (item, onSelect) => (
        <AcRow key={`nmj-${item.sales_order_id}`} item={item} onSelect={onSelect}
          chips={<>
            <AcPill tone="danger">{safeNum(item.total_short_qty)} pcs short</AcPill>
            {item.promised_date && <AcPill tone="secondary" icon="bi-calendar-event">{acDate(item.promised_date)}</AcPill>}
          </>}
          detail={
            <div className="vstack gap-1">
              {(item.short_lines || []).slice(0, 6).map((l) => (
                <div key={l.so_line_id}>
                  <span className="font-monospace fw-semibold">{l.product_id}</span>
                  <span className="text-danger fw-semibold"> short {safeNum(l.short_qty)}</span>
                  <span className="text-body-secondary"> of {safeNum(l.quantity_ordered)} ordered</span>
                  {(l.materials || []).slice(0, 1).map((m) => (
                    <div key={m.material_id} className="ms-3">
                      <AcSupplyLine m={m} />
                    </div>
                  ))}
                </div>
              ))}
              {(item.short_lines || []).length > 6 && (
                <span className="text-body-secondary">+{item.short_lines.length - 6} more lines…</span>
              )}
            </div>
          }
        />
      ),
    },
    {
      ...AC_SECTION_META.uncovered_remakes,
      render: (item, onSelect) => (
        <AcRow key={`rmk-${item.sales_order_id}`} item={item} onSelect={onSelect}
          chips={<>
            <AcPill tone="danger">{safeNum(item.total_uncovered)} pcs uncovered</AcPill>
            {item.oldest_days > 0 && <AcPill tone="warning" icon="bi-clock-history">{item.oldest_days}d old</AcPill>}
          </>}
          detail={
            <div className="vstack gap-1">
              {(item.items || []).slice(0, 6).map((r) => (
                <div key={r.demand_id}>
                  <span className="font-monospace fw-semibold">{r.product_id}</span>
                  <span className="text-danger fw-semibold"> {safeNum(r.qty_uncovered)} to remake</span>
                  <span className="text-body-secondary"> → Job #{r.source_job_num} {r.join_workstation}{r.assembly_name ? ` (${r.assembly_name})` : ""}</span>
                </div>
              ))}
              {(item.items || []).length > 6 && (
                <span className="text-body-secondary">+{item.items.length - 6} more…</span>
              )}
              <span className="text-body-secondary" style={{ fontSize: "0.8rem" }}>
                Load a makeup cut sheet — it auto-links to these demands and prints REMAKE delivery slips.
              </span>
            </div>
          }
        />
      ),
    },
    {
      ...AC_SECTION_META.material_blocked,
      render: (item, onSelect) => (
        <AcRow key={`mb-${item.sales_order_id}`} item={item} onSelect={onSelect}
          chips={<>
            {item.critical_count > 0 && <AcPill tone="danger">{item.critical_count} critical</AcPill>}
          </>}
          detail={
            <div className="vstack gap-1">
              {(item.items || []).slice(0, 5).map((m, i) => (
                <AcSupplyLine key={`${m.kind}-${m.material_id || m.consumable_id || i}`} m={m} />
              ))}
              {(item.items || []).length > 5 && (
                <span className="text-body-secondary">+{item.items.length - 5} more items…</span>
              )}
            </div>
          }
        />
      ),
    },
    {
      ...AC_SECTION_META.ready_to_seal,
      render: (item, onSelect) => (
        <AcRow key={`rs-${item.sales_order_id}`} item={item} onSelect={onSelect}
          chips={<>
            {item.boxes_ready_to_seal > 0 && <AcPill tone="warning">{item.boxes_ready_to_seal} box{item.boxes_ready_to_seal !== 1 ? "es" : ""}</AcPill>}
            {item.pallets_ready_to_seal > 0 && <AcPill tone="warning">{item.pallets_ready_to_seal} pallet{item.pallets_ready_to_seal !== 1 ? "s" : ""}</AcPill>}
            {item.is_late && <AcPill tone="danger">Late</AcPill>}
          </>}
          detail={
            <span className="text-body-secondary">
              Production complete — seal &amp; label to move it out.
              {item.promised_date && <> Promised {acDate(item.promised_date)}.</>}
            </span>
          }
        />
      ),
    },
    {
      ...AC_SECTION_META.ready_to_ship,
      render: (item, onSelect) => (
        <AcRow key={`rts-${item.sales_order_id}`} item={item} onSelect={onSelect}
          chips={<>
            {item.days_since_production_done != null && (
              <AcPill tone={item.days_since_production_done >= 5 ? "danger" : item.days_since_production_done >= 2 ? "warning" : "secondary"}>
                waiting {item.days_since_production_done}d
              </AcPill>
            )}
            {item.pallets_ready > 0 && <AcPill tone="info">{item.pallets_ready} pallet{item.pallets_ready !== 1 ? "s" : ""} sealed</AcPill>}
          </>}
          detail={<>
            <span className="text-body-secondary">{item.reason}.</span>{" "}
            <span className="text-body-secondary">
              {safeNum(item.unshipped_qty)} pcs to go
              {item.total_shipped > 0 && ` (${safeNum(item.percent_shipped)}% shipped so far)`}
              {" · "}{fmtMoney(item.commitment)}
              {item.promised_date && ` · promised ${acDate(item.promised_date)}`}
              {!item.has_plan && item.total_ordered > 0 && " · no packaging plan yet"}
            </span>
          </>}
        />
      ),
    },
    {
      ...AC_SECTION_META.awaiting_pickup,
      render: (item, onSelect) => (
        <AcRow key={`ap-${item.sales_order_id}`} item={item} onSelect={onSelect}
          chips={
            <AcPill tone={item.staged_days >= 7 ? "danger" : item.staged_days >= 3 ? "warning" : "secondary"}>
              {item.staged_days != null ? `${item.staged_days}d staged` : "staged"}
            </AcPill>
          }
          detail={
            <span className="text-body-secondary">
              {item.staged_pallets} pallet{item.staged_pallets !== 1 ? "s" : ""} on the floor since {acDate(item.oldest_staged_at) || "?"} — nudge the customer or confirm pickup.
            </span>
          }
        />
      ),
    },
    {
      ...AC_SECTION_META.stalled,
      render: (item, onSelect) => (
        <AcRow key={`st-${item.sales_order_id}`} item={item} onSelect={onSelect}
          chips={<>
            <AcPill tone="danger">{item.idle_days}d idle</AcPill>
            {item.never_started && <AcPill tone="dark">never started</AcPill>}
            {item.blocked_tasks > 0 && <AcPill tone="dark" icon="bi-sign-stop">{item.blocked_tasks} blocked</AcPill>}
          </>}
          detail={
            <span className="text-body-secondary">
              {item.current_station ? `Sitting at ${item.current_station}` : "No open station"} ·{" "}
              {item.open_tasks} open task{item.open_tasks !== 1 ? "s" : ""} ·{" "}
              {safeNum(item.production_progress_pct)}% produced
              {item.promised_date && ` · promised ${acDate(item.promised_date)}`}
            </span>
          }
        />
      ),
    },
    tmReadyToPriceSection({ AcRow, AcPill, fmtMoney }),
    {
      ...AC_SECTION_META.uninvoiced,
      render: (item, onSelect) => (
        <AcRow key={`ui-${item.sales_order_id}`} item={item} onSelect={onSelect}
          chips={<>
            <AcPill tone="success">{fmtMoney(item.uninvoiced_amount)}</AcPill>
            {item.days_since_shipped != null && (
              <AcPill tone={item.days_since_shipped >= 7 ? "danger" : "secondary"}>
                shipped {item.days_since_shipped}d ago
              </AcPill>
            )}
          </>}
          detail={
            <span className="text-body-secondary">
              Fully shipped{item.last_shipped_date ? ` on ${acDate(item.last_shipped_date)}` : ""} with no invoice issued.
            </span>
          }
        />
      ),
    },
    {
      ...AC_SECTION_META.aging_drafts,
      render: (item, onSelect) => (
        <AcRow key={`ad-${item.sales_order_id}`} item={item} onSelect={onSelect}
          chips={<>
            {item.age_days != null && <AcPill tone={item.promised_overdue ? "danger" : "secondary"}>{item.age_days}d old</AcPill>}
            {item.promised_overdue && <AcPill tone="danger" icon="bi-alarm-fill">promised date passed</AcPill>}
          </>}
          detail={
            <span className="text-body-secondary">
              Draft with no jobs released{item.promised_date ? ` — promised ${acDate(item.promised_date)}` : ""}.
              {item.has_quote ? "" : " No source quote."}
            </span>
          }
        />
      ),
    },
  ];

  const totalItems = sections.reduce((n, s) => n + (cats[s.key]?.length || 0), 0);

  return (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <h4 className="mb-0">
          <i className="bi bi-lightning-charge-fill text-warning me-2"></i>
          Action Center
        </h4>
        <div className="d-flex align-items-center gap-2">
          {lastFetched && (
            <span className="text-body-secondary" style={{ fontSize: "0.75rem" }}>
              Updated {lastFetched.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </span>
          )}
          <button className="btn btn-sm btn-outline-secondary" onClick={fetchItems} disabled={loading}>
            <i className="bi bi-arrow-clockwise"></i>
          </button>
        </div>
      </div>

      {loading && !data ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "40vh" }}>
          <div className="spinner-border text-primary"></div>
        </div>
      ) : error && !data ? (
        <div className="alert alert-warning">
          Couldn't load action items ({error}).{" "}
          <button className="btn btn-sm btn-outline-secondary ms-2" onClick={fetchItems}>Retry</button>
        </div>
      ) : (
        <>
          <div className="row g-2 mb-3">
            {[
              { label: "Orders needing action", value: summary.orders_needing_action ?? 0, icon: "bi-list-check", tone: (summary.orders_needing_action || 0) > 0 ? "text-primary" : "text-success" },
              { label: "Overdue", value: summary.overdue_count ?? 0, icon: "bi-alarm-fill", tone: (summary.overdue_count || 0) > 0 ? "text-danger" : "text-success" },
              { label: "Value at risk", value: fmtMoney(summary.value_at_risk), icon: "bi-cash-stack", tone: (summary.value_at_risk || 0) > 0 ? "text-danger" : "text-success" },
              { label: "Shipped, unbilled", value: fmtMoney(summary.uninvoiced_value), icon: "bi-receipt", tone: (summary.uninvoiced_value || 0) > 0 ? "text-warning" : "text-success" },
            ].map((kpi) => (
              <div className="col-6 col-xl-3" key={kpi.label}>
                <div className="card h-100 shadow-sm">
                  <div className="card-body py-2 px-3 d-flex align-items-center gap-3">
                    <i className={`bi ${kpi.icon} fs-4 ${kpi.tone}`}></i>
                    <div>
                      <div className="fw-bold fs-5">{kpi.value}</div>
                      <div className="text-body-secondary" style={{ fontSize: "0.72rem" }}>{kpi.label}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <ErrorBoundary label="The T&M pending line">
            <TmPendingSummaryLine summary={summary} fmtMoney={fmtMoney} />
          </ErrorBoundary>

          {error && (
            <div className="alert alert-warning py-2 small">
              Refresh failed ({error}) — showing last good data.
            </div>
          )}

          {totalItems === 0 ? (
            <div className="d-flex flex-column justify-content-center align-items-center text-center" style={{ minHeight: "40vh" }}>
              <i className="bi bi-check-circle-fill text-success display-3 mb-3"></i>
              <h5>All clear</h5>
              <p className="text-body-secondary mb-0">
                No orders need attention right now. Select an order on the left to view details.
              </p>
            </div>
          ) : (
            sections.map((s) => (
              <AcSection key={s.key} section={s} items={cats[s.key]} onSelect={onSelectOrder} renderRow={s.render} />
            ))
          )}
        </>
      )}
    </div>
  );
}

export default function SalesOrderPage() {
  const [orders, setOrders] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  // Production Detail: which part blocks are open (station rows under the
  // routing strip) and which folded set rows are open. Keyed "<job_id>:<part_id>"
  // and "<job_id>:<part_id>:<step key>". Collapsed by default: the strip is the
  // glance, the rows are the audit.
  const [expandedParts, setExpandedParts] = useState({});
  const [expandedSets, setExpandedSets] = useState({});
  // Production Detail: jobs whose other orders' parts are shown (job_id → true).
  // Hidden by default: a shared sheet's other parts are not this order's (SO-1141 / job #10304).
  const [showOtherParts, setShowOtherParts] = useState({});
  // A job is on several orders' pages: each order opens with them hidden again.
  useEffect(() => { setShowOtherParts({}); }, [selectedId]);
  const [summaryData, setSummaryData] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showClosed, setShowClosed] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Status filter: empty array = show all. When populated, only orders
  // whose status is in the array render. Chips below the search box
  // toggle statuses on/off. Chips rendered from the live set of
  // statuses present in the loaded orders so the filter only ever
  // shows relevant options.
  const [statusFilter, setStatusFilter] = useState([]);
  // Customer filter: empty string = show all. When set to a customer_id
  // (as a string), only that customer's orders render. Options derived
  // from the loaded orders so the dropdown only ever lists customers
  // who actually have orders in view.
  const [customerFilter, setCustomerFilter] = useState("");

  // --- Email History State ---
  const [showEmailHistory, setShowEmailHistory] = useState(false);

  // --- Contact Management State ---
  const [showContactModal, setShowContactModal] = useState(false);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [soAddresses, setSoAddresses] = useState([]); // customer's ship-to options for the picker

  // Load the SO customer's saved ship-to addresses for the Ship-To picker.
  useEffect(() => {
    const cid = detailData?.header?.customer_id;
    if (!cid) { setSoAddresses([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/customers/${cid}/addresses`);
        if (res.ok && !cancelled) setSoAddresses(await res.json());
      } catch (e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [detailData?.header?.customer_id]);
  const [contactRoles, setContactRoles] = useState({ order: "", billing: "", shipping: "" });
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", role: "General" });

  // --- Line Item Editing State ---
  const [isEditingLines, setIsEditingLines] = useState(false);
  const [editedLines, setEditedLines] = useState([]);
  // Warnings returned by PATCH /sales_orders/{id} after an Edit Items save
  // (e.g. "line X was added at $0.00 — no book price on file"). Shown in the
  // Line Items card until dismissed. (F45)
  const [lineSaveWarnings, setLineSaveWarnings] = useState([]);

  // --- Customer PO Edit State ---
  const [isEditingPO, setIsEditingPO] = useState(false);
  const [editPOValue, setEditPOValue] = useState('');
  const [isEditingSO, setIsEditingSO] = useState(false);
  const [editSOValue, setEditSOValue] = useState('');

  // --- Promised Completion Date State ---
  const [isEditingPromised, setIsEditingPromised] = useState(false);
  const [completionMode, setCompletionMode] = useState('lead_time');
  const [editLeadTime, setEditLeadTime] = useState('Standard (10-14 Days)');
  const [editPromisedDate, setEditPromisedDate] = useState('');

  // --- Target Completion Date (internal scheduling override) State ---
  const [isEditingTarget, setIsEditingTarget] = useState(false);
  const [editTargetDate, setEditTargetDate] = useState('');
  const [schedulePropNote, setSchedulePropNote] = useState(null);

  // Yield Policies
  const [yieldPolicies, setYieldPolicies] = useState([]);

  // Customer list for the SurplusAllocationModal customer selector
  const [customerList, setCustomerList] = useState([]);

  // --- Production Progress State ---
  const [progressData, setProgressData] = useState(null);

  // Plan summary used to surface ride-along status on the Shipments tab.
  // Fetched alongside the truth payload; lets renderDocuments show a
  // "Ride-along" badge + a Combined-manifest button when any of this
  // plan's boxes physically rode on another plan's pallet (outgoing) OR
  // this plan's pallets host boxes from other plans (incoming). The
  // SOPackagingPlanCard fetches its own copy independently — keep this
  // separate so the Shipments tab works even when the plan card is
  // collapsed.
  const [planSummary, setPlanSummary] = useState(null);
  // Bumped by child cards (proof-of-delivery photos) that changed something the
  // detail pane and the plan summary render; re-runs the detail effect.
  const [detailTick, setDetailTick] = useState(0);
  // Production Detail: the laser task the Switch cut sheet dialog is open for (null = closed).
  const [switchSheetTaskId, setSwitchSheetTaskId] = useState(null);

  // --- Surplus Allocation State ---
  const [showSurplusModal, setShowSurplusModal] = useState(false);
  const [surplusModalContext, setSurplusModalContext] = useState(null);

  // Catalog inspector — page-level state for the CatalogItemDetailModal opened
  // from every product_id render site outside SOPackagingPlanCard (Line Items,
  // Job Tasks, Defects, Shipments). The plan card has its own instance
  // since its modal lifecycle is scoped to the card.
  //
  // Holds a {kind, id} descriptor so an assembly group header and a purchased
  // component row open the same way a part does. setInspectedPartId is kept as
  // an adapter for the part-only call sites, which still pass a bare part_id.
  const [inspectedItem, setInspectedItem] = useState(null);
  const setInspectedPartId = useCallback(
    (partId) => setInspectedItem(partId ? { kind: 'part', id: partId } : null),
    []
  );

  const API_BASE = config.API_BASE_URL;

  // --- Time & Materials (slice 1d) ---
  const [tmPricingOpen, setTmPricingOpen] = useState(false);
  const [tmReopenOpen, setTmReopenOpen] = useState(false);
  const [tmPendingOnly, setTmPendingOnly] = useState(false);
  // Deep link /sales-orders?id=N selects the order; &tm=price also opens Approve T&M Pricing once
  // the order has loaded (pending orders only, once per link).
  const [searchParams] = useSearchParams();
  const linkedSoId = searchParams.get('id');
  const linkedTm = searchParams.get('tm');
  // &pallet=<id> (a pallet beacon's /s/plt- QR): the Packaging Plan opens on that pallet, once per link.
  const linkedPalletId = salesOrderPalletLink(searchParams.toString());
  const linkHandledRef = useRef(null);
  const tmAutoOpenedRef = useRef(null);
  useEffect(() => {
    if (!linkedSoId || linkHandledRef.current === linkedSoId) return;
    const id = parseInt(linkedSoId, 10);
    if (!Number.isFinite(id)) return;
    linkHandledRef.current = linkedSoId;
    setSelectedId(id);
  }, [linkedSoId]);
  useEffect(() => {
    const h = detailData?.header;
    if (linkedTm !== 'price' || !h || String(h.sales_order_id) !== String(linkedSoId)) return;
    if (tmAutoOpenedRef.current === linkedSoId) return;
    tmAutoOpenedRef.current = linkedSoId;
    if (isTmPending(h)) setTmPricingOpen(true);
  }, [linkedTm, linkedSoId, detailData]);
  // T&M (Commit C): the order's defects for the panel + the decision chips on the deep dive's defect
  // rows. A fixed-price order sends no /tm_defects request (the hook keys on the header's
  // pricing_mode); paused while the pricing window is open (it re-reads when the window closes),
  // and on the render before the &tm=price link opens that window (a read there is thrown away).
  const tmHeader = detailData?.header;
  const tmAutoOpenDue = linkedTm === 'price' && !!tmHeader && String(tmHeader.sales_order_id) === String(linkedSoId)
    && isTmPending(tmHeader) && tmAutoOpenedRef.current !== linkedSoId;
  const tmDefects = useTmDefects(tmHeader, detailTick, tmPricingOpen || tmAutoOpenDue);
  const tmDecisions = useMemo(() => decisionsByDefectId(tmDefects.data), [tmDefects.data]);
  const refreshAfterTm = () => {
    // Approve / Reopen changes the stored lines: an open line editor would edit (and try to save)
    // what the pricing just replaced or froze.
    setIsEditingLines(false);
    setEditedLines([]);
    setDetailTick((t) => t + 1);
    fetchOrders();
  };
  // A pricing window closed with an unknown outcome (a finalize / reopen with no answer): re-read.
  const closeTmWindow = (setOpen) => (outcome) => {
    setOpen(false);
    if (outcome && outcome.outcome === 'unknown') refreshAfterTm();
  };

  useEffect(() => {
    fetchPolicies();
    fetchOrders();
    fetchCustomers();
  }, [showClosed, showArchived]);

  const fetchCustomers = async () => {
    try {
      const res = await fetch(`${API_BASE}/customers`);
      if (res.ok) setCustomerList(await res.json());
    } catch (err) {
      console.error("Error loading customers:", err);
    }
  };

  const fetchOrders = async () => {
    setLoadingList(true);
    try {
      const rows = await loadSalesOrders({ showClosed, showArchived }); // features/salesOrders/soData.js
      if (rows) setOrders(rows);
    } catch (err) {
      console.error("Error loading orders:", err);
    } finally {
      setLoadingList(false);
    }
  };

  const fetchPolicies = async () => {
    try {
      const res = await fetch(`${API_BASE}/yield_policies`);
      if (res.ok) setYieldPolicies(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!selectedId) {
      setDetailData(null);
      setSummaryData(null);
      return;
    }

    setIsEditingPO(false);
    setIsEditingSO(false);
    setIsEditingPromised(false);

    const fetchDetails = async () => {
      setLoadingDetail(true);
      try {
        // features/salesOrders/soData.js (shared with the phone's order screen).
        const truth = await loadOrderTruth(selectedId);

        setContactRoles({
          order: truth.header.order_contact_id || "",
          billing: truth.header.billing_contact_id || "",
          shipping: truth.header.shipping_contact_id || "",
        });

        setDetailData(truth);

        // Plan is optional — many SOs don't have one yet. 404 is normal.
        const extras = await loadOrderExtras(selectedId);
        setSummaryData(extras.summary);
        setProgressData(extras.progress);
        setPlanSummary(extras.plan);
      } catch (err) {
        console.error("Error loading details:", err);
        alert("Failed to load full order details.");
      } finally {
        setLoadingDetail(false);
      }
    };

    fetchDetails();
  }, [selectedId, API_BASE, detailTick]);

  // (Production Financial Summary tooltips are now rendered via the InfoTooltip
  // / OverlayTrigger wrapper at the top of this file — see the comment there.
  // The old `new bootstrap.Tooltip(el)` init effect lived here.)

  /**
   * --------------------
   * Yield Health (grouped-lines aware)
   * --------------------
   */
  // Defects on the order's open jobs, by part (features/salesOrders/soData.js, shared with the
  // phone): sheet tasks attribute by their per-part breakdown, and decoupled assembly defects
  // (task_id NULL) count too, because /truth's In Prod already subtracts them (SO-1102).
  const activeDefectMap = useMemo(
    () => activeDefectsByPart(detailData?.production_data),
    [detailData?.production_data],
  );

  // A part on several lines: /truth already splits its In Prod and Shipped across them (each line's
  // qty_in_production / qty_shipped is its share; the shares sum to the part's figure, see jobq-ui
  // CLAUDE.md "SO fulfillment: /truth splits a part across its lines"), so they are read as they come.
  // Only the defects come per part: each line gets its share (soData.defectsByLine). Shared by
  // yieldViolations (the shortage highlight) AND renderLineItems (display) so they always agree.
  const lineDefects = useMemo(
    () => defectsByLine(detailData?.lines ? flattenTruthLines(detailData.lines) : [], activeDefectMap),
    [detailData?.lines, activeDefectMap],
  );

  // Each line's process steps with how many of THIS order's pieces finished each one
  // (features/salesOrders/lineSteps.js, Mike 2026-10-20): the Line Items "Process Steps" column.
  // useLineSteps reads a part no job carries yet from its routing, once per part; the phone order
  // screen runs the same hook. shopDay = the shop's day for overdue manifests (Production Detail too).
  const { lineStepsMap, shopDay } = useLineSteps(
    detailData?.lines,
    detailData?.production_data,
    detailData?.header?.sales_order_id ?? null,
  );

  const yieldViolations = useMemo(() => {
    if (!detailData?.lines || !detailData?.header) return [];

    const activePolicy = yieldPolicies.find((p) => p.policy_id === detailData.header.yield_policy_id);
    if (!activePolicy || activePolicy.min_required_type !== "Ordered") return [];

    const flatLines = flattenTruthLines(detailData.lines);
    const violations = [];

    flatLines.forEach((line) => {
      if (!line || line.consumable_id) return;

      const partId = line.part_id;
      if (!partId) return;

      const defects = safeNum(lineDefects[line.sales_order_line_id] ?? activeDefectMap[partId]);
      // The line's own share (as renderLineItems shows it): a part split across
      // assembly builds no longer hides a short line behind the full pool.
      const netActive = safeNum(line.qty_in_production);
      const shippedGood = safeNum(line.qty_shipped);
      const ordered = safeNum(line.quantity_ordered);

      // qty_in_production already has defects subtracted by the API
      const netYield = netActive + shippedGood;

      if (ordered > 0 && netYield < ordered) {
        violations.push({
          sales_order_line_id: line.sales_order_line_id,
          part_id: partId,
          product_id: line.product_id,
          description: line.description,
          quantity_ordered: ordered,
          qty_in_production: netActive + defects,
          qty_shipped: shippedGood,
          defects,
          netYield,
          shortage: ordered - netYield,
        });
      }
    });

    return violations;
  }, [detailData?.lines, detailData?.header, activeDefectMap, yieldPolicies, lineDefects]);

  /**
   * --------------------
   * Contacts
   * --------------------
   */
  const handleOpenContactModal = async () => {
    if (!detailData?.header?.customer_id) return;
    setShowContactModal(true);
    setIsCreatingContact(false);
    try {
      const res = await fetch(`${API_BASE}/customers/${detailData.header.customer_id}/contacts`);
      if (res.ok) setCustomerContacts(await res.json());
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const handleCreateContact = async () => {
    if (!newContact.name) return alert("Name is required");
    try {
      const res = await fetch(`${API_BASE}/customers/${detailData.header.customer_id}/contacts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newContact),
      });
      if (res.ok) {
        const created = await res.json();
        setCustomerContacts((prev) => [...prev, created]);
        setIsCreatingContact(false);
        setNewContact({ name: "", email: "", phone: "", role: "General" });
        setContactRoles((prev) => ({ ...prev, order: prev.order || created.contact_id }));
      } else {
        alert("Failed to create contact");
      }
    } catch (error) {
      console.error("Error creating contact:", error);
    }
  };

  const handleSaveContacts = async () => {
    try {
      const payload = {
        order_contact_id: contactRoles.order || null,
        billing_contact_id: contactRoles.billing || null,
        shipping_contact_id: contactRoles.shipping || null,
      };

      const res = await fetch(`${API_BASE}/sales_orders/${selectedId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setShowContactModal(false);
        const truthRes = await fetch(`${API_BASE}/sales_orders/${selectedId}/truth`);
        const truth = await truthRes.json();
        setDetailData((prev) => ({ ...prev, header: truth.header }));
      } else {
        alert("Failed to update contacts.");
      }
    } catch (error) {
      console.error("Error saving contacts:", error);
    }
  };

  // --- Superseded part revision: update the SO line (+ source quote) in place ---
  // Same SO number, same quote number, same price — only the part revision
  // linkage moves. Server refuses lines whose production has begun.
  const handleUpdateLineRevision = async (line) => {
    const curPartId = line?.current_revision_part_id;
    if (!curPartId) return;
    try {
      const refsRes = await fetch(`${API_BASE}/parts/${curPartId}/open_order_references`);
      const refs = await refsRes.json().catch(() => ({}));
      if (!refsRes.ok) { alert(`Failed to check references: ${detailToString(refs.detail) || refsRes.status}`); return; }
      const soRef = (refs.so_lines || []).find(r => r.so_line_id === line.sales_order_line_id);
      if (!soRef) { alert("This line no longer references a superseded revision — refresh the page."); return; }
      if (!soRef.eligible) { alert(`Cannot update this line:\n${soRef.reason}`); return; }

      const quoteId = detailData?.header?.quote_id;
      const quoteRefs = (refs.quote_lines || []).filter(r => r.eligible && quoteId && r.quote_id === quoteId);

      let msg = `Update ${line.product_id} on this SO from Rev ${soRef.old_revision_code} to Rev ${refs.part.revision_code}?\n\n` +
        `• SO line stays on ${soRef.order_number} at the same price`;
      if (quoteRefs.length > 0) msg += `\n• Source quote #${quoteId} will be updated too`;
      const cutSheets = refs.cut_sheets_on_old_rev || [];
      if (cutSheets.length > 0) {
        msg += `\n\n⚠ Cut sheet(s) still nest the OLD revision: ${cutSheets.map(c => c.cut_sheet_name).join(', ')} — import the updated nest as a cut-sheet revision before cutting.`;
      }
      if (!window.confirm(msg)) return;

      const pin = await pinPrompt("Enter PIN to update the revision on this order:");
      if (!pin) return;

      const res = await fetch(`${API_BASE}/parts/${curPartId}/apply_revision_to_open_orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pin,
          so_line_ids: [line.sales_order_line_id],
          quote_part_ids: quoteRefs.map(r => r.quote_part_id),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { alert(`Update failed: ${detailToString(data.detail) || res.status}`); return; }
      alert(data.message || "Updated.");
      const truthRes = await fetch(`${API_BASE}/sales_orders/${selectedId}/truth`);
      if (truthRes.ok) {
        const truth = await truthRes.json();
        setDetailData((prev) => ({ ...prev, header: truth.header, lines: truth.lines }));
      }
    } catch (err) {
      console.error("Error updating line revision:", err);
      alert("Failed to update the revision.");
    }
  };

  const handleEditPromised = () => {
    const start = promisedEditStart(detailData?.header); // soData (shared with the phone)
    setCompletionMode(start.mode);
    setEditPromisedDate(start.date);
    setEditLeadTime(start.leadTime);
    setIsEditingPromised(true);
  };

  // Turn the API's schedule_propagation summary into a short human note
  // shown under the date fields (and returns it for optional alerting).
  const applyPropagationNote = (data) => {
    setSchedulePropNote(propagationNote(data));
    return data?.schedule_propagation || null;
  };

  const handleSavePromised = async () => {
    try {
      const saved = await saveOrderFields({
        soId: selectedId,
        patch: promisedPayload(completionMode, editLeadTime, editPromisedDate),
        failure: QUICK_EDIT_FAILURE.promised,
        ui: desktopUi,
        onSaved: (data) => { applyPropagationNote(data); setIsEditingPromised(false); },
      });
      if (saved.ok) {
        if (saved.header) setDetailData((prev) => ({ ...prev, header: saved.header }));
        fetchOrders();
      }
    } catch (error) {
      console.error("Error saving promised date:", error);
    }
  };

  const handleEditTarget = () => {
    const t = detailData?.header?.target_completion_date;
    setEditTargetDate(t ? t.slice(0, 10) : '');
    setIsEditingTarget(true);
  };

  // Save (or clear, with null) the internal target completion date. The API
  // propagates COALESCE(target, promised) to the SO's open jobs + tasks.
  const handleSaveTarget = async (dateValue) => {
    try {
      const saved = await saveOrderFields({
        soId: selectedId,
        patch: { target_completion_date: dateValue || null },
        failure: QUICK_EDIT_FAILURE.target,
        ui: desktopUi,
        onSaved: (data) => { applyPropagationNote(data); setIsEditingTarget(false); },
      });
      if (saved.ok) {
        if (saved.header) setDetailData((prev) => ({ ...prev, header: saved.header }));
        fetchOrders();
      }
    } catch (error) {
      console.error("Error saving target date:", error);
    }
  };

  const handleEditPO = () => {
    setEditPOValue(detailData?.header?.customer_po || '');
    setIsEditingPO(true);
  };

  const handleEditSO = () => {
    setEditSOValue(detailData?.header?.customer_so || '');
    setIsEditingSO(true);
  };

  const handleSaveSO = async () => {
    try {
      const res = await fetch(`${API_BASE}/sales_orders/${selectedId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer_so: editSOValue || null }),
      });
      if (res.ok) {
        setIsEditingSO(false);
        const truthRes = await fetch(`${API_BASE}/sales_orders/${selectedId}/truth`);
        const truth = await truthRes.json();
        setDetailData((prev) => ({ ...prev, header: truth.header }));
        fetchOrders();
      } else {
        alert("Failed to update Cust SO.");
      }
    } catch (e) {
      alert("Error updating Cust SO.");
    }
  };

  const handleSavePO = async () => {
    try {
      const saved = await saveOrderFields({
        soId: selectedId,
        patch: { customer_po: editPOValue || null },
        failure: QUICK_EDIT_FAILURE.customerPo,
        ui: desktopUi,
        onSaved: () => setIsEditingPO(false),
      });
      if (saved.ok) {
        if (saved.header) setDetailData((prev) => ({ ...prev, header: saved.header }));
        fetchOrders();
      }
    } catch (error) {
      console.error("Error saving Customer PO:", error);
    }
  };

  // Change the SO's fulfillment_type. Drives mode-aware UI on the
  // Packaging Plan card (pickup orders use Stage/Confirm Pickup workflow
  // instead of Ship/Finalize Shipment) and the email subject wording.
  const handleSaveFulfillmentType = async (newType) => {
    if (!selectedId || !newType) return;
    try {
      const res = await fetch(`${API_BASE}/sales_orders/${selectedId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fulfillment_type: newType }),
      });
      if (res.ok) {
        const truthRes = await fetch(`${API_BASE}/sales_orders/${selectedId}/truth`);
        const truth = await truthRes.json();
        setDetailData((prev) => ({ ...prev, header: truth.header }));
        fetchOrders();
      } else {
        const body = await res.json().catch(() => ({}));
        alert(`Failed to update fulfillment type: ${detailToString(body.detail) || res.status}`);
      }
    } catch (error) {
      alert(`Error updating fulfillment type: ${error.message}`);
    }
  };

  // Change the SO's selected ship-to address (drives the destination on
  // manifests, labels, and the acknowledgement PDF). null = customer billing addr.
  const handleSaveShippingAddress = async (newAddressId) => {
    if (!selectedId) return;
    try {
      const res = await fetch(`${API_BASE}/sales_orders/${selectedId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shipping_address_id: newAddressId }),
      });
      if (res.ok) {
        const truthRes = await fetch(`${API_BASE}/sales_orders/${selectedId}/truth`);
        const truth = await truthRes.json();
        setDetailData((prev) => ({ ...prev, header: truth.header }));
        fetchOrders();
      } else {
        const body = await res.json().catch(() => ({}));
        alert(`Failed to update ship-to address: ${detailToString(body.detail) || res.status}`);
      }
    } catch (error) {
      alert(`Error updating ship-to address: ${error.message}`);
    }
  };

  // ── Archive / Restore / Permanent Delete ──────────────────────────────────
  const handleArchiveSO = async () => {
    if (!selectedId || !detailData?.header) return;
    const soNum = detailData.header.order_number;
    const reason = prompt(
      `Archive ${soNum}?\n\n` +
      `This hides it from active SO lists, the shipping queue and the invoicing ` +
      `dashboard's Booked Revenue, cancels any not-yet-started jobs, and releases ` +
      `their reserved material. The order stays fully restorable.\n\n` +
      `Optional reason (e.g. "customer cancelled"):`,
      ""
    );
    if (reason === null) return; // dialog cancelled
    const pin = await pinPrompt(`Enter your 4-digit PIN to archive ${soNum}.`);
    if (!pin) return;
    try {
      const res = await fetch(`${API_BASE}/sales_orders/${selectedId}/archive`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, reason: reason || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        const n = (body.jobs_cancelled || []).length;
        alert(`${soNum} archived${n ? ` — ${n} not-yet-started job(s) cancelled and their material released` : ""}.`);
        setSelectedId(null);
        fetchOrders();
      } else {
        alert(`Could not archive ${soNum}: ${detailToString(body.detail) || res.status}`);
      }
    } catch (e) {
      alert(`Error archiving ${soNum}: ${e.message}`);
    }
  };

  const handleRestoreSO = async () => {
    if (!selectedId || !detailData?.header) return;
    const soNum = detailData.header.order_number;
    const pin = await pinPrompt(`Enter your 4-digit PIN to restore ${soNum}.`);
    if (!pin) return;
    try {
      const res = await fetch(`${API_BASE}/sales_orders/${selectedId}/restore`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        const truthRes = await fetch(`${API_BASE}/sales_orders/${selectedId}/truth`);
        const truth = await truthRes.json();
        setDetailData((prev) => ({ ...prev, header: truth.header }));
        fetchOrders();
        alert(`${soNum} restored. Note: jobs cancelled at archive time stay cancelled — re-release production if the order resumes.`);
      } else {
        alert(`Could not restore ${soNum}: ${detailToString(body.detail) || res.status}`);
      }
    } catch (e) {
      alert(`Error restoring ${soNum}: ${e.message}`);
    }
  };

  const handleDeleteSO = async () => {
    if (!selectedId || !detailData?.header) return;
    const soNum = detailData.header.order_number;
    const typed = prompt(
      `PERMANENTLY DELETE ${soNum}?\n\n` +
      `This CANNOT be undone. It is only allowed when the order has no invoices, ` +
      `nothing shipped, and no production started. All line items, draft jobs, the ` +
      `packaging plan, documents and email history are destroyed.\n\n` +
      `To archive instead (reversible), cancel this and use Archive.\n\n` +
      `Type ${soNum} to confirm permanent deletion:`,
      ""
    );
    if (typed === null) return;
    if ((typed || "").trim() !== soNum) {
      alert(`Confirmation text did not match "${soNum}". Delete cancelled.`);
      return;
    }
    const pin = await pinPrompt(`Enter your 4-digit PIN to permanently delete ${soNum}.`);
    if (!pin) return;
    try {
      const res = await fetch(`${API_BASE}/sales_orders/${selectedId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin, confirm: true }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        // The API's message says whether the quote went back to Approved (Phase 0).
        alert(soDeletedAlertText(soNum, body));
        setSelectedId(null);
        fetchOrders();
      } else {
        alert(`Could not delete ${soNum}: ${detailToString(body.detail) || res.status}`);
      }
    } catch (e) {
      alert(`Error deleting ${soNum}: ${e.message}`);
    }
  };

  /**
   * --------------------
   * Line editing
   * --------------------
   */
  const handleEditLines = () => {
    if (!detailData?.lines) return;
    const flatLines = flattenTruthLines(detailData.lines);
    // A part on several lines: /truth splits its In Prod across them, so one line can read 0 while
    // the part is in production. The API refuses to remove or change ANY line of a part with jobs
    // ("Cannot remove line; Jobs already created for this part"), so the lock reads the part's total.
    const partInProd = {};
    flatLines.forEach((l) => {
      if (l.part_id != null) partInProd[l.part_id] = safeNum(partInProd[l.part_id]) + safeNum(l.qty_in_production);
    });

    setEditedLines(
      flatLines.map((line) => ({
        so_line_id: line.sales_order_line_id,
        part_id: line.part_id,
        consumable_id: line.consumable_id ?? "",
        product_id: line.product_id,
        description: line.description,
        quantity_ordered: line.quantity_ordered,
        unit_price: line.unit_price,
        // Carried for the read-only cost/margin cells in the edit grid (F18).
        unit_cost: line.unit_cost ?? line.unit_cost_snapshot ?? null,
        price_source: line.price_source ?? null,
        qty_in_production: line.qty_in_production,
        part_in_production: line.part_id != null ? partInProd[line.part_id] : line.qty_in_production,
        qty_shipped: line.qty_shipped,
        is_new: false,
      }))
    );
    setIsEditingLines(true);
  };

  const handleAddLine = () => {
    setEditedLines((prev) => [
      ...prev,
      {
        so_line_id: null,
        part_id: "",
        consumable_id: "",
        description: "",
        quantity_ordered: 1,
        unit_price: 0.0,
        qty_in_production: 0,
        qty_shipped: 0,
        is_new: true,
      },
    ]);
  };

  const handleRemoveLine = (index) => {
    const line = editedLines[index];
    if (line.is_new) {
      setEditedLines((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    if (safeNum(line.part_in_production ?? line.qty_in_production) > 0) {
      alert("Cannot remove this line because production has started.");
      return;
    }

    const newLines = [...editedLines];
    newLines[index].remove = true;
    setEditedLines(newLines);
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...editedLines];
    newLines[index][field] = value;
    setEditedLines(newLines);
  };

  // NEW rows only (F45): a typed unit price is sent as `unit_price` and the API
  // records it as a manual price. `price_touched` lets the grid say whether the
  // figure in the box is still the catalog price or an operator's number.
  const handleNewLinePriceChange = (index, value) => {
    const newLines = [...editedLines];
    newLines[index].unit_price = value;
    newLines[index].price_touched = true;
    setEditedLines(newLines);
  };

  const handlePartSelect = (index, part) => {
    const newLines = [...editedLines];
    newLines[index].part_id = part.part_id;
    newLines[index].product_id = part.product_id;

    if (!newLines[index].description || newLines[index].is_new) {
      newLines[index].description = part.description;
    }
    if (newLines[index].is_new && !newLines[index].price_touched) {
      // Seed with the catalog price (may legitimately be 0.00 — the grid then
      // says so instead of silently keeping a stale number).
      newLines[index].unit_price = part.price ?? 0;
    } else if (part.price) {
      newLines[index].unit_price = part.price;
    }

    setEditedLines(newLines);
  };

  const handlePolicyChange = async (newPolicyId) => {
    try {
      const res = await fetch(`${API_BASE}/sales_orders/${selectedId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ yield_policy_id: newPolicyId }),
      });
      if (res.ok) {
        setDetailData((prev) => ({
          ...prev,
          header: { ...prev.header, yield_policy_id: newPolicyId },
        }));
      }
    } catch (error) {
      console.error("Error updating policy", error);
    }
  };

  const handleSaveLines = async () => {
    try {
      const payload = {
        lines: editedLines.map((line) => {
          if (line.remove) return { so_line_id: line.so_line_id, remove: true };

          const partId = line.part_id !== "" && line.part_id != null ? parseInt(line.part_id, 10) : null;
          const consumableId = line.consumable_id !== "" && line.consumable_id != null ? parseInt(line.consumable_id, 10) : null;

          return {
            so_line_id: line.is_new ? null : line.so_line_id,
            part_id: partId,
            consumable_id: consumableId,
            description: line.description,
            quantity_ordered: parseInt(line.quantity_ordered, 10),
            // NEW rows may carry a typed price (F45); a cleared box must not
            // serialise as NaN → null. 0 lets the API fall back to the catalog price.
            // A NEW row only carries a unit_price when the operator actually typed one;
            // otherwise the API prices it from the catalog (price_source 'catalog') and
            // the row would wrongly be recorded as a manual price.
            unit_price: (line.is_new && !line.price_touched)
              ? null
              : (Number.isFinite(parseFloat(line.unit_price)) ? parseFloat(line.unit_price) : 0),
          };
        }),
      };

      const res = await fetch(`${API_BASE}/sales_orders/${selectedId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        // The API may return `warnings` (e.g. a new line added at $0.00 with no
        // book price on file) — surface them instead of dropping the body (F45).
        const body = await res.json().catch(() => ({}));
        setLineSaveWarnings(Array.isArray(body?.warnings) ? body.warnings : []);
        setIsEditingLines(false);
        const truthRes = await fetch(`${API_BASE}/sales_orders/${selectedId}/truth`);
        const truth = await truthRes.json();

        const jobs = truth.related_documents?.jobs || [];
        const soCustomerId2 = truth.header?.customer_id;
        const custParam2 = soCustomerId2 ? `?customer_id=${soCustomerId2}` : '';
        const jobProgressPromises = jobs.map((j) =>
          fetch(`${API_BASE}/jobs/${j.job_id}/progress${custParam2}`).then((r) => (r.ok ? r.json() : null))
        );
        const jobProgressResults = await Promise.all(jobProgressPromises);

        const jobSourceMap2 = {};
        jobs.forEach((j) => { jobSourceMap2[j.job_id] = j; });
        setDetailData({
          ...truth,
          production_data: jobProgressResults.filter((j) => j !== null).map((j) => ({
            ...j,
            master_job_num: jobSourceMap2[j.job_id]?.master_job_num ?? null,
            _source: jobSourceMap2[j.job_id]?.source || "direct",
            _source_so_id: jobSourceMap2[j.job_id]?.source_so_id || null,
            _source_so_number: jobSourceMap2[j.job_id]?.source_so_number || null,
          })),
        });
      } else {
        const err = await res.json();
        alert(`Failed to update: ${detailToString(err.detail)}`);
      }
    } catch (error) {
      console.error("Update failed:", error);
      alert("Network error updating lines.");
    }
  };

  const handleExportPDF = async () => {
    if (!selectedId) return;
    try {
      await openPdf(`${API_BASE}/sales_orders/${selectedId}/pdf`);
    } catch (error) {
      console.error("Export failed:", error);
      alert("Failed to export PDF.");
    }
  };

  const handleEmailSO = async () => {
    if (!selectedId || !detailData) return;
    const contactName = detailData.header.order_contact_name || "the customer";
    const message = prompt(
      `Send Sales Order to ${contactName}?\n\nEnter optional message body:`,
      "Please find the attached Sales Order for your review."
    );
    if (message !== null) {
      try {
        const res = await fetch(`${API_BASE}/sales_orders/${selectedId}/email`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...emailSenderHeaders() },
          body: JSON.stringify({ email_body: message }),
        });
        if (res.ok) {
          const result = await res.json();
          alert(`Success: ${result.message}`);
          // Update header with emailed info
          setDetailData(prev => prev ? {
            ...prev,
            header: {
              ...prev.header,
              last_emailed_at: result.sent_at,
              email_count: (prev.header.email_count || 0) + 1,
            }
          } : prev);
        } else {
          const err = await res.json();
          alert(`Error: ${detailToString(err.detail) || "Failed to send email."}`);
        }
      } catch (error) {
        console.error("Email failed:", error);
        alert("Network error sending email.");
      }
    }
  };

  // Compact delivery-risk badge shown on each row in the SO list. Mirrors
  // the Delivery & Progress card's risk_level palette + labels so operators
  // build one mental model across list + detail views. Derived client-side
  // from the list-row fields (status + promised_date + est_complete_date)
  // since the detail-view `progressData` isn't fetched for list rows.
  const LIST_RISK_PALETTE = {
    on_track: { bg: 'bg-success',   text: 'text-white',  icon: 'bi-check-circle-fill',     label: 'On Track' },
    at_risk:  { bg: 'bg-warning',   text: 'text-dark',   icon: 'bi-exclamation-triangle-fill', label: 'At Risk' },
    behind:   { bg: 'bg-danger',    text: 'text-white',  icon: 'bi-x-circle-fill',         label: 'Behind' },
    overdue:  { bg: 'bg-danger',    text: 'text-white',  icon: 'bi-alarm-fill',            label: 'Overdue' },
    complete: { bg: 'bg-success',   text: 'text-white',  icon: 'bi-box-seam',              label: 'Complete' },
    no_date:  { bg: 'bg-secondary', text: 'text-white',  icon: 'bi-calendar-x',            label: 'No Date' },
  };
  const computeListRiskLevel = (order) => listRiskLevel(order); // features/salesOrders/soData.js

  // The list's filter options and the filtered list: features/salesOrders/soData.js (shared with the phone).
  const availableCustomers = useMemo(() => customersFromOrders(orders), [orders]);
  const availableStatuses = useMemo(() => stagesFromOrders(orders), [orders]);
  const filteredOrders = useMemo(
    () => filterOrders(orders, { searchTerm, statusFilter, customerFilter, tmPendingOnly }),
    [orders, searchTerm, statusFilter, customerFilter, tmPendingOnly],
  );

  const toggleStatusFilter = (status) => {
    setStatusFilter(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const handleCreateInvoice = async () => {
    if (!detailData || !detailData.header) return;
    const { header } = detailData;
    const tm = isTm(header);

    if (tm) {
      // T&M: one close-out invoice, only once pricing is approved (the server enforces both).
      const gate = tmInvoiceGate(header, detailData.related_documents);
      if (gate.disabled) { alert(gate.title); return; }
      if (!window.confirm(`Create the close-out invoice for ${header.order_number}: ${fmtMoney(header.total)} (approved)?`)) return;
    } else {
      // Warn if the SO already has at least one non-voided invoice. Voided
      // invoices are excluded — those don't represent actual billing and a
      // new invoice replacing them is normal.
      const existingInvoices = (detailData.related_documents?.invoices || [])
        .filter((inv) => {
          const s = (inv.status || '').toLowerCase();
          return s !== 'voided' && s !== 'void' && s !== 'cancelled';
        });

      if (existingInvoices.length > 0) {
        const list = existingInvoices
          .map((inv) => `  • #${inv.invoice_number} — ${inv.status} — $${Number(inv.total || 0).toFixed(2)}`)
          .join('\n');
        const proceed = window.confirm(
          `Order #${header.order_number} already has ${existingInvoices.length} active invoice(s):\n\n${list}\n\n` +
          `Creating another invoice may result in duplicate billing for the same shipped parts.\n\n` +
          `Click OK to create another invoice anyway, or Cancel to abort.`
        );
        if (!proceed) return;
      } else if (!window.confirm(`Generate a new invoice for Order #${header.order_number}?`)) {
        return;
      }
    }

    // Fixed-price: the body this page has always sent. T&M: {customer_id, sales_order_id, notes}.
    const payload = soInvoicePayload(header, detailData.related_documents, tm);

    try {
      const res = await fetch(`${API_BASE}/invoices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const result = await res.json();
        alert(`Success: Invoice #${result.invoice_number} created.`);
        window.location.reload();
      } else {
        const err = await res.json();
        alert(`Error creating invoice: ${detailToString(err.detail) || "Unknown error"}`);
      }
    } catch (error) {
      console.error("Invoice creation failed:", error);
      alert("Network error. Please check your connection.");
    }
  };

  /**
   * --------------------
   * Contact modal
   * --------------------
   */
  const renderContactModal = () => {
    if (!showContactModal) return null;

    return (
      <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Manage Points of Contact</h5>
              <button type="button" className="btn-close" onClick={() => setShowContactModal(false)}></button>
            </div>

            <div className="modal-body">
              {isCreatingContact ? (
                <div className="bg-light p-3 rounded border">
                  <h6 className="fw-bold mb-3">Add New Contact</h6>
                  <div className="mb-2">
                    <label className="form-label small text-muted">Name</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={newContact.name}
                      onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                    />
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <label className="form-label small text-muted">Email</label>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small text-muted">Phone</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={newContact.phone}
                        onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small text-muted">Role</label>
                    <select
                      className="form-select form-select-sm"
                      value={newContact.role}
                      onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                    >
                      <option value="General">General</option>
                      <option value="Billing">Billing</option>
                      <option value="Shipping">Shipping</option>
                      <option value="Purchasing">Purchasing</option>
                    </select>
                  </div>
                  <div className="d-flex justify-content-end gap-2">
                    <button className="btn btn-sm btn-secondary" onClick={() => setIsCreatingContact(false)}>
                      Cancel
                    </button>
                    <button className="btn btn-sm btn-success" onClick={handleCreateContact}>
                      Create & Select
                    </button>
                  </div>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  <div>
                    <label className="form-label small fw-bold text-muted">Order Contact (General)</label>
                    <select
                      className="form-select"
                      value={contactRoles.order}
                      onChange={(e) => setContactRoles({ ...contactRoles, order: e.target.value })}
                    >
                      <option value="">Select Contact...</option>
                      {customerContacts.map((c) => (
                        <option key={c.contact_id} value={c.contact_id}>
                          {c.name} ({c.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label small fw-bold text-muted">Billing Contact</label>
                    <select
                      className="form-select"
                      value={contactRoles.billing}
                      onChange={(e) => setContactRoles({ ...contactRoles, billing: e.target.value })}
                    >
                      <option value="">Same as Order Contact</option>
                      {customerContacts.map((c) => (
                        <option key={c.contact_id} value={c.contact_id}>
                          {c.name} ({c.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label small fw-bold text-muted">Shipping Contact</label>
                    <select
                      className="form-select"
                      value={contactRoles.shipping}
                      onChange={(e) => setContactRoles({ ...contactRoles, shipping: e.target.value })}
                    >
                      <option value="">Same as Order Contact</option>
                      {customerContacts.map((c) => (
                        <option key={c.contact_id} value={c.contact_id}>
                          {c.name} ({c.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="text-center mt-2">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => setIsCreatingContact(true)}>
                      <i className="bi bi-plus-lg me-1"></i> Add New Contact
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowContactModal(false)}>
                Close
              </button>
              {!isCreatingContact && (
                <button type="button" className="btn btn-primary" onClick={handleSaveContacts}>
                  Save Changes
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Completion summary shared by the Delivery card + the header date block.
  // When production is complete we surface the ACTUAL finish date instead of a
  // forever-growing "Nd past due". Most-accurate date = the latest SHIP date
  // (stable, stamped at ship time); if the SO is complete but not yet shipped,
  // fall back to the latest completed production task's updated_at. Variance vs
  // the promised date drives the early / on-time / late label (+ = late).
  // The API's delivery risk, held back from "complete" / "on track" while a line still needs
  // jobs (features/salesOrders/deliveryRisk.js: SO-1138 read Production Complete with a line
  // that had no job).
  const deliveryRisk = effectiveDeliveryRisk(progressData?.delivery_risk, yieldViolations);

  const getCompletionInfo = () => {
    const dr = deliveryRisk;
    const isComplete = dr?.risk_level === "complete";
    const toDateStr = (v) => (v ? String(v).slice(0, 10) : null); // YYYY-MM-DD from a date or ISO string

    const shipDates = (detailData?.related_documents?.shipments || [])
      .map((s) => toDateStr(s.shipped_date))
      .filter(Boolean)
      .sort();
    const latestShip = shipDates.length ? shipDates[shipDates.length - 1] : null;

    // Fallback when complete but not yet shipped: latest completed production task.
    const completedDates = (detailData?.production_data || [])
      .flatMap((j) => j.tasks || [])
      .filter((t) => t.status === "Completed" && t.updated_at)
      .map((t) => toDateStr(t.updated_at))
      .filter(Boolean)
      .sort();
    const lastProdDate = completedDates.length ? completedDates[completedDates.length - 1] : null;

    const actualDate = latestShip || lastProdDate || null;
    const promised = toDateStr(dr?.promised_date) || toDateStr(detailData?.header?.promised_date) || null;

    let varianceDays = null;
    if (actualDate && promised) {
      varianceDays = Math.round(
        (new Date(actualDate + "T00:00:00Z") - new Date(promised + "T00:00:00Z")) / 86400000
      );
    }

    let label = null;
    if (varianceDays != null) {
      const n = Math.abs(varianceDays);
      const unit = n === 1 ? "day" : "days";
      label = varianceDays === 0 ? "on time" : varianceDays > 0 ? `${n} ${unit} late` : `${n} ${unit} early`;
    }

    return { isComplete, actualDate, latestShip, promised, varianceDays, label };
  };

  /**
   * --------------------
   * Header
   * --------------------
   */
  const renderHeader = () => {
    if (!detailData) return null;
    const { header } = detailData;

    const renderContactLine = (label, name, email, icon) => (
      <div className="d-flex align-items-center mb-1 text-truncate">
        <i className={`bi ${icon} me-2 text-primary opacity-75`}></i>
        <div className="lh-1">
          <div className="small fw-bold">{label}</div>
          <div className="small text-muted text-truncate" style={{ maxWidth: "200px" }}>
            {name || <span className="fst-italic text-warning-emphasis">Not Assigned</span>}
            {name && email && <span className="ms-1 opacity-75">&lt;{email}&gt;</span>}
          </div>
        </div>
      </div>
    );

    // F50: Lines subtotal / Shipping / Fees / Discount / Tax / Order Total from
    // the header fields; `total` is the STORED sales_orders.total and the block
    // says when it no longer reproduces from the other five.
    const totals = headerTotals(header);

    return (
      <div className="card mb-3 shadow-sm border-primary border-top-0 border-end-0 border-bottom-0 border-4">
        {yieldViolations.length > 0 && (
          <div className="alert alert-danger m-3 d-flex align-items-center mb-0">
            <i className="bi bi-exclamation-triangle-fill me-3 fs-3"></i>
            <div>
              <strong>Yield Policy Violation Detected</strong>
              <div className="small">
                {yieldViolations.length} line item(s) are projected to yield below the ordered quantity. The order's
                yield policy requires meeting the ordered quantity — please release more jobs or review defective parts.
              </div>
            </div>
          </div>
        )}

        {header.archived_at && (
          <div className="alert alert-secondary m-3 mb-0 d-flex align-items-center">
            <i className="bi bi-archive-fill me-3 fs-3"></i>
            <div className="flex-grow-1">
              <strong>This sales order is archived</strong>
              <div className="small">
                Archived {new Date(header.archived_at).toLocaleString()}{header.archived_by ? ` by ${header.archived_by}` : ""}.
                {header.archive_reason ? ` Reason: ${header.archive_reason}.` : ""}
                {" "}It's hidden from active SO lists, Booked Revenue and the shipping queue. Restoring won't recreate cancelled jobs.
              </div>
            </div>
            <button className="btn btn-sm btn-success ms-3 text-nowrap" onClick={handleRestoreSO}>
              <i className="bi bi-arrow-counterclockwise me-1"></i> Restore
            </button>
          </div>
        )}

        <ErrorBoundary label="The T&M banner">
          <TmSoBanner
            header={header}
            related={detailData.related_documents}
            onApprove={() => setTmPricingOpen(true)}
            onReopen={() => setTmReopenOpen(true)}
            blockedReason={isEditingLines ? 'Save or cancel the line edits first' : null}
          />
        </ErrorBoundary>

        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h2 className="card-title fw-bold mb-0">{header.order_number}</h2>
              <h5 className="text-muted mb-0">{header.customer_name}</h5>
              {header.quote_id && (
                <div className="small mt-1">
                  <span className="text-muted">From Quote:</span>{' '}
                  <Link to={`/quotes?id=${header.quote_id}`} className="text-decoration-none">
                    Q-{header.quote_id}
                  </Link>
                </div>
              )}
            </div>

            <div className="text-end">
              <div className="d-flex gap-2 justify-content-end mb-2">
                <button className="btn btn-sm btn-outline-success" onClick={handleCreateInvoice} title={tmInvoiceGate(header, detailData.related_documents).title} disabled={tmInvoiceGate(header, detailData.related_documents).disabled}>
                  <i className="bi bi-receipt me-1"></i> Create Invoice
                </button>
                <button className="btn btn-sm btn-outline-danger" onClick={handleExportPDF} title="Download PDF">
                  <i className="bi bi-file-earmark-pdf me-1"></i> Export PDF
                </button>
                <button className="btn btn-sm btn-outline-primary" onClick={handleEmailSO} title="Email to Contact">
                  <i className="bi bi-envelope-at me-1"></i> Email SO
                </button>
                {header.archived_at ? (
                  <button className="btn btn-sm btn-success" onClick={handleRestoreSO} title="Restore this archived order">
                    <i className="bi bi-arrow-counterclockwise me-1"></i> Restore
                  </button>
                ) : (
                  <button className="btn btn-sm btn-outline-secondary" onClick={handleArchiveSO} title="Archive — hide & free reserved material; fully restorable">
                    <i className="bi bi-archive me-1"></i> Archive
                  </button>
                )}
                <button className="btn btn-sm btn-outline-danger" onClick={handleDeleteSO} title="Permanently delete — only allowed pre-production & un-invoiced">
                  <i className="bi bi-trash me-1"></i> Delete
                </button>
              </div>
              <h3>
                <StatusBadge status={header.status} />
                <TmSoStatusBadge header={header} />
              </h3>
              <div className="text-muted small">
                Created: {new Date(header.created_at).toLocaleDateString()}
                {header.last_emailed_at && (
                  <span className="ms-2 text-primary" style={{ cursor: 'pointer' }} onClick={() => setShowEmailHistory(true)} title="View email history">
                    &bull; Emailed: {new Date(header.last_emailed_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>

          <hr className="my-2" />

          <div className="row g-3">
            <div className="col-md-3 d-flex flex-column">
              <div className="mb-3">
                <label className="small text-muted fw-bold text-uppercase d-flex align-items-center gap-2">
                  Customer PO
                  {!isEditingPO && (
                    <i className="bi bi-pencil-square text-primary" role="button" title="Edit" onClick={handleEditPO} style={{ fontSize: '0.85rem', cursor: 'pointer' }}></i>
                  )}
                </label>
                {isEditingPO ? (
                  <div className="d-flex gap-2 mt-1">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={editPOValue}
                      onChange={(e) => setEditPOValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSavePO(); if (e.key === 'Escape') setIsEditingPO(false); }}
                      autoFocus
                    />
                    <button className="btn btn-sm btn-primary" onClick={handleSavePO}>Save</button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setIsEditingPO(false)}>Cancel</button>
                  </div>
                ) : (
                  <div className="fs-5">{header.customer_po || "N/A"}</div>
                )}
              </div>
              <div className="mb-3">
                <label className="small text-muted fw-bold text-uppercase d-flex align-items-center gap-2">
                  Cust SO
                  {!isEditingSO && (
                    <i className="bi bi-pencil-square text-primary" role="button" title="Edit" onClick={handleEditSO} style={{ fontSize: '0.85rem', cursor: 'pointer' }}></i>
                  )}
                </label>
                {isEditingSO ? (
                  <div className="d-flex gap-2 mt-1">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={editSOValue}
                      onChange={(e) => setEditSOValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSO(); if (e.key === 'Escape') setIsEditingSO(false); }}
                      autoFocus
                    />
                    <button className="btn btn-sm btn-primary" onClick={handleSaveSO}>Save</button>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setIsEditingSO(false)}>Cancel</button>
                  </div>
                ) : (
                  <div className="fs-5">{header.customer_so || "N/A"}</div>
                )}
              </div>
              <div className="mb-3">
                <label className="small text-muted fw-bold text-uppercase d-flex align-items-center gap-2">
                  Fulfillment <span className="text-danger">*</span>
                  <i className="bi bi-info-circle text-muted" title="Required. Inherited from the source quote at conversion; can be changed here if the customer arrangement changes. Pickup orders use a two-stage workflow on the Packaging Plan card: Mark Ready for Pickup (stages, sends notification) → Confirm Pickup (when customer arrives)." style={{ fontSize: '0.85rem' }}></i>
                </label>
                <select
                  className={`form-select form-select-sm mt-1 ${!header.fulfillment_type ? 'border-danger' : ''}`}
                  value={header.fulfillment_type || ''}
                  onChange={(e) => {
                    if (!e.target.value) return;  // can't clear back to empty once SO exists
                    handleSaveFulfillmentType(e.target.value);
                  }}
                >
                  {!header.fulfillment_type && <option value="">— Select —</option>}
                  <option value="shipping">Carrier Shipment</option>
                  <option value="pickup">Customer Pickup</option>
                  <option value="delivery">We Deliver</option>
                </select>
              </div>
              <div className="mb-3">
                <label className="small text-muted fw-bold text-uppercase d-flex align-items-center gap-2">
                  Ship To
                  <i className="bi bi-info-circle text-muted" title="Destination for this order's manifests, labels, and acknowledgement. Manage a customer's locations on the Customers page. Falls back to the customer's billing address when left blank." style={{ fontSize: '0.85rem' }}></i>
                </label>
                <select
                  className="form-select form-select-sm mt-1"
                  value={header.shipping_address_id || ''}
                  onChange={(e) => handleSaveShippingAddress(e.target.value ? parseInt(e.target.value, 10) : null)}
                >
                  <option value="">Customer billing address (default)</option>
                  {soAddresses.map(a => (
                    <option key={a.address_id} value={a.address_id}>
                      {a.label}{a.city ? ` — ${a.city}, ${a.state || ''}` : ''}{a.is_default ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
                {header.shipping_address_id && (header.ship_to_address || header.ship_to_city) && (
                  <div className="small text-muted mt-1">
                    {[header.ship_to_address, header.ship_to_address_line2].filter(Boolean).join(', ')}
                    {(header.ship_to_city || header.ship_to_state || header.ship_to_zip) && <>, {[header.ship_to_city, header.ship_to_state].filter(Boolean).join(', ')} {header.ship_to_zip}</>}
                    {header.ship_to_contact_name && <> · Attn: {header.ship_to_contact_name}</>}
                    {header.ship_to_receiving_hours && <><br />🕒 Receiving Hours: {header.ship_to_receiving_hours}</>}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="small text-muted fw-bold text-uppercase d-flex align-items-center gap-2">
                  Promised Completion Date
                  {!isEditingPromised && (
                    <i className="bi bi-pencil-square text-primary" role="button" title="Edit" onClick={handleEditPromised} style={{ fontSize: '0.85rem', cursor: 'pointer' }}></i>
                  )}
                </label>
                {isEditingPromised ? (
                  <div className="p-2 border rounded bg-light mt-1">
                    <div className="d-flex gap-3 mb-2">
                      <div className="form-check">
                        <input className="form-check-input" type="radio" name="soCompletionMode" id="soModeLeadTime"
                          checked={completionMode === 'lead_time'} onChange={() => setCompletionMode('lead_time')} />
                        <label className="form-check-label small" htmlFor="soModeLeadTime">Lead Time</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="radio" name="soCompletionMode" id="soModeDate"
                          checked={completionMode === 'date'} onChange={() => setCompletionMode('date')} />
                        <label className="form-check-label small" htmlFor="soModeDate">Specific Date</label>
                      </div>
                    </div>
                    {completionMode === 'lead_time' ? (
                      <LeadTimeSelector value={editLeadTime} onChange={setEditLeadTime} />
                    ) : (
                      <input type="date" className="form-control form-control-sm" value={editPromisedDate}
                        onChange={(e) => setEditPromisedDate(e.target.value)} />
                    )}
                    <div className="d-flex gap-2 mt-2">
                      <button className="btn btn-sm btn-primary" onClick={handleSavePromised}>Save</button>
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setIsEditingPromised(false)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="fs-5 fw-bold text-primary">
                    {header.promised_date
                      ? new Date(header.promised_date).toLocaleDateString(undefined, { timeZone: "UTC" })
                      : header.lead_time || "Not set"}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="small text-muted fw-bold text-uppercase d-flex align-items-center gap-2">
                  Target Completion (Internal)
                  <i className="bi bi-info-circle text-muted" title="Internal scheduling target. When set, production jobs and tasks are dated to THIS date instead of the promised date — use it to work an order in early (e.g. set it a week ahead, or hit ASAP) without changing the customer promise. Clear it to fall back to the promised date." style={{ fontSize: '0.85rem' }}></i>
                  {!isEditingTarget && (
                    <i className="bi bi-pencil-square text-primary" role="button" title="Edit" onClick={handleEditTarget} style={{ fontSize: '0.85rem', cursor: 'pointer' }}></i>
                  )}
                </label>
                {isEditingTarget ? (
                  <div className="p-2 border rounded bg-light mt-1">
                    <input type="date" className="form-control form-control-sm" value={editTargetDate}
                      onChange={(e) => setEditTargetDate(e.target.value)} />
                    <div className="d-flex gap-2 mt-2 flex-wrap">
                      <button className="btn btn-sm btn-primary" onClick={() => handleSaveTarget(editTargetDate)} disabled={!editTargetDate}>Save</button>
                      <button className="btn btn-sm btn-warning" title="Schedule this order as soon as capacity allows"
                        onClick={() => handleSaveTarget(shopToday())}>⚡ ASAP (today)</button>
                      {header.target_completion_date && (
                        <button className="btn btn-sm btn-outline-danger" title="Clear the target and re-date production back to the promised date"
                          onClick={() => handleSaveTarget(null)}>Clear</button>
                      )}
                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setIsEditingTarget(false)}>Cancel</button>
                    </div>
                  </div>
                ) : header.target_completion_date ? (
                  <div>
                    <div className="fs-5 fw-bold text-info">
                      {new Date(header.target_completion_date).toLocaleDateString(undefined, { timeZone: "UTC" })}
                    </div>
                    <div className="small text-muted">Production schedules to this date (promise unchanged)</div>
                  </div>
                ) : (
                  <div className="text-muted small">— follows promised date</div>
                )}
                {schedulePropNote && (
                  <div className="small text-success mt-1">
                    <i className="bi bi-check-circle me-1"></i>{schedulePropNote}
                  </div>
                )}
              </div>
              {(() => {
                // Once production is complete, show the ACTUAL completion/ship
                // date (green) instead of the forward-looking estimate.
                const ci = getCompletionInfo();
                if (ci.isComplete && ci.actualDate) {
                  const fmt = new Date(ci.actualDate + "T00:00:00Z")
                    .toLocaleDateString(undefined, { timeZone: "UTC" });
                  return (
                    <div className="mb-3">
                      <label className="small text-muted fw-bold text-uppercase">
                        {ci.latestShip ? "Shipped" : "Completed"}
                      </label>
                      <div className="fs-5 fw-bold text-success">
                        {fmt}
                        {ci.label && (
                          <span className="ms-2 small fw-normal text-muted">({ci.label})</span>
                        )}
                      </div>
                    </div>
                  );
                }
                const allDates = (detailData?.production_data || [])
                  .flatMap(j => (j.tasks || []))
                  .map(t => t.scheduled_date)
                  .filter(Boolean);
                if (allDates.length === 0) return null;
                const maxDate = allDates.sort().pop();
                return (
                  <div className="mb-3">
                    <label className="small text-muted fw-bold text-uppercase">Est Complete Date</label>
                    <div className="fs-5 fw-bold text-warning">
                      {new Date(maxDate + "T00:00:00").toLocaleDateString()}
                    </div>
                  </div>
                );
              })()}
              <div>
                <label className="small text-muted fw-bold text-uppercase">Order Totals</label>
                {/* F50: the old block printed the line-sum twice ("Order Total" and
                    "Sub") and never showed shipping/fees/tax/discount or the stored
                    sales_orders.total. Every row is a header field; the last row
                    is the stored total, and the note under it is the formula that
                    should reproduce it. */}
                <table className="table table-sm table-borderless mb-0 small" style={{ width: "auto" }}>
                  <tbody>
                    <tr>
                      <td className="text-muted pe-3 py-0" title="Σ quantity ordered × unit price over the line items">Lines subtotal</td>
                      <td className="text-end py-0">{fmtMoneyShared(totals.subtotal)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted pe-3 py-0">Shipping</td>
                      <td className="text-end py-0">{fmtMoneyShared(totals.shipping)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted pe-3 py-0">Fees</td>
                      <td className="text-end py-0">{fmtMoneyShared(totals.fees)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted pe-3 py-0">Discount</td>
                      <td className="text-end py-0">{totals.discount ? `− ${fmtMoneyShared(totals.discount)}` : fmtMoneyShared(0)}</td>
                    </tr>
                    <tr>
                      <td className="text-muted pe-3 py-0">Tax</td>
                      <td className="text-end py-0">{fmtMoneyShared(totals.tax)}</td>
                    </tr>
                    <tr className="border-top">
                      <td className="fw-bold pe-3 py-0">Order Total</td>
                      <td
                        className="text-end py-0 fs-5 text-success fw-bold"
                        title={`sales_orders.total as stored = ${totals.formula}`}
                      >
                        {fmtMoneyShared(totals.total)}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="text-muted" style={{ fontSize: "0.7rem" }}>= {totals.formula}</div>
                {!totals.reconciles && (
                  <div
                    className="text-warning-emphasis"
                    style={{ fontSize: "0.7rem" }}
                    title="The stored total is rewritten by the API when lines or header amounts change; a mismatch means one side changed without the recalculation."
                  >
                    Stored total {fmtMoneyShared(totals.stored)} ≠ computed {fmtMoneyShared(totals.computed)} ({fmtDelta(totals.stored - totals.computed)}).
                  </div>
                )}
              </div>
              <div className="mt-3">
                <label className="small text-muted fw-bold text-uppercase">
                  Yield & Billing Policy
                  {!header.yield_policy_id && (
                    <span className="ms-2 fs-6" title="Action Required">❗️</span>
                  )}
                </label>
                <select
                  className="form-select form-select-sm mt-1 border-secondary"
                  value={header.yield_policy_id || ""}
                  onChange={(e) => handlePolicyChange(e.target.value ? parseInt(e.target.value, 10) : null)}
                  style={{ fontWeight: "500" }}
                  disabled={isTm(header)}
                  title={isTm(header) ? "T&M orders bill what ships (Ship Any Qty, Bill Shipped): the policy is locked." : undefined}
                >
                  <option value="">Select Policy...</option>
                  {yieldPolicies.map((p) => (
                    <option key={p.policy_id} value={p.policy_id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                {header.yield_policy_id && (
                  <div className="form-text fst-italic" style={{ fontSize: "0.7rem" }}>
                    {yieldPolicies.find((p) => p.policy_id === header.yield_policy_id)?.description}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-4 d-flex flex-column">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="small text-muted fw-bold text-uppercase">Points of Contact</label>
                <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={handleOpenContactModal}>
                  <i className="bi bi-pencil-square"></i> Manage
                </button>
              </div>
              <div className="bg-light p-2 rounded border flex-grow-1">
                {renderContactLine("Order", header.order_contact_name, header.order_contact_email, "bi-person-circle")}
                {renderContactLine("Billing", header.billing_contact_name, header.billing_contact_email, "bi-credit-card")}
                {renderContactLine("Shipping", header.shipping_contact_name, header.shipping_contact_email, "bi-truck")}
              </div>
            </div>

            <div className="col-md-5 d-flex flex-column">
              <label className="small text-muted fw-bold text-uppercase">Notes</label>
              <div className="text-muted fst-italic p-2 bg-light rounded border flex-grow-1" style={{ minHeight: "80px" }}>
                {header.notes || "No notes provided."}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * --------------------
   * Production summary (Smart Billing Logic)
   * --------------------
   */
  const renderProductionSummary = () => {
    if (!summaryData || !detailData) return null;

    const {
      summary_by_station,
      actual_realizable_revenue,    // Integrated from enhanced API
      original_commitment,          // sales_orders.total (lines + shipping + fees + tax − discount)
      // original_cost_estimate / quote_cost_basis / order_subtotal / estimate_splits /
      // not_yet_realizable_lines are read through summaryBases() below (F17, F46, F47, F48).
      grand_total_estimated_cost,   // Recalculated from live rates
      grand_total_estimated_seconds,
      grand_total_actual_seconds,
      grand_total_labor_cost,
      grand_total_consumable_cost_est = 0,
      grand_total_consumable_cost_act,
      grand_total_material_cost,
      // Breakdown sub-fields
      grand_total_consumable_cost_act_gas = 0,
      grand_total_consumable_cost_act_wear = 0,
      // Premature-swap ("incident") attribution — nozzles/lens savers
      // destroyed well before their expected life, billed directly to
      // the laser task that caused the loss. See add_consumable_transaction
      // in jobq-api/main.py. Already folded into grand_total_consumable_cost_act
      // but surfaced separately here for the tooltip breakdown.
      grand_total_consumable_cost_act_incidents = 0,
      grand_total_material_cost_scanned = 0,
      grand_total_material_cost_fallback = 0,
      grand_total_assembly_consumable_cost = 0,
      grand_total_assembly_consumable_cost_est = 0,
      grand_total_purchased_component_cost = 0,
      grand_total_coating_cost_est = 0,     // outsourced coating, estimated (part per-unit + assembly per-set)
      // Outsourced finishing (grand_total_finishing_cost_act / _est / _to_date) is read by
      // accruedFinancials below. `_to_date` is COALESCE(actual, estimated) + freight + tax, so it
      // is the right headline at EVERY stage of the vendor lifecycle.
      finishing_units_at_vendor = 0,
      outside_processing_order_count = 0,
    } = summaryData;

    // Outsourced finishing (invoiced actual, else the committed order value; never both), the
    // accrued cost and the actual profit / margin: utils/salesOrderMath.js accruedFinancials
    // (shared with the phone's order screen).
    const {
      finishingCostToDate, finishingIsInvoiced, actualTotalCost, actualProfit, actualMargin,
    } = accruedFinancials(summaryData);

    const activePolicy = yieldPolicies.find((p) => p.policy_id === detailData.header.yield_policy_id);
    const billingMethod = activePolicy?.bill_type || "Ordered";


    // Projected metrics on LINE revenue (F46). The commitment is
    // sales_orders.total and carries pass-through shipping/fees/tax, so a
    // margin on it booked a $200 shipping line as profit. summaryBases()
    // (src/utils/salesOrderMath.js) uses production_summary.order_subtotal and
    // falls back to the /truth header's line subtotal on an older API; it also
    // pairs the two stored cost bases (F17/F48) and the estimate splits (F47).
    const bases = summaryBases({ summary: summaryData, header: detailData.header });
    const estimatedProfit = bases.est_profit;   // fraction-free dollars, null when unknown
    const estimatedMargin = bases.est_margin;   // FRACTION (fmtPct renders it), null when unknown
    const splits = estimateSplitRows(summaryData.estimate_splits, grand_total_estimated_cost);
    const unrealizable = notYetRealizableRows(summaryData.not_yet_realizable_lines);
    // Wave 2 (decision 5a / F62): the quote engine's per-line estimates behind
    // Recalc Est (line_estimates / estimate_fallback_lines / estimate_basis) and
    // the part-vs-assembly split on the Coating (outsourced) row. Every helper
    // (src/utils/salesOrderMath.js) degrades to "not returned" on an older API.
    const lineEst = lineEstimateRows(
      summaryData.line_estimates,
      summaryData.estimate_fallback_lines,
      detailData.lines,
      {
        recalcEst: grand_total_estimated_cost,
        // what Recalc Est carries outside the per-line list (the API's own reconciliation)
        assemblyConsumables: summaryData.estimate_splits?.assembly_consumables,
        lineRounding: summaryData.line_estimates_rounding,
      },
    );
    const estimateBasis = summaryData.estimate_basis || null;
    const coatingSplit = coatingRowSplit({
      stations: summary_by_station,
      splits: summaryData.estimate_splits,
      grandTotalCoatingEst: grand_total_coating_cost_est,
    });
    // The API may now send its own "Coating (outsourced)" station row (C2.3);
    // this page has always rendered that row itself, so keep it out of the loop.
    const stationRows = stationRowsForTable(summary_by_station);

    let healthColor = "bg-success-subtle text-success-emphasis";
    let badgeColor = "bg-success";
    if (actualMargin < 15) {
      healthColor = "bg-danger-subtle text-danger-emphasis";
      badgeColor = "bg-danger";
    } else if (actualMargin < 30) {
      healthColor = "bg-warning-subtle text-warning-emphasis";
      badgeColor = "bg-warning text-dark";
    }

    return (
      <div className="card mb-4 shadow-sm border-info border-top-0 border-end-0 border-bottom-0 border-4">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-info text-dark">
            <i className="bi bi-graph-up-arrow me-2"></i>Production Financial Summary
          </h5>
          <span className="badge bg-light text-dark border">
              Policy: {activePolicy?.name || "Standard"} ({billingMethod} Billing)
          </span>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Workstation</th>
                  <th className="text-end">Est Time</th>
                  <th className="text-end">Time (Prod / Maint)</th>
                  <th className="text-center">Efficiency</th>
                  <th className="text-end">Labor</th>
                  <th className="text-end">Consumables (Act)</th>
                  <th className="text-end">Material</th>
                  <th className="text-end">Total Cost</th>
                </tr>
              </thead>
              <tbody>
                {stationRows.map((station, idx) => {
                  const effColor =
                    safeNum(station.total_actual_seconds) === 0
                      ? "text-muted"
                      : safeNum(station.efficiency_percent) >= 100
                      ? "text-success fw-bold"
                      : "text-danger";

                  const totalStationAct =
                    safeNum(station.total_labor_cost) + safeNum(station.total_consumable_cost_act) + safeNum(station.total_material_cost);

                  return (
                    <tr key={idx}>
                      <td className="fw-bold">{station.workstation}</td>
                      <td className="text-end text-muted">
                        <TimeDisplay seconds={station.total_estimated_seconds} />
                      </td>
                      <td className="text-end">
                        <div className="d-flex flex-column lh-1">
                          <span className="fw-bold">
                            <TimeDisplay seconds={station.total_actual_seconds} />
                          </span>
                          {safeNum(station.total_maintenance_seconds) > 0 && (
                            <span className="text-muted small fst-italic" style={{ fontSize: "0.75rem" }}>
                              + <TimeDisplay seconds={station.total_maintenance_seconds} /> Maint
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={`text-center ${effColor}`}>
                        {safeNum(station.total_actual_seconds) > 0 ? `${safeNum(station.efficiency_percent).toFixed(0)}%` : "-"}
                      </td>
                      <td className="text-end">
                        <InfoTooltip
                          id={`tt-station-labor-${idx}`}
                          content={<>Time punches × hourly wage rates</>}
                        >
                          <span style={{ cursor: "help", borderBottom: "1px dotted #999" }}>
                            {fmtMoney(station.total_labor_cost)}
                          </span>
                        </InfoTooltip>
                      </td>
                      <td className="text-end">
                        {station.workstation === "Laser" ? (
                          <div className="d-flex flex-column lh-1" style={{ fontSize: "0.85rem" }}>
                            <InfoTooltip
                              id={`tt-station-consumable-${idx}`}
                              content={
                                <>
                                  <div>Gas: {fmtMoney(station.total_consumable_cost_act_gas)}</div>
                                  <div>Wear (Nozzle + Lens): {fmtMoney(station.total_consumable_cost_act_wear)}</div>
                                  {safeNum(station.total_consumable_cost_act_incidents) > 0 && (
                                    <div>⚠ Incidents (premature swaps): {fmtMoney(station.total_consumable_cost_act_incidents)}</div>
                                  )}
                                </>
                              }
                            >
                              <span
                                className={
                                  safeNum(station.total_consumable_cost_act) > safeNum(station.total_consumable_cost_est)
                                    ? "text-danger"
                                    : "text-success"
                                }
                                style={{ cursor: "help", borderBottom: "1px dotted #999" }}
                              >
                                {safeNum(station.total_consumable_cost_act_incidents) > 0 ? "⚠ " : ""}
                                {fmtMoney(station.total_consumable_cost_act)}
                              </span>
                            </InfoTooltip>
                            <span className="text-muted small">Est: {fmtMoney(station.total_consumable_cost_est)}</span>
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-end">
                        <InfoTooltip
                          id={`tt-station-material-${idx}`}
                          content={(() => {
                            const rows = [];
                            if (safeNum(station.total_material_cost_scanned) > 0)
                              rows.push(<div key="po">PO-Matched: {fmtMoney(station.total_material_cost_scanned)}</div>);
                            if (safeNum(station.total_material_cost_fallback) > 0)
                              rows.push(<div key="fb">Fallback Rate: {fmtMoney(station.total_material_cost_fallback)}</div>);
                            if (safeNum(station.total_assembly_consumable_cost) > 0)
                              rows.push(<div key="asy">Assy Components: {fmtMoney(station.total_assembly_consumable_cost)}</div>);
                            return rows.length > 0 ? <>{rows}</> : <>No material cost recorded</>;
                          })()}
                        >
                          <span style={{ cursor: "help", borderBottom: "1px dotted #999" }}>
                            {fmtMoney(station.total_material_cost)}
                          </span>
                        </InfoTooltip>
                      </td>
                      <td className="text-end fw-bold">
                        <InfoTooltip
                          id={`tt-station-total-${idx}`}
                          content={
                            <>
                              <div>Labor: {fmtMoney(station.total_labor_cost)}</div>
                              <div>Consumables: {fmtMoney(station.total_consumable_cost_act)}</div>
                              <div>Material: {fmtMoney(station.total_material_cost)}</div>
                            </>
                          }
                        >
                          <span style={{ cursor: "help", borderBottom: "1px dotted #999" }}>
                            {fmtMoney(totalStationAct)}
                          </span>
                        </InfoTooltip>
                      </td>
                    </tr>
                  );
                })}
                {(safeNum(grand_total_coating_cost_est) > 0
                  || finishingCostToDate > 0
                  || safeNum(outside_processing_order_count) > 0
                  || safeNum(coatingSplit.total) > 0) && (
                  <tr>
                    <td className="fw-bold">
                      Coating <span className="fw-normal text-muted small">(outsourced)</span>
                      {safeNum(finishing_units_at_vendor) > 0 && (
                        <div className="text-warning small fw-normal">
                          {safeNum(finishing_units_at_vendor).toLocaleString()} pcs at vendor
                        </div>
                      )}
                      {/* F62: the estimate split into per-unit PART coating and per-set
                          ASSEMBLY-operation coating — C2.3 station-row keys when the API
                          sends them, else the wave-1 estimate_splits. */}
                      {coatingSplit.text && (
                        <div
                          className="text-muted small fw-normal"
                          title={`Estimated coating ${coatingSplit.source_label}; part coating + assembly coating = Est`}
                        >
                          {coatingSplit.text}
                          {coatingSplit.sums_to_est === false && (
                            <span className="text-warning"> (does not sum to Est {fmtMoney(grand_total_coating_cost_est)})</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="text-end text-muted">&mdash;</td>
                    <td className="text-end text-muted">&mdash;</td>
                    <td className="text-center text-muted">&mdash;</td>
                    <td className="text-end text-muted">&mdash;</td>
                    <td className="text-end text-muted">&mdash;</td>
                    <td className="text-end text-muted">&mdash;</td>
                    <td className="text-end fw-bold">
                      <div className="d-flex flex-column lh-1">
                        <InfoTooltip
                          id="tt-coating-actual"
                          content={
                            <>
                              <div>Outsourced finishing (e.g. powder coat).</div>
                              <div className="mt-1">
                                Shown: {finishingIsInvoiced ? "invoiced vendor cost" : "committed order value"}
                                {safeNum(outside_processing_order_count) > 0 &&
                                  ` across ${outside_processing_order_count} outside-processing order(s)`}.
                              </div>
                              <div className="mt-1">
                                Est is the budgeted coating cost — per-unit on the part, or per-set on the
                                assembly's operations.
                              </div>
                              {coatingSplit.text && (
                                <div className="mt-1">{coatingSplit.text} = {fmtMoneyShared(coatingSplit.total)}</div>
                              )}
                            </>
                          }
                        >
                          <span style={{ cursor: "help", borderBottom: "1px dotted #999" }}>
                            {fmtMoney(finishingCostToDate)}
                          </span>
                        </InfoTooltip>
                        {!finishingIsInvoiced && finishingCostToDate > 0 && (
                          <span className="text-muted small">committed &mdash; awaiting invoice</span>
                        )}
                        {safeNum(grand_total_coating_cost_est) > 0 && (
                          <span className="text-muted small">Est: {fmtMoney(grand_total_coating_cost_est)}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="table-light fw-bold" style={{ borderTop: "2px solid #dee2e6" }}>
                <tr>
                  <td>TOTALS</td>
                  <td className="text-end">
                    <TimeDisplay seconds={grand_total_estimated_seconds} />
                  </td>
                  <td className="text-end">
                    <div className="d-flex flex-column lh-1">
                      <span>
                        <TimeDisplay seconds={grand_total_actual_seconds} />
                      </span>
                    </div>
                  </td>
                  <td className="text-center">
                    {safeNum(grand_total_actual_seconds) > 0
                      ? `${((safeNum(grand_total_estimated_seconds) / safeNum(grand_total_actual_seconds)) * 100).toFixed(0)}%`
                      : "-"}
                  </td>
                  <td className="text-end">
                    <InfoTooltip
                      id="tt-totals-labor"
                      content={<>Sum of time punches × hourly wage rates across all stations</>}
                    >
                      <span style={{ cursor: "help", borderBottom: "1px dotted #999" }}>
                        {fmtMoney(grand_total_labor_cost)}
                      </span>
                    </InfoTooltip>
                  </td>
                  <td className="text-end">
                    <InfoTooltip
                      id="tt-totals-consumable"
                      content={
                        <>
                          <div>Gas: {fmtMoney(grand_total_consumable_cost_act_gas)}</div>
                          <div>Wear (Nozzle + Lens): {fmtMoney(grand_total_consumable_cost_act_wear)}</div>
                          {safeNum(grand_total_consumable_cost_act_incidents) > 0 && (
                            <div>⚠ Incidents (premature swaps): {fmtMoney(grand_total_consumable_cost_act_incidents)}</div>
                          )}
                        </>
                      }
                    >
                      <span style={{ cursor: "help", borderBottom: "1px dotted #999" }}>
                        {safeNum(grand_total_consumable_cost_act_incidents) > 0 ? "⚠ " : ""}
                        {fmtMoney(grand_total_consumable_cost_act)}
                      </span>
                    </InfoTooltip>
                  </td>
                  <td className="text-end">
                    <InfoTooltip
                      id="tt-totals-material"
                      content={(() => {
                        const rows = [];
                        if (safeNum(grand_total_material_cost_scanned) > 0)
                          rows.push(<div key="po">PO-Matched: {fmtMoney(grand_total_material_cost_scanned)}</div>);
                        if (safeNum(grand_total_material_cost_fallback) > 0)
                          rows.push(<div key="fb">Fallback Rate: {fmtMoney(grand_total_material_cost_fallback)}</div>);
                        if (safeNum(grand_total_assembly_consumable_cost) > 0) {
                          rows.push(
                            <div key="asy">
                              Assy Components: {fmtMoney(grand_total_assembly_consumable_cost)}
                              {safeNum(grand_total_assembly_consumable_cost_est) > 0 && (
                                <small> (est {fmtMoney(grand_total_assembly_consumable_cost_est)})</small>
                              )}
                            </div>
                          );
                        } else if (safeNum(grand_total_assembly_consumable_cost_est) > 0) {
                          rows.push(
                            <div key="asy">
                              Assy Components: $0.00 <small>(est {fmtMoney(grand_total_assembly_consumable_cost_est)})</small>
                            </div>
                          );
                        }
                        if (safeNum(grand_total_purchased_component_cost) > 0)
                          rows.push(<div key="pc">Purchased Components: {fmtMoney(grand_total_purchased_component_cost)}</div>);
                        return rows.length > 0 ? <>{rows}</> : <>No material cost recorded</>;
                      })()}
                    >
                      <span style={{ cursor: "help", borderBottom: "1px dotted #999" }}>
                        {fmtMoney(grand_total_material_cost)}
                      </span>
                    </InfoTooltip>
                  </td>
                  <td className="text-end text-primary fs-5">
                    <InfoTooltip
                      id="tt-totals-total"
                      content={
                        <>
                          <div>Labor: {fmtMoney(grand_total_labor_cost)}</div>
                          <div>
                            Consumables: {fmtMoney(grand_total_consumable_cost_act)}
                            {safeNum(grand_total_consumable_cost_est) > 0 && (
                              <small> (est {fmtMoney(grand_total_consumable_cost_est)})</small>
                            )}
                          </div>
                          <div>Material: {fmtMoney(grand_total_material_cost)}</div>
                          {finishingCostToDate > 0 && (
                            <div>
                              Outsourced coating: {fmtMoney(finishingCostToDate)}
                              {!finishingIsInvoiced && <small> (committed)</small>}
                            </div>
                          )}
                        </>
                      }
                    >
                      <span style={{ cursor: "help", borderBottom: "1px dotted #999" }}>
                        {fmtMoney(actualTotalCost)}
                      </span>
                    </InfoTooltip>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className={`p-4 border-top ${healthColor}`}>
            {isTmPending(detailData.header) ? <TmProductionNote /> : (
            <div className="row text-center align-items-center">
              <div className="col-md-3 border-end border-secondary-subtle">
                <div className="text-uppercase small fw-bold mb-1 opacity-75">Revenue</div>
                <InfoTooltip
                  id="tt-stat-revenue"
                  content={
                    <>
                      <div>Realizable Revenue: {fmtMoney(actual_realizable_revenue)}</div>
                      <small>Σ billable qty × unit price per line (billable = min(ordered, produced + shipped) under Bill-Ordered)</small>
                      {bases.order_subtotal != null && (
                        <div className="mt-1">
                          <small>
                            Line revenue {fmtMoneyShared(bases.order_subtotal)} − realizable {fmtMoney(actual_realizable_revenue)} ={" "}
                            {fmtMoneyShared(bases.order_subtotal - safeNum(actual_realizable_revenue))} not yet realized
                          </small>
                        </div>
                      )}
                      {/* F47: lines the API excludes from realizable revenue (fee/no-process
                          parts, loose consumables) with the amount and the reason. */}
                      {unrealizable.rows.length > 0 && (
                        <div className="mt-2 text-start">
                          <div><strong>Not yet realizable</strong> ({fmtMoneyShared(unrealizable.total)}):</div>
                          {unrealizable.rows.map((r) => (
                            <div key={r.so_line_id ?? r.product_id}>
                              {r.product_id}: {fmtMoneyShared(r.amount)} — {r.reason}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  }
                >
                  <div className="fs-3 fw-bold text-success" style={{ cursor: "help" }}>
                    {fmtMoney(actual_realizable_revenue)}
                  </div>
                </InfoTooltip>
                <div className="small text-muted">
                  <span title="Σ quantity ordered × unit price (the Est Profit / Est Margin base)">
                    Line revenue: {fmtMoneyShared(bases.order_subtotal, { dash: true })}
                  </span>
                  <span className="mx-1">·</span>
                  <span title={`sales_orders.total = lines + shipping + fees + tax − discount${bases.pass_through != null ? ` (pass-through ${fmtMoneyShared(bases.pass_through)})` : ""}`}>
                    Commitment (incl. shipping/fees/tax): {fmtMoney(original_commitment)}
                  </span>
                </div>
              </div>

              <div className="col-md-3 border-end border-secondary-subtle">
                <div className="text-uppercase small fw-bold mb-1 opacity-75">Cost</div>
                <InfoTooltip
                  id="tt-stat-cost"
                  content={
                    <>
                      <div><strong>Actual Accrued Cost</strong></div>
                      <div>Labor: {fmtMoney(grand_total_labor_cost)}</div>
                      <div>Consumables: {fmtMoney(grand_total_consumable_cost_act)}</div>
                      <div>Material: {fmtMoney(grand_total_material_cost)}</div>
                      {finishingCostToDate > 0 && (
                        <div>
                          Outsourced coating: {fmtMoney(finishingCostToDate)}
                          {!finishingIsInvoiced && <small> (committed)</small>}
                        </div>
                      )}
                    </>
                  }
                >
                  <div className="fs-3 fw-bold text-dark" style={{ cursor: "help" }}>
                    {fmtMoney(actualTotalCost)}
                  </div>
                </InfoTooltip>
                <InfoTooltip
                  id="tt-stat-recalc"
                  content={
                    <>
                      <div><strong>{bases.labels.grand_total_estimated_cost}</strong>: {fmtMoney(grand_total_estimated_cost)}</div>
                      <small>
                        {estimateBasis ? (
                          <>Basis: {estimateBasis}</>
                        ) : (
                          <>
                            Live labor rates × historical time averages + current material costs, plus
                            assembly-operation labor and coating priced per set from the assembly's routing.
                          </>
                        )}
                      </small>
                      {splits.rows.length > 0 ? (
                        <div className="mt-1 text-start">
                          {splits.rows.map((r) => (
                            <div key={r.key}>{r.label}: {fmtMoneyShared(r.value)}</div>
                          ))}
                          <div>
                            = {fmtMoneyShared(splits.total)}
                            {splits.sums_to_total === false && (
                              <span className="text-warning"> (does not sum to {fmtMoney(grand_total_estimated_cost)})</span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-1"><small>Split by component (labor / material / gas / coating / purchased / assembly ops) not returned by this API version.</small></div>
                      )}
                      {/* Decision 5a: per-line engine estimates (unit cost × qty); the
                          full table is the "Recalc Est by line" panel under the tiles. */}
                      {lineEst.count > 0 && (
                        <div className="mt-1 text-start">
                          <div>
                            <strong>Per line</strong> — {lineEst.engine_count} priced by the quote engine
                            {lineEst.fallback_count > 0 && <span className="text-warning">, {lineEst.fallback_count} fell back</span>}:
                          </div>
                          {lineEst.rows.slice(0, 8).map((r) => (
                            <div key={`tt-le-${r.row_key}`}>
                              {r.product_id}: {r.formula}{r.fallback ? " (fallback)" : ""}
                            </div>
                          ))}
                          {lineEst.rows.length > 8 && <div>… +{lineEst.rows.length - 8} more in the panel below</div>}
                          <div>
                            Σ lines = {fmtMoneyShared(lineEst.total, { dash: true })}
                            {lineEst.remainder != null && !lineEst.remainder_is_zero && (
                              <span> · remainder {fmtDelta(lineEst.remainder)} = {lineEst.remainder_label}</span>
                            )}
                          </div>
                        </div>
                      )}
                      {lineEst.fallback_count > 0 && (
                        <div className="mt-1 text-start text-warning">
                          Fallback (old per-line arithmetic): {lineEst.fallback_rows.map((f) => f.product_id).join(", ")}
                        </div>
                      )}
                      {/* F17/F48: both stored bases, labelled, with deltas — never a bare
                          "Quote" figure that is really a conversion-time part rollup. */}
                      <div className="mt-2 text-start">
                        <div><strong>{bases.labels.quote_cost_basis}</strong>: {bases.quote_cost_basis != null ? fmtMoneyShared(bases.quote_cost_basis) : bases.absent_reason}</div>
                        <div><strong>{bases.labels.original_cost_estimate}</strong>: {bases.conversion_cost != null ? fmtMoneyShared(bases.conversion_cost) : bases.absent_reason}</div>
                        {bases.delta_conversion_vs_quote != null && (
                          <div><small>conversion − quote basis = {fmtDelta(bases.delta_conversion_vs_quote)}</small></div>
                        )}
                        {bases.delta_recalc_vs_conversion != null && (
                          <div><small>Recalc Est − conversion = {fmtDelta(bases.delta_recalc_vs_conversion)}</small></div>
                        )}
                        {bases.delta_recalc_vs_quote != null && (
                          <div><small>Recalc Est − quote basis = {fmtDelta(bases.delta_recalc_vs_quote)}</small></div>
                        )}
                        <div className="mt-1"><small>{bases.cost_basis_note}</small></div>
                      </div>
                    </>
                  }
                >
                  <div className="small text-muted" style={{ cursor: "help", borderBottom: "1px dotted #999" }}>
                    <span>Quote cost basis {bases.quote_cost_basis != null ? fmtMoneyShared(bases.quote_cost_basis) : bases.absent_reason}</span>
                    <span className="mx-1">·</span>
                    <span>Cost at conversion (part rollup) {bases.conversion_cost != null ? fmtMoneyShared(bases.conversion_cost) : bases.absent_reason}</span>
                    <span className="mx-1">·</span>
                    <span>Recalc Est {fmtMoney(grand_total_estimated_cost)}</span>
                    {bases.delta_recalc_vs_conversion != null && (
                      <span className="ms-1">({fmtDelta(bases.delta_recalc_vs_conversion)} vs conversion)</span>
                    )}
                    {bases.delta_recalc_vs_conversion == null && bases.delta_recalc_vs_quote != null && (
                      <span className="ms-1">({fmtDelta(bases.delta_recalc_vs_quote)} vs quote basis)</span>
                    )}
                  </div>
                </InfoTooltip>
              </div>

              <div className="col-md-3 border-end border-secondary-subtle">
                <div className="text-uppercase small fw-bold mb-1 opacity-75">Net Profit</div>
                <InfoTooltip
                  id="tt-stat-net-profit"
                  content={<>Revenue ({fmtMoney(actual_realizable_revenue)}) − Cost ({fmtMoney(actualTotalCost)})</>}
                >
                  <div
                    className={`fs-3 fw-bold ${actualProfit >= 0 ? 'text-success' : 'text-danger'}`}
                    style={{ cursor: "help" }}
                  >
                    {fmtMoney(actualProfit)}
                  </div>
                </InfoTooltip>
                <div
                  className="small text-muted"
                  title={`${fmtMoneyShared(bases.order_subtotal, { dash: true })} line revenue − ${fmtMoney(grand_total_estimated_cost)} Recalc Est`}
                >
                  Est Profit: {fmtMoneyShared(estimatedProfit, { dash: true })}
                  <div style={{ fontSize: "0.7rem" }}>{bases.base_label}</div>
                </div>
              </div>

              <div className="col-md-3">
                <div className="text-uppercase small fw-bold mb-1 opacity-75">Live Margin</div>
                <div className="fs-2 fw-bold">
                  <span
                    className={`badge ${badgeColor} rounded-pill px-4`}
                    title={`(realizable revenue ${fmtMoney(actual_realizable_revenue)} − accrued cost ${fmtMoney(actualTotalCost)}) ÷ realizable revenue`}
                  >
                    {actualMargin.toFixed(1)}%
                  </span>
                </div>
                <div className="small text-muted" style={{ fontSize: "0.7rem" }}>of {fmtMoney(actual_realizable_revenue)} realizable revenue</div>
                <div
                  className="small text-muted mt-1"
                  title={`(${fmtMoneyShared(bases.order_subtotal, { dash: true })} line revenue − ${fmtMoney(grand_total_estimated_cost)} Recalc Est) ÷ line revenue`}
                >
                  Est Margin: {fmtPct(estimatedMargin)}
                  <div style={{ fontSize: "0.7rem" }}>{bases.base_label}</div>
                </div>
              </div>
            </div>
            )}

            {/* Decision 5a: the quote engine's per-line estimates behind Recalc Est,
                as a table the operator can reproduce (unit cost × qty = ext cost).
                Rendered only when the API returns line_estimates / estimate_basis. */}
            {(lineEst.count > 0 || lineEst.fallback_count > 0 || estimateBasis) && (
              <details className="mt-3 small text-start">
                <summary style={{ cursor: "pointer" }}>
                  <strong>Recalc Est by line</strong> — {lineEst.engine_count} line{lineEst.engine_count === 1 ? "" : "s"} priced by the quote engine
                  {lineEst.fallback_count > 0 && (
                    <span className="text-warning"> · {lineEst.fallback_count} fell back to the old per-line arithmetic</span>
                  )}
                  {estimateBasis && <span className="text-muted"> · basis: {estimateBasis}</span>}
                </summary>
                {/* Engine notes: what the engine could not price the way it wanted to
                    (this order's own history not yet excluded, stale $0 material lines
                    priced by the old arithmetic, the per-request call cap). */}
                {Array.isArray(summaryData.estimate_notes) && summaryData.estimate_notes.length > 0 && (
                  <ul className="mb-1 mt-2 text-muted" style={{ paddingLeft: 18 }}>
                    {summaryData.estimate_notes.map((n, i) => <li key={i}>{String(n)}</li>)}
                  </ul>
                )}
                {summaryData.estimate_engine_calls && (
                  <div className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                    engine calls: {summaryData.estimate_engine_calls.part_lines ?? 0} part line(s), {summaryData.estimate_engine_calls.assembly_ops ?? 0} assembly op(s), {summaryData.estimate_engine_calls.consumable_lines ?? 0} purchased line(s)
                    {engineCallsSummary(summaryData.estimate_engine_calls).map((t) => ` · ${t}`).join("")}
                    {summaryData.estimate_engine_calls.ms != null ? ` · ${Math.round(summaryData.estimate_engine_calls.ms)} ms` : ""}
                  </div>
                )}
                <div className="table-responsive mt-2">
                  <table className="table table-sm mb-1" style={{ fontSize: "0.8rem" }}>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Basis</th>
                        <th className="text-end">Qty</th>
                        <th className="text-end">Unit cost</th>
                        <th className="text-end">Ext cost</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineEst.rows.map((r) => (
                        <tr key={`le-${r.row_key}`} className={r.fallback ? "table-warning" : ""}>
                          <td>
                            {r.product_id}
                            {r.so_line_id != null && <span className="text-muted"> · line #{r.so_line_id}</span>}
                          </td>
                          <td className="text-muted">COST · {r.basis}</td>
                          <td className="text-end">{r.qty == null ? "—" : r.qty}</td>
                          <td className="text-end">{fmtUnitCost(r.unit_cost, { dash: true })}</td>
                          <td className="text-end" title={r.formula}>{fmtMoneyShared(r.ext_cost, { dash: true })}</td>
                          <td>
                            <span className={r.fallback ? "badge bg-warning text-dark" : "badge bg-secondary"}>
                              {r.fallback ? "fallback" : "estimate"}
                            </span>
                            {r.error && <span className="ms-1 text-danger">{r.error}</span>}
                            {r.sums_ok === false && <span className="ms-1 text-warning">unit × qty ≠ ext</span>}
                          </td>
                        </tr>
                      ))}
                      {lineEst.fallback_only_rows.map((f) => (
                        <tr key={`fb-${f.row_key}:${f.product_id}`} className="table-warning">
                          <td>
                            {f.product_id}
                            {f.so_line_id != null && <span className="text-muted"> · line #{f.so_line_id}</span>}
                          </td>
                          <td className="text-muted">COST · old per-line arithmetic</td>
                          <td className="text-end">—</td>
                          <td className="text-end">—</td>
                          <td className="text-end">—</td>
                          <td>
                            <span className="badge bg-warning text-dark">fallback</span>
                            <span className="ms-1 text-muted">{f.reason}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="4" className="text-end">Σ lines</td>
                        <td className="text-end fw-bold">{fmtMoneyShared(lineEst.total, { dash: true })}</td>
                        <td />
                      </tr>
                      {lineEst.remainder != null && !lineEst.remainder_is_zero && (
                        <tr>
                          <td colSpan="4" className="text-end text-muted">
                            Recalc Est {fmtMoney(grand_total_estimated_cost)} − Σ lines = {lineEst.remainder_label}
                          </td>
                          <td className="text-end">{fmtDelta(lineEst.remainder)}</td>
                          <td />
                        </tr>
                      )}
                    </tfoot>
                  </table>
                </div>
              </details>
            )}
          </div>
        </div>
      </div>
    );
  };

  /**
   * --------------------
   * Delivery & Progress
   * --------------------
   */
  const renderDeliveryProgress = () => {
    if (!progressData) return null;
    const { time_summary, station_flow } = progressData;
    const delivery_risk = deliveryRisk;
    if (!time_summary || time_summary.total_estimated_hours === 0) return null;

    const riskColors = {
      on_track: { bg: "bg-success", text: "text-white", icon: "bi-check-circle-fill", label: "On Track" },
      at_risk:  { bg: "bg-warning", text: "text-dark",  icon: "bi-exclamation-triangle-fill", label: "At Risk" },
      behind:   { bg: "bg-danger",  text: "text-white", icon: "bi-x-circle-fill", label: "Behind" },
      overdue:  { bg: "bg-danger",  text: "text-white", icon: "bi-alarm-fill", label: "Overdue" },
      no_date:  { bg: "bg-secondary", text: "text-white", icon: "bi-calendar-x", label: "No Date" },
      complete: { bg: "bg-success", text: "text-white", icon: "bi-box-seam", label: "Production Complete" },
      needs_jobs: { bg: "bg-danger", text: "text-white", icon: "bi-exclamation-triangle-fill", label: "Needs More Jobs" },
    };
    const risk = riskColors[delivery_risk.risk_level] || riskColors.no_date;

    const fmtHrs = (h) => {
      if (h == null) return "—";
      if (h < 1) return `${Math.round(h * 60)}m`;
      return `${h.toFixed(1)}h`;
    };

    return (
      <div className="card shadow-sm mb-4 border-top-0 border-end-0 border-bottom-0 border-4 border-primary">
        <div className="card-header bg-white fw-bold">
          <i className="bi bi-graph-up me-2 text-primary"></i>Delivery & Progress
        </div>
        <div className="card-body">
          <div className="row g-3">
            {/* Delivery Risk */}
            <div className="col-md-3">
              <div className={`rounded-3 p-3 h-100 ${risk.bg} ${risk.text}`}>
                <div className="text-uppercase small fw-bold opacity-75 mb-1">Delivery</div>
                <div className="d-flex align-items-center mb-2">
                  <i className={`bi ${risk.icon} me-2 fs-4`}></i>
                  <span className="fs-5 fw-bold">{risk.label}</span>
                </div>
                {delivery_risk.risk_level === "complete" ? (() => {
                  // Production done: show the actual finish/ship date + how it
                  // landed vs the promise, not a forever-growing "Nd past due".
                  const ci = getCompletionInfo();
                  return (
                    <>
                      {ci.actualDate && (
                        <div className="small">
                          <i className="bi bi-calendar-check me-1"></i>
                          {ci.latestShip ? "Shipped" : "Completed"}{" "}
                          {new Date(ci.actualDate + "T00:00:00Z").toLocaleDateString(undefined, { timeZone: "UTC" })}
                        </div>
                      )}
                      {ci.label && (
                        <div className="small opacity-75">
                          {ci.varianceDays === 0
                            ? "Finished on the promised date"
                            : `${ci.label} vs promise`}
                        </div>
                      )}
                    </>
                  );
                })() : (
                  <>
                    {delivery_risk.days_remaining != null && (
                      <div className="small">
                        {delivery_risk.days_remaining > 0
                          ? `${delivery_risk.days_remaining}d remaining`
                          : delivery_risk.days_remaining === 0
                            ? "Due today"
                            : `${Math.abs(delivery_risk.days_remaining)}d past due`}
                      </div>
                    )}
                    {delivery_risk.days_margin != null && !["no_date", "needs_jobs"].includes(delivery_risk.risk_level) && (
                      <div className="small opacity-75">
                        {delivery_risk.days_margin >= 0
                          ? `${delivery_risk.days_margin}d buffer`
                          : `${Math.abs(delivery_risk.days_margin)}d behind schedule`}
                      </div>
                    )}
                  </>
                )}
                {delivery_risk.risk_reason && delivery_risk.risk_level !== "complete" && (
                  <div className="small opacity-75 mt-1 fst-italic">{delivery_risk.risk_reason}</div>
                )}
              </div>
            </div>

            {/* Production Progress */}
            <div className="col-md-3">
              <div className="border rounded-3 p-3 h-100">
                <div className="text-uppercase small fw-bold text-muted mb-1">Production</div>
                <div className="fs-3 fw-bold text-primary mb-1">{time_summary.percent_complete_time}%</div>
                <div className="progress mb-2" style={{ height: "8px" }}>
                  <div
                    className={`progress-bar ${time_summary.percent_complete_time >= 100 ? "bg-success" : "bg-primary"}`}
                    style={{ width: `${Math.min(100, time_summary.percent_complete_time)}%` }}
                  ></div>
                </div>
                <div className="small text-muted">
                  {fmtHrs(time_summary.total_remaining_hours)} remaining of {fmtHrs(time_summary.total_estimated_hours)}
                </div>
                <div className="small text-muted">
                  {fmtHrs(time_summary.total_elapsed_hours)} elapsed
                </div>
                {yieldViolations.length > 0 && (
                  <div className="small text-danger mt-1" title="Hours and % cover the jobs released so far; a line with no job has no tasks yet">
                    Released jobs only: {yieldViolations.length} {yieldViolations.length === 1 ? "line is" : "lines are"} short
                  </div>
                )}
              </div>
            </div>

            {/* Bottleneck */}
            <div className="col-md-2">
              <div className="border rounded-3 p-3 h-100">
                <div className="text-uppercase small fw-bold text-muted mb-1">Bottleneck</div>
                {time_summary.bottleneck_station ? (
                  <>
                    <div className="fs-5 fw-bold text-danger">{time_summary.bottleneck_station}</div>
                    <div className="small text-muted">{fmtHrs(time_summary.bottleneck_hours)} remaining</div>
                    <div className="small text-muted">
                      {(time_summary.by_station || []).find(s => s.workstation === time_summary.bottleneck_station)?.task_count || 0} tasks
                    </div>
                  </>
                ) : (
                  <div className="text-success fw-bold"><i className="bi bi-check-circle me-1"></i>Clear</div>
                )}
              </div>
            </div>

            {/* Station Pipeline */}
            <div className="col-md-4">
              <div className="border rounded-3 p-3 h-100">
                <div className="text-uppercase small fw-bold text-muted mb-2">Station Pipeline</div>
                {(station_flow || []).map((s) => (
                  <div key={s.station} className="d-flex align-items-center mb-1" style={{ fontSize: "0.8rem" }}>
                    <span className="text-end me-2" style={{ width: "70px", fontWeight: s.done < s.total ? "bold" : "normal", color: s.done === s.total ? "#6c757d" : "#212529" }}>
                      {s.station}
                    </span>
                    <div className="flex-grow-1 d-flex" style={{ height: "14px", borderRadius: "3px", overflow: "hidden", backgroundColor: "#e9ecef" }}>
                      {s.done > 0 && (
                        <div style={{ width: `${(s.done / (s.total || 1)) * 100}%`, backgroundColor: "#198754" }} title={`${s.done} done`}></div>
                      )}
                      {s.active > 0 && (
                        <div style={{ width: `${(s.active / (s.total || 1)) * 100}%`, backgroundColor: "#0d6efd" }} title={`${s.active} active`}></div>
                      )}
                      {s.pending > 0 && (
                        <div style={{ width: `${(s.pending / (s.total || 1)) * 100}%`, backgroundColor: "#dee2e6" }} title={`${s.pending} pending`}></div>
                      )}
                    </div>
                    <span className="ms-2 text-muted" style={{ minWidth: "30px" }}>{s.done}/{s.total}</span>
                  </div>
                ))}
                <div className="d-flex gap-3 mt-2" style={{ fontSize: "0.7rem" }}>
                  <span><span style={{ display: "inline-block", width: 8, height: 8, backgroundColor: "#198754", borderRadius: 2, marginRight: 3 }}></span>Done</span>
                  <span><span style={{ display: "inline-block", width: 8, height: 8, backgroundColor: "#0d6efd", borderRadius: 2, marginRight: 3 }}></span>Active</span>
                  <span><span style={{ display: "inline-block", width: 8, height: 8, backgroundColor: "#dee2e6", borderRadius: 2, marginRight: 3 }}></span>Pending</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /**
   * --------------------
   * Line items & fulfillment
   * --------------------
   */
  const renderLineItems = () => {
    if (!detailData || !detailData.lines) return null;

    const allLines = flattenTruthLines(detailData.lines);

    // Ordered / shipped / % over every line (soData.shippedTotals, shared with the phone): /truth's
    // per-line Shipped shares sum to the part's, so a part shared across lines counts once.
    const { percentComplete } = shippedTotals(allLines);

    // Each part's surplus, once, on its last line (features/salesOrders/surplus.js, shared with Job
    // Entry): pieces other orders send in (incoming allocations) are in qty_in_production but not
    // this order's surplus, so Allocate must not offer them (SO-1139 FAB311162, 2026-10-21); and a
    // part on 2+ lines gets ONE Allocate for the part, not one per line.
    const surplusAtLine = surplusByLastLine(allLines);

    const renderSingleLineRow = (line, isComponent, withinAssemblyGroup) => {
      const lineId = line?.sales_order_line_id;
      // The line's share of the part's shipped (/truth splits it across the part's lines).
      const shipped = safeNum(line?.qty_shipped);
      const remaining = safeNum(line?.quantity_ordered) - shipped;
      const violation = yieldViolations.find((v) => v.sales_order_line_id === line?.sales_order_line_id);
      const isPurchasedItem = !!line?.consumable_id;
      const componentHealth = normalizeIssues(line?.consumable_health);
      // Per-line money (F18/F19): ext price/cost + margin from the enriched
      // /truth line (unit_cost, price_source, quote_part_id, below_cost) via
      // lineFinancials(); degrades to "—" (never $0.00) when the snapshot is
      // absent. price_source badge text/colour from priceSourceMeta().
      const fin = lineFinancials(line);
      const srcMeta = priceSourceMeta(fin.price_source);
      const quoteId = detailData?.header?.quote_id || null;

      const hasHealthIssue = componentHealth.length > 0;
      const hasShortage = componentHealth.some((issue) => issue.status === "Critical");
      const isStockOk = isPurchasedItem && !hasHealthIssue;

      // --- Net Production (API qty_in_production already subtracts defects) ---
      // The line's share of the part's In Prod (/truth splits it) and of its defects (lineDefects), so a
      // part shared across assembly lines doesn't show its full pool on every line.
      const defects = (lineId != null && lineDefects[lineId] != null)
        ? lineDefects[lineId] : safeNum(activeDefectMap[line?.part_id]);
      const netInProd = safeNum(line?.qty_in_production);
      const grossInProd = netInProd + defects;

      const isAssemblyTracked =
        grossInProd > safeNum(line?.quantity_ordered) ||
        shipped > safeNum(line?.quantity_ordered);

      const indentStyle = isComponent ? { paddingLeft: "40px", borderLeft: "4px solid #dee2e6" } : {};
      const rowClass = violation || hasShortage ? "table-danger" : "";

      const ordered = safeNum(line?.quantity_ordered);
      const remainingToShip = Math.max(0, ordered - shipped);
      const outgoingAllocs = Array.isArray(line?.outgoing_allocations) ? line.outgoing_allocations : [];
      const incomingAllocs = Array.isArray(line?.incoming_allocations) ? line.incoming_allocations : [];
      const allocOutIntent = outgoingAllocs.reduce((s, a) => s + safeNum(a?.intent_quantity ?? a?.quantity), 0);
      // Unallocated surplus available for reallocation (features/salesOrders/surplus.js, once per
      // part on its last line): this order's own pieces still on the floor beyond what it still
      // has to ship, less what is already promised to other orders (an outgoing allocation's
      // unrouted remainder; routed pieces are already out of qty_in_production — Boyd
      // SO-1088→SO-1089). Pieces another order sends here are over-coverage, not surplus: POST
      // /surplus_allocations counts only this order's own jobs and rejects them ("Over-allocation
      // ... surplus=-62", SO-1139 FAB311162).
      const partSurplusHere = lineId != null ? surplusAtLine[lineId] : null;
      const surplusUnallocated = partSurplusHere ? partSurplusHere.allocatable : 0;
      const overCoverage = partSurplusHere && !isPurchasedItem
        ? overCoverageText(partSurplusHere, partSurplusHere.sources) : null;
      const hasUnallocatedSurplus = !isPurchasedItem && surplusUnallocated > 0 && line?.sales_order_line_id != null;
      // PHYSICAL cross-SO placement counts (ground truth from the packing
      // plan; includes sibling-overflow routing that has no jsa row). Drive
      // the "Allocated N" chips so they can never disagree with what's
      // actually in the boxes. The jsa lists (incoming/outgoingAllocs) are
      // kept only for the tooltip's which-SO breakdown.
      const allocOutPlaced = safeNum(line?.allocated_out_placed);
      const allocInPlaced = safeNum(line?.allocated_in_placed);
      const allocInIntent = incomingAllocs.reduce((s, a) => s + safeNum(a?.intent_quantity ?? a?.quantity), 0);
      const incomingSoNames = [...new Set(incomingAllocs.map(a => a?.source_so_order_number || (a?.source_job_num ? `Job ${a.source_job_num}` : null)).filter(Boolean))];
      const outgoingSoNames = [...new Set(outgoingAllocs.map(a => a?.target_so_order_number).filter(Boolean))];

      return (
        <tr key={line?.sales_order_line_id || `${line?.product_id}-${Math.random()}`} className={rowClass}>
          <td style={indentStyle}>
            {isComponent && <i className="bi bi-arrow-return-right me-2 text-muted"></i>}
            {isPurchasedItem && <i className="bi bi-cart-fill me-2 text-info" title="Purchased Item"></i>}
            {/* A line is a part OR a purchased component; the component rows
                carry consumable_id with a NULL part_id, which used to fall
                through to plain text. Nested rows no longer get the muted
                className — a grey, un-underlined button is indistinguishable
                from static text, which is why these read as unclickable. The
                indent + ↳ arrow already carry the hierarchy. */}
            <CatalogItemLink
              productId={line?.product_id || "N/A"}
              item={
                line?.part_id ? { kind: 'part', id: line.part_id }
                  : (line?.consumable_id ? { kind: 'consumable', id: line.consumable_id } : null)
              }
              onInspect={setInspectedItem}
            />
            {line?.revision_code && line?.is_current_revision !== false && (
              <span className="text-muted small ms-2">Rev {line.revision_code}</span>
            )}
            {line?.is_current_revision === false && line?.current_revision_part_id && (
              <>
                <span
                  className="badge bg-warning text-dark ms-2 small"
                  title={`This line references Rev ${line.revision_code || '?'}, which has been superseded by Rev ${line.current_revision_code}`}
                >
                  Rev {line.revision_code || '?'} — superseded
                </span>
                <button
                  className="btn btn-sm btn-outline-primary ms-2 py-0 px-2"
                  style={{ fontSize: '0.75rem', lineHeight: '1.4' }}
                  onClick={(e) => { e.stopPropagation(); handleUpdateLineRevision(line); }}
                  title={`Update this line (and the source quote) to Rev ${line.current_revision_code} in place — same SO number, same price. Blocked once production has begun.`}
                >
                  <i className="bi bi-arrow-up-circle me-1"></i>
                  Update to Rev {line.current_revision_code}
                </button>
              </>
            )}
            {isAssemblyTracked && surplusUnallocated > 0 && (
              <span className="badge bg-warning text-dark ms-2 small" title="Produced more than ordered, with surplus not yet allocated">
                Overtracked
              </span>
            )}
            {hasUnallocatedSurplus && !isTm(detailData?.header) && (
              <button
                className="btn btn-sm btn-outline-warning ms-2 py-0 px-2"
                style={{ fontSize: '0.75rem', lineHeight: '1.4' }}
                onClick={(e) => {
                  e.stopPropagation();
                  setSurplusModalContext({
                    part_id: line.part_id,
                    part_product_id: line.product_id,
                    part_description: line.description,
                    surplus: surplusUnallocated,
                    source_so_line_id: line.sales_order_line_id,
                    part_customer_id: detailData?.header?.customer_id || null
                  });
                  setShowSurplusModal(true);
                }}
                title={`Allocate ${surplusUnallocated} surplus part(s) to another Sales Order`}
              >
                <i className="bi bi-arrow-right-circle me-1"></i>
                Allocate +{surplusUnallocated}
              </button>
            )}
            {overCoverage && (
              <span
                className="badge bg-info-subtle text-info border border-info-subtle ms-2 small"
                style={{ cursor: "help" }}
                title={`${overCoverage.detail}. ${overCoverage.title}`}
              >
                {overCoverage.label}
              </span>
            )}
          </td>
          <td className="small text-muted">{line?.description}</td>
          <td style={{ minWidth: "180px" }}>
            {!isPurchasedItem && lineId != null && lineStepsMap[lineId] ? (
              <>
                <RoutingStrip
                  compact
                  steps={lineStepsMap[lineId].steps}
                  ariaLabel={`Process steps for ${line?.product_id || "this line"}`}
                />
                {lineStepsMap[lineId].fromRouting && (
                  <div className="text-muted mt-1" style={{ fontSize: "0.65rem" }}>no job yet: from the part's routing</div>
                )}
              </>
            ) : (
              <span className="text-muted small">—</span>
            )}
          </td>
          <td className="text-center fw-bold fs-5 text-secondary">{safeNum(line?.quantity_ordered)}</td>
          <td className="text-center">
            {hasHealthIssue ? (
              <ConsumableStatusBadge issues={componentHealth} />
            ) : (
              <span className="badge bg-light text-muted small">—</span>
            )}
            {isStockOk && <span className="badge bg-success-subtle text-success small ms-1">In Stock</span>}
          </td>
          
          {/* --- ENHANCED COLUMN: In Prod --- */}
          <td className="text-center text-primary">
            {isPurchasedItem ? (
              <span className="text-muted small">N/A</span>
            ) : (
              <div className="d-flex flex-column align-items-center justify-content-center">
                <span className={defects > 0 ? "fw-bold" : ""}>{netInProd}</span>
                {(allocInPlaced > 0 || allocInIntent > 0) && (
                  <span
                    className="badge bg-info-subtle text-info border border-info-subtle mt-1"
                    style={{ fontSize: "0.65rem", padding: "2px 5px", cursor: "help" }}
                    title={`${allocInPlaced} physically in the packing plan${allocInIntent > allocInPlaced ? ` · ${allocInIntent} earmarked` : ''}${incomingSoNames.length ? ` · from ${incomingSoNames.join(', ')}` : ''}`}
                  >
                    <i className="bi bi-arrow-left-circle me-1"></i>
                    Allocated {allocInPlaced}{allocInIntent > allocInPlaced ? ` of ${allocInIntent}` : ''}
                  </span>
                )}
                {(allocOutPlaced > 0 || allocOutIntent > 0) && (
                  <span
                    className="badge bg-warning-subtle text-warning border border-warning-subtle mt-1"
                    style={{ fontSize: "0.65rem", padding: "2px 5px", cursor: "help" }}
                    title={`${allocOutPlaced} physically routed out${allocOutIntent > allocOutPlaced ? ` · ${allocOutIntent} earmarked` : ''}${outgoingSoNames.length ? ` · to ${outgoingSoNames.join(', ')}` : ''}`}
                  >
                    <i className="bi bi-arrow-right-circle me-1"></i>
                    {allocOutPlaced}{allocOutIntent > allocOutPlaced ? ` of ${allocOutIntent}` : ''} allocated out
                  </span>
                )}
                {defects > 0 && (() => {
                  const defectsReplaced = netInProd >= remainingToShip;
                  const badgeClass = defectsReplaced
                    ? "badge bg-success-subtle text-success border border-success-subtle mt-1"
                    : "badge bg-danger-subtle text-danger border border-danger-subtle mt-1";
                  const tooltip = defectsReplaced
                    ? `Started: ${grossInProd}\nDefects: ${defects}\nActual Good: ${netInProd}\n✓ Net production covers the order — defects fully replaced`
                    : `Started: ${grossInProd}\nDefects: ${defects}\nActual Good: ${netInProd}`;
                  return (
                    <span
                      className={badgeClass}
                      style={{ fontSize: "0.65rem", padding: "2px 5px", cursor: "help" }}
                      title={tooltip}
                    >
                      -{defects} Defect{defects !== 1 ? 's' : ''}{defectsReplaced ? ' · Fully Replaced' : ''}
                    </span>
                  );
                })()}
              </div>
            )}
          </td>
          {/* ------------------------------- */}

          <td className="text-center bg-success-subtle text-success fw-bold">{shipped}</td>
          <td className="text-center">
            {remaining > 0 ? (
              <span className="badge bg-warning text-dark">{remaining}</span>
            ) : (
              <span className="badge bg-success">Done</span>
            )}
          </td>
          <td className="text-end">
            <div className={isComponent ? "text-muted" : ""} title={fin.formula_price}>
              {fmtMoneyShared(line?.unit_price)}
            </div>
            <div className="d-flex flex-column align-items-end gap-1 mt-1">
              {srcMeta && (
                <span className={srcMeta.cls} style={{ fontSize: "0.65rem" }} title={srcMeta.title}>
                  {srcMeta.label}
                </span>
              )}
              {fin.below_cost && (
                <span
                  className="badge bg-danger"
                  style={{ fontSize: "0.65rem" }}
                  title={`unit price ${fmtMoneyShared(fin.unit_price)} is below the est unit cost ${fmtMoneyShared(fin.unit_cost, { dash: true })}`}
                >
                  below cost
                </span>
              )}
              {fin.quote_part_id != null && (
                quoteId ? (
                  <Link
                    to={`/quotes?id=${quoteId}`}
                    className="text-decoration-none"
                    style={{ fontSize: "0.7rem" }}
                    title={`quote_parts.quote_part_id ${fin.quote_part_id} on Q-${quoteId}`}
                  >
                    from quote line #{fin.quote_part_id}
                  </Link>
                ) : (
                  <span className="text-muted" style={{ fontSize: "0.7rem" }}>from quote line #{fin.quote_part_id}</span>
                )
              )}
            </div>
          </td>
          <td className="text-end" title={fin.formula_price}>{fmtMoneyShared(fin.ext_price)}</td>
          <td
            className="text-end"
            title={fin.unit_cost == null
              ? "No cost snapshot on this line (converted before per-line snapshots existed, or a manual line without a part cost)"
              : (fin.cost_source === "snapshot" ? "Per-unit cost snapshot stamped at conversion" : `cost source: ${fin.cost_source}`)}
          >
            {fmtMoneyShared(fin.unit_cost, { dash: true })}
            {fin.unit_cost == null && <div className="text-muted" style={{ fontSize: "0.65rem" }}>no snapshot</div>}
          </td>
          <td className="text-end" title={fin.formula_cost}>{fmtMoneyShared(fin.ext_cost, { dash: true })}</td>
          <td
            className="text-end"
            title={fin.margin == null
              ? "margin needs both an ext price > 0 and an ext cost"
              : `(${fmtMoneyShared(fin.ext_price)} − ${fmtMoneyShared(fin.ext_cost)}) ÷ ${fmtMoneyShared(fin.ext_price)}`}
          >
            <span className={fin.margin == null ? "text-muted" : fin.margin < 0 ? "text-danger fw-bold" : fin.margin < 0.15 ? "text-warning-emphasis" : ""}>
              {fmtPct(fin.margin)}
            </span>
          </td>
        </tr>
      );
    };

    return (
      <div className="card mb-4 shadow-sm">
        <div className="card-header bg-white d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0 text-primary">
              <i className="bi bi-list-check me-2"></i>Line Items & Fulfillment
            </h5>
            {isEditingLines ? (
              <>
                <button className="btn btn-sm btn-success" onClick={handleSaveLines}>
                  <i className="bi bi-check-lg me-1"></i>Save
                </button>
                <button className="btn btn-sm btn-secondary" onClick={() => setIsEditingLines(false)}>
                  Cancel
                </button>
              </>
            ) : isTmFinal(detailData.header) ? (
              <TmLinesLockNote header={detailData.header} />
            ) : (
              <button className="btn btn-sm btn-outline-primary" onClick={handleEditLines}>
                <i className="bi bi-pencil me-1"></i> Edit Items
              </button>
            )}
          </div>
          <div className="progress" style={{ width: "200px", height: "20px" }}>
            <div className="progress-bar bg-success" role="progressbar" style={{ width: `${percentComplete}%` }}>
              {percentComplete}% Shipped
            </div>
          </div>
        </div>

        {lineSaveWarnings.length > 0 && (
          <div className="alert alert-warning m-3 mb-0 py-2 small d-flex align-items-start">
            <i className="bi bi-exclamation-triangle-fill me-2 mt-1"></i>
            <div className="flex-grow-1">
              <strong>Line update warnings</strong>
              <ul className="mb-0 ps-3">
                {lineSaveWarnings.map((w, i) => (
                  <li key={i}>{typeof w === "string" ? w : JSON.stringify(w)}</li>
                ))}
              </ul>
            </div>
            <button type="button" className="btn-close btn-sm" aria-label="Dismiss" onClick={() => setLineSaveWarnings([])}></button>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Product / Part ID</th>
                <th>Description</th>
                <th title="Every station the part goes through before shipping (shipping is the Shipped column), with how many of this order's pieces have finished it: green = all ordered, yellow = some (or running now), gray = none yet. Hover a step for the jobs behind the number.">
                  Process Steps
                </th>
                <th className="text-center" style={{ width: "100px" }}>Ord</th>
                <th className="text-center">Health & Source</th>
                <th className="text-center">In Prod</th>
                <th className="text-center bg-success-subtle">Shipped</th>
                <th className="text-center">Remaining</th>
                <th className="text-end" style={{ width: "140px" }}>Unit Price</th>
                {/* F18: per-line money the table never had — every cell is
                    reproducible from Ord × Unit Price / Est Unit Cost. */}
                <th className="text-end" title="quantity ordered × unit price">Ext Price</th>
                <th className="text-end" title="Per-unit cost snapshot stamped on the line at conversion (quote-line cost, or the part rollup as fallback); '—' when the line predates snapshots">Est Unit Cost</th>
                <th className="text-end" title="quantity ordered × est unit cost">Ext Cost</th>
                <th className="text-end" title="(ext price − ext cost) ÷ ext price">Margin %</th>
                {isEditingLines && <th className="text-end">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {isEditingLines ? (
                editedLines.map((line, idx) => {
                  if (line.remove) return null;
                  const isLocked = !line.is_new && safeNum(line.part_in_production ?? line.qty_in_production) > 0;
                  const remaining = safeNum(line.quantity_ordered) - safeNum(line.qty_shipped);
                  return (
                    <tr key={idx} className={line.is_new ? "table-info" : ""}>
                      <td style={{ minWidth: "150px" }}>
                        <PartSearch
                          value={line.part_id}
                          initialDisplay={line.product_id}
                          disabled={isLocked || (!line.is_new && isTm(detailData.header))}
                          API_BASE={API_BASE}
                          onSelect={(part) => handlePartSelect(idx, part)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Description"
                          value={line.description}
                          onChange={(e) => handleLineChange(idx, "description", e.target.value)}
                        />
                      </td>
                      <td></td>
                      <td className="text-center">
                        <input
                          type="number"
                          className="form-control form-control-sm text-center"
                          value={line.quantity_ordered}
                          disabled={isLocked}
                          onChange={(e) => handleLineChange(idx, "quantity_ordered", e.target.value)}
                        />
                      </td>
                      <td className="text-center"></td>
                      <td className="text-center text-primary">
                        {safeNum(line.qty_in_production)}
                        {line.incoming_allocations && line.incoming_allocations.length > 0 && (
                          <span className="badge bg-info-subtle text-info ms-1"
                                title={line.incoming_allocations.map(a =>
                                  `${a.quantity} from ${a.source_so_order_number || `Job ${a.source_job_num || a.source_job_id} (internal)`}`
                                ).join('\n')}
                                style={{fontSize: '0.65rem'}}>
                            <i className="bi bi-arrow-left-circle me-1"></i>
                            +{line.incoming_allocations.reduce((s,a) => s + a.quantity, 0)} allocated
                          </span>
                        )}
                        {line.outgoing_allocations && line.outgoing_allocations.length > 0 && (
                          <span className="badge bg-warning-subtle text-warning ms-1"
                                title={line.outgoing_allocations.map(a =>
                                  `${a.quantity} to ${a.target_so_order_number}`
                                ).join('\n')}
                                style={{fontSize: '0.65rem'}}>
                            <i className="bi bi-arrow-right-circle me-1"></i>
                            -{line.outgoing_allocations.reduce((s,a) => s + a.quantity, 0)} allocated out
                          </span>
                        )}
                      </td>
                      <td className="text-center bg-success-subtle fw-bold text-success">{safeNum(line.qty_shipped)}</td>
                      <td className="text-center">{remaining > 0 ? remaining : 0}</td>
                      <td className="text-end">
                        {line.is_new && isTm(detailData.header) ? (
                          <TmNewLinePrice />
                        ) : line.is_new ? (
                          <>
                            {/* F45: NEW rows may carry a typed unit price (sent as
                                unit_price, recorded by the API as a manual price). */}
                            <input
                              type="number"
                              className="form-control form-control-sm text-end"
                              step="0.01"
                              min="0"
                              value={line.unit_price ?? ""}
                              onChange={(e) => handleNewLinePriceChange(idx, e.target.value)}
                              title="New line: keep the catalog price or type a unit price — a typed price is saved as a manual price."
                            />
                            <div className="text-muted" style={{ fontSize: "0.65rem" }} title={LOCKED_PRICE_TOOLTIP}>
                              {line.price_touched
                                ? "manual price"
                                : (safeNum(line.unit_price) > 0 ? "catalog price — edit to override" : "no book price — type a price or it bills $0.00")}
                            </div>
                          </>
                        ) : (
                          // Wrapped in a titled div: disabled inputs do not reliably show
                          // their own title tooltip, and the operator needs to know WHY
                          // the price is greyed out (F45).
                          <div title={LOCKED_PRICE_TOOLTIP} style={{ cursor: "help" }}>
                            <input
                              type="number"
                              className="form-control form-control-sm text-end"
                              step="0.01"
                              value={safeNum(line.unit_price)}
                              readOnly={true}
                              disabled={true}
                              style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
                            />
                            <div className="text-muted" style={{ fontSize: "0.65rem" }}>
                              <i className="bi bi-lock-fill me-1"></i>locked
                              {priceSourceMeta(line.price_source) ? ` · ${priceSourceMeta(line.price_source).label}` : ""}
                            </div>
                          </div>
                        )}
                      </td>
                      {(() => {
                        const ef = lineFinancials({
                          quantity_ordered: line.quantity_ordered,
                          unit_price: line.unit_price,
                          unit_cost: line.unit_cost,
                          price_source: line.price_source,
                        });
                        return (
                          <>
                            <td className="text-end" title={ef.formula_price}>{fmtMoneyShared(ef.ext_price)}</td>
                            <td className="text-end" title={ef.unit_cost == null ? "no cost snapshot" : "cost snapshot on the line"}>
                              {fmtMoneyShared(ef.unit_cost, { dash: true })}
                            </td>
                            <td className="text-end" title={ef.formula_cost}>{fmtMoneyShared(ef.ext_cost, { dash: true })}</td>
                            <td className="text-end">
                              <span className={ef.margin != null && ef.margin < 0 ? "text-danger fw-bold" : ""}>{fmtPct(ef.margin)}</span>
                            </td>
                          </>
                        );
                      })()}
                      <td className="text-end">
                        <button
                          className="btn btn-sm btn-outline-danger border-0"
                          onClick={() => handleRemoveLine(idx)}
                          title={isLocked ? "Locked by Production" : "Remove Line"}
                          disabled={isLocked}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                (detailData.lines || []).map((entry, idx) => {
                    if (entry?.type === "assembly_group") {
                        // F19: the group row used to leave Unit Price blank, and the
                        // folded ops price / assembly override were invisible on this
                        // page. groupFinancials() (src/utils/salesOrderMath.js) takes
                        // sets, per-set price, group subtotal/cost/margin and the
                        // pricing note from the enriched /truth group, falling back to
                        // Σ qty × unit_price ÷ group.quantity_ordered (which /truth
                        // already divides by ppq).
                        const g = groupFinancials(entry);
                        const groupKey = `group-${entry.source_assembly_id}-${idx}`;
                        return (
                        <React.Fragment key={groupKey}>
                            <tr className="table-secondary border-bottom border-secondary">
                            <td colSpan="3">
                                <div className="d-flex align-items-center ps-2">
                                <i className="bi bi-box-seam-fill me-2 text-primary fs-5"></i>
                                <div>
                                    {/* The group header is the assembly itself —
                                        source_assembly_id opens it in the same
                                        inspector its child parts use. */}
                                    <div className="fw-bold">
                                      <CatalogItemLink
                                        productId={entry.assembly_name}
                                        item={entry.source_assembly_id
                                          ? { kind: 'assembly', id: entry.source_assembly_id } : null}
                                        onInspect={setInspectedItem}
                                        className="fs-6"
                                        title={`View details for assembly ${entry.assembly_name}`}
                                      />
                                    </div>
                                    <div className="small text-muted fst-italic">
                                      Assembly Build · {g.part_count} part{g.part_count === 1 ? "" : "s"} · {g.component_count} component{g.component_count === 1 ? "" : "s"}
                                      {g.ops_folded_per_unit != null && " · ops folded"}
                                      {g.override_per_set != null && " · override"}
                                    </div>
                                    {g.pricing_note && (
                                      <div className="small text-primary-emphasis" style={{ fontSize: "0.75rem" }}>
                                        <i className="bi bi-info-circle me-1"></i>{g.pricing_note}
                                      </div>
                                    )}
                                </div>
                                </div>
                            </td>
                            <td className="text-center fw-bold fs-5" title="sets = primary line qty ÷ primary parts per assembly">
                              {safeNum(entry.quantity_ordered)}
                              <div className="fw-normal text-muted" style={{ fontSize: "0.7rem" }}>sets</div>
                            </td>
                            <td className="text-center">
                                <span className="badge bg-light text-muted small">—</span>
                            </td>
                            <td colSpan="3"></td>
                            <td
                              className="text-end"
                              title={g.sets ? `group subtotal ${fmtMoneyShared(g.group_subtotal)} ÷ ${g.sets} sets` : "set count unknown"}
                            >
                              <div className="fw-bold">
                                {fmtMoneyShared(g.per_set_price, { dash: true })}
                                <span className="small fw-normal text-muted">/set</span>
                              </div>
                              {g.override_per_set != null && (
                                <span className="badge bg-warning text-dark" style={{ fontSize: "0.65rem" }} title="assembly_price_overrides from the quote">
                                  override {fmtMoneyShared(g.override_per_set)}/set
                                </span>
                              )}
                            </td>
                            <td className="text-end fw-bold" title="Σ (qty × unit price) over the group's lines">
                              {fmtMoneyShared(g.group_subtotal)}
                            </td>
                            <td
                              className="text-end"
                              title={g.per_set_cost != null ? `group cost ${fmtMoneyShared(g.group_cost)} ÷ ${g.sets} sets` : "no cost snapshots in this group"}
                            >
                              {fmtMoneyShared(g.per_set_cost, { dash: true })}
                              {g.per_set_cost != null && <span className="small fw-normal text-muted">/set</span>}
                            </td>
                            <td className="text-end" title="Σ (qty × est unit cost) over the group's lines that carry a cost">
                              {fmtMoneyShared(g.group_cost, { dash: true })}
                              {g.cost_missing_count > 0 && (
                                <div className="text-warning-emphasis" style={{ fontSize: "0.65rem" }}>
                                  {g.cost_missing_count} line{g.cost_missing_count === 1 ? "" : "s"} without cost
                                </div>
                              )}
                            </td>
                            <td
                              className="text-end"
                              title={g.group_margin == null ? "needs a group cost" : `(${fmtMoneyShared(g.group_subtotal)} − ${fmtMoneyShared(g.group_cost)}) ÷ ${fmtMoneyShared(g.group_subtotal)}`}
                            >
                              <span className={g.group_margin != null && g.group_margin < 0 ? "text-danger fw-bold" : ""}>{fmtPct(g.group_margin)}</span>
                            </td>
                            </tr>
                            {(entry.components || []).map((line) => (
                              <React.Fragment key={`${groupKey}-line-${line?.sales_order_line_id ?? line?.product_id}`}>
                                {renderSingleLineRow(line, true, true)}
                                {/* "Assembly operations (folded)" pseudo-row under the primary:
                                    the quote's ops line was folded INTO the primary's unit price
                                    at conversion, so this row explains the number above it and
                                    is never added to any total. */}
                                {g.ops_folded_per_unit != null && line?.sales_order_line_id === g.primary_line_id && (
                                  <tr className="fst-italic text-muted" style={{ fontSize: "0.85rem" }}>
                                    <td colSpan="3" style={{ paddingLeft: "60px", borderLeft: "4px solid #dee2e6" }}>
                                      <i className="bi bi-gear me-2"></i>Assembly operations (folded)
                                      <div style={{ fontSize: "0.7rem" }}>
                                        from the quote's ops line — already inside the primary's unit price above, not an additional charge
                                      </div>
                                    </td>
                                    <td className="text-center">{safeNum(line?.quantity_ordered)}</td>
                                    <td colSpan="4"></td>
                                    <td className="text-end" title="Σ(ops price × ops qty) ÷ primary qty at conversion">
                                      {fmtMoneyShared(g.ops_folded_per_unit)}<span className="small">/ea</span>
                                    </td>
                                    <td className="text-end" title={`${safeNum(line?.quantity_ordered)} × ${fmtMoneyShared(g.ops_folded_per_unit)}`}>
                                      {fmtMoneyShared(g.ops_folded_per_unit * safeNum(line?.quantity_ordered))}
                                      <div style={{ fontSize: "0.65rem" }}>included above</div>
                                    </td>
                                    <td className="text-end" colSpan="3">
                                      <span style={{ fontSize: "0.7rem" }}>ops cost is inside the primary's cost snapshot</span>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                        </React.Fragment>
                        );
                    }
                    if (entry?.type === "item") return renderSingleLineRow(entry.data, false, false);
                    return renderSingleLineRow(entry, false, false);
                    })
              )}
            </tbody>
            {!isEditingLines && (() => {
              // Lines subtotal / known cost / margin over the flattened lines —
              // the subtotal here must equal the header's "Lines subtotal".
              const t = linesTotals(allLines);
              return (
                <tfoot className="table-light">
                  <tr className="fw-bold">
                    <td colSpan="9" className="text-end small text-muted fw-normal">
                      Lines subtotal (Σ qty × unit price over {t.line_count} line{t.line_count === 1 ? "" : "s"}) — equals the header's Lines subtotal
                    </td>
                    <td className="text-end">{fmtMoneyShared(t.subtotal)}</td>
                    <td className="text-end small fw-normal text-muted">
                      {t.cost_missing_count > 0 ? `${t.cost_line_count}/${t.line_count} costed` : ""}
                    </td>
                    <td className="text-end" title="Σ ext cost over lines that carry a cost snapshot">
                      {fmtMoneyShared(t.cost, { dash: true })}
                    </td>
                    <td className="text-end" title="(lines subtotal − known cost) ÷ lines subtotal">
                      <span className={t.margin != null && t.margin < 0 ? "text-danger" : ""}>{fmtPct(t.margin)}</span>
                    </td>
                  </tr>
                </tfoot>
              );
            })()}
            {isEditingLines && (
              <tfoot>
                <tr>
                  <td colSpan="14" className="text-center">
                    <button className="btn btn-sm btn-outline-secondary" onClick={handleAddLine}>
                      <i className="bi bi-plus-lg me-1"></i> Add New Line
                    </button>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    );
  };

  /**
   * --------------------
   * Production deep dive
   * --------------------
   * Rows are grouped the way the shop reads a job (2026-09-08, Mike, SO-1119 /
   * job 9537): the shared laser sheet (or saw list) first, then one block per
   * part in routing order. A part's header row carries a routing strip of
   * station chips (the glance) and toggles the station rows beneath it (the
   * audit); per-set twins fold into one row that opens into "Set 1 / Set 2"; a
   * decoupled defect sits under the station that made it; a laser-only part
   * consumed at a join (526-3104) gets a header instead of vanishing. The
   * grouping itself lives in src/utils/groupJobTasks.js (pure, unit-tested);
   * the chip strip is src/components/RoutingStrip.jsx.
   */
  const togglePart = (pk) => setExpandedParts((prev) => ({ ...prev, [pk]: !prev[pk] }));
  const setAllParts = (job, grouped, open) =>
    setExpandedParts((prev) => {
      const next = { ...prev };
      grouped.groups.forEach((g) => {
        if (!g.sharedOnly) next[`${job.job_id}:${g.part_id}`] = open;
      });
      return next;
    });
  const toggleSets = (sk) => setExpandedSets((prev) => ({ ...prev, [sk]: !prev[sk] }));
  const fmtSched = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString() : "-");
  const fmtStamp = (iso) => (iso ? new Date(iso).toLocaleString() : "-");
  const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "numeric", day: "numeric" }) : "");
  const fmtDur = (sec) => {
    const s = safeNum(sec);
    if (s <= 0) return "—";
    if (s < 600) {
      const m = Math.floor(s / 60);
      const r = Math.round(s % 60);
      return m > 0 ? `${m}m ${r}s` : `${r}s`;
    }
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };
  const stampCell = (row) => (row.end_time ? fmtStamp(row.end_time) : row.start_time ? "Started: " + fmtStamp(row.start_time) : "-");
  const userChip = (name) => (
    <span className="badge bg-light text-dark border">
      <i className="bi bi-person me-1"></i>
      {name || "Operator"}
    </span>
  );

  // Pace comes from /production_progress (schedule variance per task). A
  // folded step takes its worst task: the largest variance, blocked if any is.
  const paceForTaskIds = (taskIds) => {
    const rows = (progressData?.task_progress || []).filter((p) => taskIds.includes(p.task_id));
    if (rows.length === 0) return null;
    let variance = null;
    rows.forEach((r) => {
      if (r.schedule_variance_days != null && (variance == null || r.schedule_variance_days > variance)) variance = r.schedule_variance_days;
    });
    return { variance, blocked: rows.some((r) => r.is_blocked) };
  };
  const renderPaceBadge = (pace, status) => {
    if (!pace || pace.variance == null) return <span className="text-muted">—</span>;
    const v = pace.variance;
    if (status === "Completed") {
      if (v <= 0) return <span className="badge bg-success-subtle text-success"><i className="bi bi-check-lg"></i></span>;
      if (v <= 2) return <span className="badge bg-warning-subtle text-warning">{v}d late</span>;
      return <span className="badge bg-danger-subtle text-danger">{v}d late</span>;
    }
    if (pace.blocked) return <span className="badge bg-secondary-subtle text-secondary"><i className="bi bi-lock-fill me-1"></i>Blocked</span>;
    if (v > 0) return <span className="badge bg-danger-subtle text-danger">{v}d over</span>;
    if (v === 0) return <span className="badge bg-primary-subtle text-primary">Today</span>;
    return <span className="badge bg-success-subtle text-success">In {Math.abs(v)}d</span>;
  };
  const chipStateFor = (step, pace, isNext) => {
    if (step.terminal) return "cancelled";
    if (step.status === "Completed") return "done";
    if (step.status === "In Progress" || step.status === "Partial") return "active";
    if ((pace && pace.blocked) || step.blocked) return "blocked";
    if (pace && pace.variance != null && pace.variance > 0) return "late";
    if (pace && pace.variance === 0) return "today";
    return isNext ? "next" : "pending";
  };
  const stripStepsFor = (group) => {
    const out = [];
    group.sharedTasks.forEach((s) => {
      const where = s.cut_sheet_id != null ? `sheet #${s.cut_sheet_id}` : s.cut_list_id != null ? `list #${s.cut_list_id}` : "batch";
      // The shared task follows the same colour flow as every other chip:
      // green once done, pace-driven until then. A yellow "shared" chip read
      // like the "today" yellow on Coating (Mike, 2026-09-09).
      const sharedLabel = s.sub_process ? `${s.workstation} (${s.sub_process})` : s.workstation;
      const sharedState = chipStateFor(
        { terminal: ["Cancelled", "Voided"].includes(s.status), status: s.status, blocked: !!s.blocked },
        paceForTaskIds([s.task_id]),
        true
      );
      out.push({
        key: `shared-${s.task_id}`,
        label: sharedLabel,
        sub: s.status === "Completed" ? `${where} · ${fmtDay(s.end_time)}` : `${where} · ${s.status}`,
        title: `${sharedLabel} · ${s.status} · ${where} · shared by every part on the job${s.last_user_name ? ` · ${s.last_user_name}` : ""}`,
        state: sharedState,
      });
    });
    group.steps.forEach((step) => {
      const pace = paceForTaskIds(step.taskIds);
      const isNext = !!group.next && group.next.key === step.key;
      let state = chipStateFor(step, pace, isNext);
      let sub;
      if (step.terminal) sub = step.status;
      else if (step.status === "Completed") sub = [fmtDur(step.elapsed_seconds), step.last_user_name, fmtDay(step.end_time)].filter((x) => x && x !== "—").join(" · ");
      else if (step.status === "In Progress" || step.status === "Partial") sub = `${step.setsDone || step.qty_completed} of ${step.sets || step.quantity} done`;
      else if (step.scheduled_date) sub = `sched ${fmtSched(step.scheduled_date)}${pace && pace.variance > 0 ? ` · ${pace.variance}d over` : pace && pace.variance === 0 ? " · today" : ""}`;
      else sub = "not scheduled";
      // At an outside vendor (the task stays Pending while its manifest is out):
      // say so instead of the schedule (Jesse, 2026-10-20). The job's own view, so
      // every paper's pieces count; the phone's job strips use the same outsideChip.
      const outside = outsideChip(step, { today: shopDay, shownSoId: detailData?.header?.sales_order_id ?? null });
      if (outside && outside.state) {
        state = outside.state;
        sub = outside.sub;
      }
      const paperNote = outside ? outside.paperNote : "";
      const decoupled = step.defectRows.reduce((a, d) => a + safeNum(d.defective_quantity), 0);
      out.push({
        key: step.key,
        label: step.label,
        sub,
        title: `${step.label} · ${step.status}${step.sets ? ` · ${step.setsDone}/${step.sets} sets` : ""}${paperNote}`,
        state,
        sets: step.sets > 0 ? { done: step.setsDone, total: step.sets } : null,
        defects: step.defect_quantity + decoupled,
        stepRef: step,
      });
    });
    if (group.sharedOnly) {
      out.push({
        key: "join",
        label: group.joinsProductId ? `Joins ${group.joinsProductId}` : group.componentOfAssembly ? "Consumed in assembly" : "No further stations",
        sub: group.joinWorkstation ? `at ${group.joinWorkstation}` : group.componentOfAssembly || (group.sharedKind === "list" ? "cut to length only" : "laser only"),
        state: "join",
      });
    }
    return out;
  };

  const renderDefectRow = (d, indent) => (
    <tr key={`dd-${d.defect_id}`} className="table-danger">
      <td className={`fw-bold ${indent ? "ps-4" : ""}`}>
        {indent ? <i className="bi bi-arrow-return-right me-1 text-muted"></i> : d.workstation}
        <span className="badge bg-danger ms-2">DEFECT</span>
      </td>
      <td className="small text-muted">-</td>
      <td className="text-center"><span className="text-muted">—</span></td>
      <td>
        {!indent && <span className="font-monospace fw-semibold">{d.product_id}</span>}
        <div className="small text-muted" style={{ maxWidth: "260px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={d.defect_reason || ""}>
          {d.defect_reason || ""}
        </div>
      </td>
      <td className="text-center"><span className="badge bg-danger">Defected</span>{tmDecisions.has(d.defect_id) && <TmDecisionChip decision={tmDecisions.get(d.defect_id)} />}</td>
      <td className="text-center">-</td>
      <td className="text-center text-danger fw-bold">
        {safeNum(d.defective_quantity)}<span className="text-muted small ms-1">pcs</span>
      </td>
      <td className="text-center">-</td>
      <td>-</td>
      <td>{userChip(d.recorded_by_name || "?")}</td>
      <td className="small text-muted">{fmtStamp(d.created_at)}</td>
    </tr>
  );

  const renderSharedRow = (task, job, otherOrders = []) => {
    const isSheet = task.cut_sheet_id != null;
    const isList = task.cut_list_id != null;
    const unit = isSheet ? "sheets" : isList ? "sticks" : "pcs";
    const qtyValue = safeNum(task.sheet_required || task.quantity);
    const doneValue = safeNum(task.sheet_count != null ? task.sheet_count : task.qty_completed);
    const parts = Array.isArray(task.parts) ? task.parts : [];
    const defectsByPart = Array.isArray(task.defects_by_part) ? task.defects_by_part : [];
    const totalDefects = safeNum(task.defect_quantity);
    return (
      <tr key={task.task_id} className={task.status === "Completed" ? "" : "table-warning"}>
        <td className="fw-bold">
          {task.workstation} {task.sub_process ? `(${task.sub_process})` : ""}
          <span className="badge bg-light text-dark border ms-2" title={otherOrders.length ? `This task also cut parts for ${otherOrders.join(", ")}: its sheets and time are the whole task's, not this order's alone` : "Every part on this job rides on this task"}>
            {otherOrders.length ? `shared with ${otherOrders.join(", ")}` : "shared"}
          </span>
        </td>
        <td className="small text-muted">{fmtSched(task.scheduled_date)}</td>
        <td className="text-center">{renderPaceBadge(paceForTaskIds([task.task_id]), task.status)}</td>
        <td style={{ minWidth: "260px" }}>
          <div className="d-flex flex-wrap align-items-center gap-1">
            <span className="font-monospace fw-semibold">
              {isSheet ? `Sheet #${task.cut_sheet_id}` : isList ? `List #${task.cut_list_id}` : "Batch / Sheet Task"}
            </span>
            {parts.length > 0 ? <span className="small text-muted">feeds</span> : <span className="fst-italic text-muted small">{isSheet ? "sheet task" : "batch task"}</span>}
            {canOfferCutSheetSwitch(task, job?.job_status) && (
              <button type="button" className="btn btn-outline-warning btn-sm py-0 px-1 ms-1"
                title="Cut this job from another cut sheet (the same nest on other steel)"
                onClick={() => setSwitchSheetTaskId(task.task_id)}>
                Switch sheet
              </button>
            )}
            {parts.map((p) => (
              <span key={p.part_id} className="badge bg-white text-dark border fw-normal">
                <ProductIdLink productId={p.product_id || `part #${p.part_id}`} partId={p.part_id} onInspect={setInspectedPartId} className="text-dark" />
                {p.quantity != null && <span className="ms-1">×{safeNum(p.quantity)}</span>}
              </span>
            ))}
          </div>
        </td>
        <td className="text-center"><StatusBadge status={task.status} /></td>
        <td className="text-center fw-bold">{qtyValue}<span className="text-muted small ms-1">{unit}</span></td>
        <td className="text-center text-danger fw-bold">
          {totalDefects > 0 ? (
            defectsByPart.length > 0 ? (
              <div className="d-flex flex-column align-items-center">
                {defectsByPart.map((d) => (
                  <span key={d.part_id} className="small" style={{ lineHeight: 1.2 }}>
                    {safeNum(d.defective_quantity)} <span className="text-muted">× </span>
                    <ProductIdLink productId={d.product_id || `part #${d.part_id}`} partId={d.part_id} onInspect={setInspectedPartId} className="text-muted" />
                  </span>
                ))}
              </div>
            ) : (
              <>{totalDefects}<span className="text-muted small ms-1">pcs</span></>
            )
          ) : "-"}
        </td>
        <td className="text-center text-success">{doneValue}<span className="text-muted small ms-1">{unit}</span></td>
        <td><TimeComparison estimated={task.estimated_time} actual={task.elapsed_time_seconds} /></td>
        <td>{userChip(task.last_user_name)}</td>
        <td className="small text-muted">{stampCell(task)}</td>
      </tr>
    );
  };

  const renderStepRows = (job, group, step) => {
    const sk = `${job.job_id}:${group.part_id}:${step.key}`;
    const setsOpen = !!expandedSets[sk];
    const pace = paceForTaskIds(step.taskIds);
    const decoupled = step.defectRows.reduce((a, d) => a + safeNum(d.defective_quantity), 0);
    const defectsHere = step.defect_quantity + decoupled;
    const rows = [
      <tr key={sk} className={step.terminal ? "text-muted" : ""}>
        <td className="fw-bold ps-4">
          {step.label}
          {step.sets > 0 && (
            <>
              <span className="badge bg-info-subtle text-info ms-2" title="One task per set"><i className="bi bi-layers me-1"></i>{step.sets} sets</span>
              <button type="button" className="btn btn-link btn-sm p-0 ms-2 text-decoration-none" onClick={() => toggleSets(sk)} aria-expanded={setsOpen} aria-label={`${setsOpen ? "Hide" : "Show"} sets for ${step.label}`}>
                <i className={`bi ${setsOpen ? "bi-caret-down-fill" : "bi-caret-right-fill"} me-1`}></i>sets
              </button>
            </>
          )}
        </td>
        <td className="small text-muted">{fmtSched(step.scheduled_date)}</td>
        <td className="text-center">{renderPaceBadge(pace, step.status)}</td>
        <td className="small text-muted">{step.taskIds.length === 1 ? `task #${step.taskIds[0]}` : `tasks #${step.taskIds.join(", #")}`}</td>
        <td className="text-center">
          <StatusBadge status={step.status === "Partial" ? "In Progress" : step.status} />
          {step.sets > 0 && <div className="small text-muted">{step.setsDone}/{step.sets}</div>}
        </td>
        <td className="text-center fw-bold">{step.quantity}<span className="text-muted small ms-1">pcs</span></td>
        <td className="text-center text-danger fw-bold">{defectsHere > 0 ? <>{defectsHere}<span className="text-muted small ms-1">pcs</span></> : "-"}</td>
        <td className="text-center text-success">{step.qty_completed}<span className="text-muted small ms-1">pcs</span></td>
        <td><TimeComparison estimated={step.estimated_seconds} actual={step.elapsed_seconds} /></td>
        <td>{userChip(step.last_user_name)}</td>
        <td className="small text-muted">{stampCell(step)}</td>
      </tr>,
    ];
    if (step.sets > 0 && setsOpen) {
      step.tasks.forEach((t, i) =>
        rows.push(
          <tr key={`${sk}:${t.task_id}`} className="small" style={{ background: "var(--bs-tertiary-bg)" }}>
            <td className="ps-5 text-muted">{setLabel(t, i)}</td>
            <td className="text-muted">{fmtSched(t.scheduled_date)}</td>
            <td className="text-center">{renderPaceBadge(paceForTaskIds([t.task_id]), t.status)}</td>
            <td className="text-muted">task #{t.task_id}</td>
            <td className="text-center"><StatusBadge status={t.status} /></td>
            <td className="text-center">{safeNum(t.quantity)}</td>
            <td className="text-center text-danger">{safeNum(t.defect_quantity) > 0 ? safeNum(t.defect_quantity) : "-"}</td>
            <td className="text-center text-success">{safeNum(t.qty_completed)}</td>
            <td><TimeComparison estimated={t.estimated_time} actual={t.elapsed_time_seconds} /></td>
            <td>{userChip(t.last_user_name)}</td>
            <td className="text-muted">{stampCell(t)}</td>
          </tr>
        )
      );
    }
    step.defectRows.forEach((d) => rows.push(renderDefectRow(d, true)));
    return rows;
  };

  const renderPartGroup = (job, group, assemblyByPart, soLines, owners = null) => {
    const pk = `${job.job_id}:${group.part_id}`;
    const open = !!expandedParts[pk];
    const asm = assemblyByPart[group.part_id];
    const lines = soLines[group.part_id] || [];
    const nextPace = group.next ? paceForTaskIds(group.next.taskIds) : null;
    const label = group.product_id || `part #${group.part_id}`;
    return (
      <React.Fragment key={pk}>
        <tr className="so-part-header" style={{ background: "var(--bs-tertiary-bg)", borderTop: "2px solid var(--bs-border-color)", opacity: owners ? 0.75 : 1 }}>
          <td colSpan={11} style={{ padding: "8px 10px" }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center flex-wrap gap-2" style={{ minWidth: 0 }}>
                {group.sharedOnly ? (
                  <i className="bi bi-dash text-muted" aria-hidden="true"></i>
                ) : (
                  <button type="button" className="btn btn-sm btn-link p-0 text-decoration-none" onClick={() => togglePart(pk)} aria-expanded={open} aria-label={`${open ? "Hide" : "Show"} stations for ${label}`}>
                    <i className={`bi ${open ? "bi-caret-down-fill" : "bi-caret-right-fill"}`}></i>
                  </button>
                )}
                <ProductIdLink productId={label} partId={group.part_id} onInspect={setInspectedPartId} className="text-primary fw-semibold" />
                {group.description && (
                  <span className="small text-muted text-truncate" style={{ maxWidth: "360px" }} title={group.description}>{group.description}</span>
                )}
                {group.qty > 0 && <span className="badge bg-primary-subtle text-primary">{group.qty} pcs</span>}
                {lines.length > 0 && <span className="badge bg-secondary-subtle text-secondary">SO line {lines.join(", ")}</span>}
                {owners && <span className="badge bg-warning-subtle text-warning-emphasis border" title="Not on this order: it rides on the same job">for {owners.join(", ")}</span>}
                {asm && (
                  <span className="badge bg-info-subtle text-info" title={asm.isPrimary ? "Primary part of the assembly" : "Component of the assembly"}>
                    <i className="bi bi-layers me-1"></i>{asm.sets} {asm.sets === 1 ? "set" : "sets"} · {asm.assembly_name}
                  </span>
                )}
                {group.defectCount > 0 && (
                  <span className="badge bg-danger-subtle text-danger"><i className="bi bi-exclamation-triangle me-1"></i>{group.defectCount} {group.defectCount === 1 ? "defect" : "defects"}</span>
                )}
              </div>
              <div className="d-flex align-items-center flex-wrap gap-3 small text-muted">
                {group.sharedOnly ? (
                  <span>
                    <i className="bi bi-arrow-return-right me-1"></i>
                    {group.sharedKind === "list" ? "Saw list only" : "Laser only"}
                    {group.joinsProductId ? (
                      <> · joins <span className="font-monospace">{group.joinsProductId}</span>{group.joinWorkstation ? ` at ${group.joinWorkstation}` : ""}</>
                    ) : group.componentOfAssembly ? (
                      <> · consumed in {group.componentOfAssembly}</>
                    ) : null}
                    {" · no further stations"}
                  </span>
                ) : (
                  <>
                    <span><strong>{group.stepsDone} of {group.stepsTotal}</strong> steps done</span>
                    {group.next ? (
                      <span>
                        Next: <strong>{group.next.label}</strong>
                        {group.next.scheduled_date ? ` · ${fmtSched(group.next.scheduled_date)}` : ""} {renderPaceBadge(nextPace, group.next.status)}
                      </span>
                    ) : (
                      <span className="text-success"><i className="bi bi-check-circle me-1"></i>All stations done</span>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="mt-2">
              <RoutingStrip
                steps={stripStepsFor(group)}
                ariaLabel={`Routing for ${label}`}
                onStepClick={(s) => { if (s.stepRef && !open) togglePart(pk); }}
              />
            </div>
          </td>
        </tr>
        {open && group.steps.flatMap((step) => renderStepRows(job, group, step))}
        {open && group.looseDefects.map((d) => renderDefectRow(d, true))}
      </React.Fragment>
    );
  };

  const renderProductionDeepDive = () => {
    if (!detailData || !detailData.production_data || detailData.production_data.length === 0) {
      return (
        <div className="alert alert-info shadow-sm">
          <i className="bi bi-info-circle me-2"></i> No active production jobs linked to this order yet.
        </div>
      );
    }
    const assemblyByPart = assemblyInfoByPart(detailData.lines);
    const soLines = soLinesByPart(flattenTruthLines(detailData.lines));
    const scope = orderScope(detailData);

    return (
      <div className="mb-4">
        <h5 className="text-primary mb-3">
          <i className="bi bi-gear-wide-connected me-2"></i>Production Detail (Live Floor Data)
        </h5>

        <div className="accordion shadow-sm" id="productionAccordion">
          {detailData.production_data.map((job, idx) => {
            // Compute display status: if job is "In Progress" but all non-Shipping
            // tasks are done, show "Awaiting Shipment" instead
            const tasks = job.tasks || [];
            const nonShipTasks = tasks.filter(t => t.workstation !== "Shipping" && !["Cancelled", "Voided"].includes(t.status));
            const allProdDone = nonShipTasks.length > 0 && nonShipTasks.every(t => t.status === "Completed");
            const hasShipPending = tasks.some(t => t.workstation === "Shipping" && !["Completed", "Cancelled", "Voided"].includes(t.status));
            const displayStatus = (job.job_status === "In Progress" && allProdDone && hasShipPending)
              ? "Awaiting Shipment" : job.job_status;
            // This order's parts only; the job's other orders' parts sit behind a toggle.
            const grouped = scopeJobToOrder(groupJobTasks(job), job, scope);
            const { summary } = grouped;
            const othersOpen = !!showOtherParts[job.job_id];
            const togglable = grouped.groups.filter((g) => !g.sharedOnly);
            const allOpen = togglable.length > 0 && togglable.every((g) => expandedParts[`${job.job_id}:${g.part_id}`]);

            return (
            <div className="accordion-item" key={job.job_id}>
              <h2 className="accordion-header">
                <button
                  className={`accordion-button ${idx !== 0 ? "collapsed" : ""}`}
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#collapseJob${job.job_id}`}
                >
                  <div className="d-flex justify-content-between w-100 me-3 align-items-center flex-wrap gap-2">
                    <span>
                      <strong className="me-2">Job #{job.master_job_num || job.job_id}</strong>
                      <span className="text-muted me-3">({job.job_description})</span>
                      <StatusBadge status={displayStatus} />
                      {job._source === "allocated" && (
                        <span className="badge bg-info text-dark ms-2" title={`Parts allocated from ${job._source_so_number || "another SO"}`}>
                          <i className="bi bi-arrow-left-right me-1"></i>Allocated from {job._source_so_number || "another SO"}
                        </span>
                      )}
                    </span>
                    {tasks.length > 0 && (
                      <span className="small text-muted d-flex flex-wrap gap-3 so-job-summary">
                        {summary.sheets.length > 0 && <span>Sheet <strong>{summary.sheets.join(", ")}</strong></span>}
                        {summary.lists.length > 0 && <span>List <strong>{summary.lists.join(", ")}</strong></span>}
                        <span><strong>{summary.partCount}</strong> {summary.partCount === 1 ? "part" : "parts"}</span>
                        <span><strong>{summary.taskCount}</strong> tasks</span>
                        {summary.next && (
                          <span>Next: <strong>{summary.next.label}</strong>{summary.next.scheduled_date ? ` · ${fmtSched(summary.next.scheduled_date)}` : ""}</span>
                        )}
                      </span>
                    )}
                  </div>
                </button>
              </h2>
              <div
                id={`collapseJob${job.job_id}`}
                className={`accordion-collapse collapse ${idx === 0 ? "show" : ""}`}
                data-bs-parent="#productionAccordion"
              >
                <div className="accordion-body p-0">
                  {togglable.length > 0 && (
                    <div className="d-flex justify-content-end px-2 py-1 border-bottom bg-light">
                      <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => setAllParts(job, grouped, !allOpen)}>
                        <i className={`bi ${allOpen ? "bi-arrows-collapse" : "bi-arrows-expand"} me-1`}></i>
                        {allOpen ? "Collapse all parts" : "Expand all parts"}
                      </button>
                    </div>
                  )}
                  <table className="table table-sm mb-0" style={{ fontSize: "0.9rem" }}>
                    <thead className="table-dark">
                      <tr>
                        <th>Station</th>
                        <th>Scheduled</th>
                        <th>Pace</th>
                        <th>Part / Sheet</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">Qty</th>
                        <th className="text-center text-danger">Defects</th>
                        <th className="text-center">Done</th>
                        <th>Time (Est / Act)</th>
                        <th>Last User</th>
                        <th>Timestamp</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped.shared.map((t) => renderSharedRow(t, job, grouped.otherOrders))}
                      {grouped.groups.map((g) => renderPartGroup(job, g, assemblyByPart, soLines))}
                      {grouped.others.length > 0 && (
                        <tr className="so-other-parts-toggle">
                          <td colSpan={11} className="small text-muted" style={{ padding: "6px 10px" }}>
                            <button type="button" className="btn btn-link btn-sm p-0 text-decoration-none"
                              onClick={() => setShowOtherParts((prev) => ({ ...prev, [job.job_id]: !prev[job.job_id] }))}
                              aria-expanded={othersOpen}>
                              <i className={`bi ${othersOpen ? "bi-eye-slash" : "bi-eye"} me-1`}></i>
                              {othersOpen ? "Hide" : "Show"} {grouped.others.length} other {grouped.others.length === 1 ? "part" : "parts"} on this job ({grouped.otherOrders.join(", ")})
                            </button>
                            <span className="ms-2">not on this order</span>
                          </td>
                        </tr>
                      )}
                      {othersOpen && grouped.others.map((o) => renderPartGroup(job, o.group, assemblyByPart, soLines, o.owners))}
                      {/* Defects with no part (purchased components) keep their own
                          red ledger line at the bottom, as before (SO-1102, 2026-08-05). */}
                      {grouped.unmatchedDefects.map((d) => renderDefectRow(d, false))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handlePrintInvoice = (invoiceId) => openPdf(`${API_BASE}/invoices/${invoiceId}/pdf`);
  const handleReprintManifest = (batch, mode = "so") => {
    // mode: "so" — customer-facing per-SO manifest (this SO's items only
    //              with cross-SO footnotes pointing at companion SOs).
    //       "combined" — driver/ops view: full physical shipment with every
    //              item on every pallet regardless of source SO, per-box
    //              "From SO-XXXX" chips on multi-source pallets.
    // Finalized SO Plan -> unified manifest. When the row is a BATCH of a
    // split shipment (groupIds collected from its pallets), scope the
    // manifest to exactly those shipment groups via ?groups= so a second
    // batch reprints its own document instead of the whole plan. A plan
    // that shipped in one batch passes all of its groups — same content
    // as the unscoped manifest.
    const path = batchManifestPath(batch, mode); // features/salesOrders/soData.js
    if (!path) return alert("This is a legacy shipment without a batch ID.");
    return openPdf(`${API_BASE}${path}`);
  };

  const renderDocuments = () => {
    if (!detailData || !detailData.related_documents) return null;
    const { invoices = [], shipments = [] } = detailData.related_documents;

    // Shipments grouped into batches, newest first: features/salesOrders/soData.js shipmentBatches
    // (shared with the phone's Documents).
    const sortedBatches = shipmentBatches(shipments);

    // Ride-along detection: this plan is a ride-along scenario when any
    // of its boxes physically rode on another plan's pallet (outgoing),
    // or its pallets host boxes from another plan (incoming via the
    // boxes' source plan_id). Surfaces a "Ride-along" badge on each
    // shipment row + swaps the single Manifest button for Combined +
    // This-SO-only when true.
    const outgoingRiders = planSummary?.outgoing_ride_alongs?.length || 0;
    const incomingRiders = planSummary?.incoming_riders?.length || 0;
    const hasRideAlongs = outgoingRiders > 0 || incomingRiders > 0;
    const rideAlongTitle = hasRideAlongs
      ? `This shipment travelled with parts from other sales orders (${outgoingRiders ? `${outgoingRiders} outgoing` : ""}${outgoingRiders && incomingRiders ? ", " : ""}${incomingRiders ? `${incomingRiders} incoming` : ""}). Combined manifest shows the full physical shipment; per-SO manifest scopes to this SO only.`
      : "";

    return (
      <>
      <div className="row g-3">
        <div className="col-md-4">
          <DocumentsCard salesOrderId={selectedId} API_BASE={API_BASE} />
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100 border-top-0 border-end-0 border-bottom-0 border-4 border-success">
            <div className="card-header bg-white fw-bold d-flex justify-content-between align-items-center">
              <span><i className="bi bi-receipt me-2 text-success"></i>Invoices</span>
            </div>
            <ul className="list-group list-group-flush">
              {invoices.length === 0 ? (
                <li className="list-group-item text-muted fst-italic">No invoices generated.</li>
              ) : (
                invoices.map((inv) => (
                  <li key={inv.invoice_id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <span className="fw-bold me-2">#{inv.invoice_number}</span>
                        <StatusBadge status={inv.status} />
                      </div>
                      <span className="fw-bold">${safeNum(inv.total).toFixed(2)}</span>
                    </div>
                    <div className="d-flex gap-2 justify-content-end">
                      <a href={`/invoices/${inv.invoice_id}`} className="btn btn-sm btn-outline-secondary">
                        <i className="bi bi-eye me-1"></i> View
                      </a>
                      <button onClick={() => handlePrintInvoice(inv.invoice_id)} className="btn btn-sm btn-outline-danger">
                        <i className="bi bi-file-pdf me-1"></i> PDF
                      </button>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100 border-top-0 border-end-0 border-bottom-0 border-4 border-info">
            <div className="card-header bg-white fw-bold">
              <i className="bi bi-box-seam me-2 text-info"></i>Shipments
            </div>
            <div className="list-group list-group-flush">
              {shipments.length === 0 ? (
                <div className="list-group-item text-muted fst-italic">No shipments yet.</div>
              ) : (
                sortedBatches.map((batch) => (
                  <div key={batch.id} className="list-group-item p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="small text-muted text-uppercase fw-bold">
                        {new Date(batch.date).toLocaleDateString()}
                        {batch.isPlanRollup && (
                          <span className="badge bg-success ms-2" title="Closed out via SO Finalize. Click Manifest for the unified shipping document.">
                            SO Finalized
                          </span>
                        )}
                        {batch.isPlanRollup && hasRideAlongs && (
                          <span
                            className="badge bg-warning text-dark ms-2"
                            title={rideAlongTitle}
                          >
                            <i className="bi bi-arrow-left-right me-1"></i>
                            Ride-along
                          </span>
                        )}
                      </div>
                      {batch.id !== "Legacy" && (
                        <div className="d-flex gap-1">
                          {batch.isPlanRollup && hasRideAlongs ? (
                            <>
                              <button
                                className="btn btn-sm btn-warning text-dark border"
                                onClick={() => handleReprintManifest(batch, "combined")}
                                title="Driver/ops view: every item on every pallet in this physical shipment, with per-box source-SO chips."
                              >
                                <i className="bi bi-truck me-1"></i> Combined
                              </button>
                              <button
                                className="btn btn-sm btn-light border text-danger"
                                onClick={() => handleReprintManifest(batch, "so")}
                                title="Customer-facing manifest scoped to this SO's items only."
                              >
                                <i className="bi bi-printer me-1"></i> This SO
                              </button>
                            </>
                          ) : (
                            <button
                              className="btn btn-sm btn-light border text-danger"
                              onClick={() => handleReprintManifest(batch, "so")}
                            >
                              <i className="bi bi-printer me-1"></i> Manifest
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="bg-light p-2 rounded border-start border-3">
                      {batch.items.map((item, idx) => (
                        <div key={idx} className="d-flex justify-content-between small mb-1">
                          <ProductIdLink
                            productId={item.product_id}
                            partId={item.part_id}
                            onInspect={setInspectedPartId}
                          />
                          <span className="fw-bold">Qty: {item.qty_completed}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Proof of delivery photos (iPhone photos uploaded here after the handoff) */}
      <PodPanel
        apiBase={API_BASE}
        planId={planSummary?.plan_id}
        salesOrderId={selectedId}
        orderNumber={detailData?.header?.order_number}
        pallets={planSummary?.pallets || []}
        fulfillmentType={planSummary?.fulfillment_type}
        onChanged={() => setDetailTick(t => t + 1)}
      />
      {/* Email History Row */}
      <div className="row g-3 mt-1">
        <div className="col-12">
          <SOEmailHistoryCard salesOrderId={selectedId} apiBase={API_BASE} />
        </div>
      </div>
      {/* Void surplus jobs (office tool, once every line has shipped) */}
      {detailData?.lines && (
        <SOVoidSurplusBanner
          salesOrderId={selectedId}
          lines={flattenTruthLines(detailData.lines)}
          allowOverShip={detailData?.header?.allow_over_ship}
          onVoided={() => setDetailTick(t => t + 1)}
        />
      )}
      {/* Packaging Plan Row (persistent, per-SO — /packaging/plans/*) */}
      <div className="row g-3 mt-1">
        <div className="col-12">
          <SOPackagingPlanCard salesOrderId={selectedId} apiBase={API_BASE}
            focusPalletId={linkedPalletId && String(selectedId) === String(linkedSoId) ? linkedPalletId : null} />
        </div>
      </div>
      </>
    );
  };

  /**
   * --------------------
   * Main render
   * --------------------
   */
  return (
    <div className="container-fluid p-0 h-100">
      <div className="row g-0 h-100">
        <div className="col-md-3 border-end bg-white" style={{ height: "calc(100vh - 60px)", overflowY: "auto" }}>
          <div className="p-3 border-bottom sticky-top bg-white">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0 fw-bold">Sales Orders</h4>
              <Link to="/sales-order-entry" className="btn btn-sm btn-primary rounded-circle" title="Create Sales Order">
                <i className="bi bi-plus-lg"></i>
              </Link>
            </div>
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Search Orders or PO..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="form-check form-switch small">
              <input className="form-check-input" type="checkbox" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
              <label className="form-check-label text-muted">Show Completed/Shipped</label>
            </div>
            <div className="form-check form-switch small">
              <input className="form-check-input" type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              <label className="form-check-label text-muted">Show Archived</label>
            </div>
            {availableCustomers.length >= 1 && (
              <div className="mt-2 d-flex align-items-center gap-2">
                <span className="small text-muted" style={{ fontSize: '0.75rem' }}>
                  Customer:
                </span>
                <select
                  className="form-select form-select-sm flex-grow-1"
                  style={{ fontSize: '0.8rem' }}
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                  title="Filter orders by customer"
                >
                  <option value="">All customers ({availableCustomers.length})</option>
                  {availableCustomers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {customerFilter && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    style={{ fontSize: '0.7rem', lineHeight: 1 }}
                    onClick={() => setCustomerFilter("")}
                    title="Clear customer filter"
                  >
                    clear
                  </button>
                )}
              </div>
            )}
            {availableStatuses.length >= 1 && (
              <div className="mt-2 d-flex flex-wrap gap-1 align-items-center">
                <span className="small text-muted me-1" style={{ fontSize: '0.75rem' }}>
                  Status:
                </span>
                {availableStatuses.map(status => {
                  const active = statusFilter.includes(status);
                  const anyActive = statusFilter.length > 0;
                  // When no filter is active, every chip renders as "neutral selected"
                  // (subtle outline) so the default state reads as "showing all".
                  // Once the operator picks any chip, un-selected ones fade to muted.
                  const cls = active
                    ? 'btn btn-primary'
                    : anyActive
                      ? 'btn btn-outline-secondary text-muted'
                      : 'btn btn-outline-primary';
                  return (
                    <button
                      key={status}
                      type="button"
                      className={`${cls} btn-sm py-0 px-2`}
                      style={{ fontSize: '0.7rem', lineHeight: 1.6 }}
                      onClick={() => toggleStatusFilter(status)}
                      title={active ? `Click to hide ${status} orders` : `Click to show only ${status} orders`}
                    >
                      {status}
                    </button>
                  );
                })}
                {statusFilter.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 ms-1"
                    style={{ fontSize: '0.7rem', lineHeight: 1 }}
                    onClick={() => setStatusFilter([])}
                    title="Clear status filter"
                  >
                    clear
                  </button>
                )}
              </div>
            )}
          </div>
          {(tmPendingOnly || orders.some(isTmPending)) && (
            <div className="px-3 pb-2">
              <button
                type="button"
                className={`btn btn-sm py-0 px-2 ${tmPendingOnly ? 'btn-info' : 'btn-outline-info'}`}
                style={{ fontSize: '0.7rem', lineHeight: 1.6 }}
                onClick={() => setTmPendingOnly((v) => !v)}
                title={tmPendingOnly ? 'Show every order' : 'Show only Time & Materials orders waiting on pricing'}
              >
                <i className="bi bi-calculator me-1"></i>T&amp;M pricing pending ({orders.filter(isTmPending).length})
              </button>
            </div>
          )}
          <div className="list-group list-group-flush">
            {loadingList ? (
              <div className="text-center p-4">
                <div className="spinner-border text-primary" role="status"></div>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center p-4 text-muted">No orders found.</div>
            ) : (
              filteredOrders.map((order) => (
                <button
                    key={order.sales_order_id}
                    className={`list-group-item list-group-item-action py-3 lh-sm ${
                    selectedId === order.sales_order_id ? "active" : ""
                    }`}
                    onClick={() => setSelectedId(order.sales_order_id)}
                    aria-current={selectedId === order.sales_order_id}
                >
                    <div className="d-flex w-100 justify-content-between mb-1">
                    <strong className="fs-6 text-truncate">
                        {order.order_number}
                        {order.archived_at && (
                          <span className="badge bg-secondary ms-2" style={{ fontSize: '0.6rem', verticalAlign: 'middle' }} title="Archived — hidden from active lists & Booked Revenue">
                            <i className="bi bi-archive me-1"></i>Archived
                          </span>
                        )}
                        <TmOrderChip order={order} selected={selectedId === order.sales_order_id} />
                    </strong>
                    <small className={selectedId === order.sales_order_id ? "text-light" : "text-muted"}>
                        Created: {new Date(order.created_at).toLocaleDateString()}
                    </small>
                    </div>

                    {/* Customer Name Row + Fulfillment chip (pickup/delivery only — shipping is the unmarked default) */}
                    <div className="mb-1 d-flex align-items-center gap-2">
                        <div className="text-truncate pe-2 fw-bold flex-grow-1" style={{fontSize: "0.85rem"}}>
                            {order.customer_name || `Customer ID: ${order.customer_id}`}
                        </div>
                        {order.fulfillment_type === 'pickup' && (
                            <span
                                className={`badge ${selectedId === order.sales_order_id ? 'bg-light text-dark' : 'bg-warning text-dark'}`}
                                style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', whiteSpace: 'nowrap' }}
                                title="Customer Pickup — uses two-stage Mark Ready for Pickup → Confirm Pickup workflow on the Packaging Plan card."
                            >
                                <i className="bi bi-person me-1" style={{ fontSize: '0.7rem' }}></i>Pickup
                            </span>
                        )}
                        {order.fulfillment_type === 'delivery' && (
                            <span
                                className={`badge ${selectedId === order.sales_order_id ? 'bg-light text-dark' : 'bg-secondary'}`}
                                style={{ fontSize: '0.65rem', padding: '0.15rem 0.4rem', whiteSpace: 'nowrap' }}
                                title="We Deliver"
                            >
                                <i className="bi bi-truck me-1" style={{ fontSize: '0.7rem' }}></i>Delivery
                            </span>
                        )}
                    </div>

                    {/* PO + Delivery Risk Row. PO left, mini-badge right. Badge
                        mirrors the detail-view Delivery palette so operators
                        can scan the list for Overdue / Behind / On Track at a
                        glance without opening each SO. */}
                    <div className="d-flex justify-content-between align-items-start mb-1 gap-2">
                      <div className={`small text-truncate flex-grow-1 ${selectedId === order.sales_order_id ? "text-light opacity-75" : "text-muted"}`}>
                          {(() => {
                            const bits = [];
                            if (order.customer_po) bits.push(`PO: ${order.customer_po}`);
                            if (order.customer_so) bits.push(`SO: ${order.customer_so}`);
                            return bits.length ? bits.join(' · ') : "No PO";
                          })()}
                      </div>
                      {(() => {
                        const riskLevel = computeListRiskLevel(order);
                        if (!riskLevel) return null;
                        const risk = LIST_RISK_PALETTE[riskLevel];
                        return (
                          <span
                            className={`badge ${risk.bg} ${risk.text} d-inline-flex align-items-center`}
                            style={{ fontSize: '0.7rem', padding: '0.2rem 0.45rem', whiteSpace: 'nowrap' }}
                            title={risk.label}
                          >
                            <i className={`bi ${risk.icon} me-1`} style={{ fontSize: '0.8rem' }}></i>
                            {risk.label}
                          </span>
                        );
                      })()}
                    </div>

                    {/* Promised + Est stacked on the left so operators read
                        the commitment first (Promised), then the current
                        projection (Est) directly underneath it. Matches the
                        detail view's top-of-card date block ordering. */}
                    {(order.lead_time || order.promised_date || order.est_complete_date) && (
                      <div className="mb-1">
                        {(order.lead_time || order.promised_date) && (
                          <div className={`small ${selectedId === order.sales_order_id ? "text-light opacity-75" : "text-muted"}`}>
                            {order.promised_date
                              ? `Promised: ${new Date(order.promised_date).toLocaleDateString(undefined, { timeZone: "UTC" })}`
                              : `Promised: ${order.lead_time}`}
                          </div>
                        )}
                        {order.target_completion_date && (
                          <div className="small fw-bold text-info"
                            title="Internal target — production is scheduled to this date">
                            🎯 Target: {new Date(order.target_completion_date).toLocaleDateString(undefined, { timeZone: "UTC" })}
                          </div>
                        )}
                        {order.est_complete_date && (
                          <div className={`small fw-bold ${selectedId === order.sales_order_id ? "text-warning-emphasis" : "text-warning"}`}>
                            Est: {new Date(order.est_complete_date + "T00:00:00").toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Lifecycle Component - Passes data regardless of selection */}
                    <LifecycleBadge
                        stage={order.lifecycle_stage}
                        percent={order.percent_shipped}
                        productionProgress={order.production_progress_pct}
                        totalShipped={order.total_shipped}
                        totalOrdered={order.total_ordered}
                    />
                </button>
              ))
            )}
          </div>
        </div>
        <div className="col-md-9 bg-light" style={{ height: "calc(100vh - 60px)", overflowY: "auto" }}>
          {loadingDetail ? (
            <div className="d-flex h-100 justify-content-center align-items-center">
              <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}></div>
            </div>
          ) : !detailData ? (
            <ActionItemsDashboard apiBase={API_BASE} onSelectOrder={setSelectedId} />
          ) : (
            <div className="p-4">
              <button
                type="button"
                className="btn btn-sm btn-link text-decoration-none px-0 mb-1"
                onClick={() => setSelectedId(null)}
                title="Close this order and show the Action Center"
              >
                <i className="bi bi-arrow-left me-1"></i>Action Center
              </button>
              {renderHeader()}
              {renderDeliveryProgress()}
              <SurplusRackMatchBanner soId={selectedId} refreshKey={detailData} />
              {renderLineItems()}
              {renderProductionSummary()}
              {isTm(detailData.header) && (
                <ErrorBoundary label="The T&M defects panel">
                  <TmDefectsPanel
                    header={detailData.header}
                    defects={tmDefects}
                    jobNums={jobNumsFrom(detailData.related_documents)}
                  />
                </ErrorBoundary>
              )}
              {renderProductionDeepDive()}
              <ErrorBoundary>
                <CutSheetSwitchModal
                  show={switchSheetTaskId != null}
                  taskId={switchSheetTaskId}
                  onHide={() => setSwitchSheetTaskId(null)}
                  onSwitched={() => setDetailTick((t) => t + 1)}
                />
              </ErrorBoundary>
              {renderDocuments()}
              {renderContactModal()}
            </div>
          )}
        </div>
      </div>

      {/* Surplus Allocation Modal — opens when the user clicks "Allocate" on an
          Overtracked line item. POSTs the allocation immediately using backend
          auto-resolution of job_id (no new job creation required). */}
      <SurplusAllocationModal
        show={showSurplusModal}
        onClose={() => { setShowSurplusModal(false); setSurplusModalContext(null); }}
        context={surplusModalContext}
        currentSoId={selectedId}
        sourceCustomerId={detailData?.header?.customer_id || null}
        customerList={customerList}
        onConfirm={async (newAllocations) => {
          if (!newAllocations || newAllocations.length === 0) return;
          const soId = parseInt(selectedId);
          const payload = newAllocations.map(a => ({
            job_id: null,           // Backend auto-resolves from source_so_id + part_id
            source_so_id: soId,
            part_id: a.part_id,
            source_so_line_id: a.source_so_line_id != null ? a.source_so_line_id : null,
            target_so_id: a.target_so_id,
            target_so_line_id: a.target_so_line_id,
            quantity: a.quantity
          }));
          try {
            const res = await fetch(`${API_BASE}/surplus_allocations`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ allocations: payload })
            });
            if (!res.ok) {
              const errText = await res.text();
              alert(`Failed to save allocation: ${errText}`);
              return;
            }
            await res.json();
            // Refresh SO detail to show updated qty_in_production / Overtracked state
            const truthRes = await fetch(`${API_BASE}/sales_orders/${selectedId}/truth`);
            if (truthRes.ok) {
              const truth = await truthRes.json();
              const jobs = truth.related_documents?.jobs || [];
              const soCustomerId = truth.header?.customer_id;
              const custParam = soCustomerId ? `?customer_id=${soCustomerId}` : '';
              const jobProgressPromises = jobs.map((j) =>
                fetch(`${API_BASE}/jobs/${j.job_id}/progress${custParam}`).then((r) => (r.ok ? r.json() : null))
              );
              const jobProgressResults = await Promise.all(jobProgressPromises);
              const jobSourceMap3 = {};
              jobs.forEach((j) => { jobSourceMap3[j.job_id] = j; });
              setDetailData({
                ...truth,
                production_data: jobProgressResults.filter((j) => j !== null).map((j) => ({
                  ...j,
                  master_job_num: jobSourceMap3[j.job_id]?.master_job_num ?? null,
                  _source: jobSourceMap3[j.job_id]?.source || "direct",
                  _source_so_id: jobSourceMap3[j.job_id]?.source_so_id || null,
                  _source_so_number: jobSourceMap3[j.job_id]?.source_so_number || null,
                })),
              });
            }
          } catch (err) {
            console.error("Error creating surplus allocations:", err);
            alert(`Error: ${String(err)}`);
          }
        }}
      />

      {/* Time & Materials: Approve T&M Pricing + Reopen (mounted only for a T&M order). */}
      {isTm(detailData?.header) && (
        <ErrorBoundary
          label="The T&M pricing window"
          onError={() => { setTmPricingOpen(false); setTmReopenOpen(false); refreshAfterTm(); }}
        >
          <TmPricingModal
            show={tmPricingOpen}
            salesOrderId={detailData.header.sales_order_id}
            orderNumber={detailData.header.order_number}
            soLines={flattenTruthLines(detailData.lines || [])}
            onClose={closeTmWindow(setTmPricingOpen)}
            onApproved={refreshAfterTm}
          />
          <TmReopenDialog
            show={tmReopenOpen}
            salesOrderId={detailData.header.sales_order_id}
            orderNumber={detailData.header.order_number}
            onClose={closeTmWindow(setTmReopenOpen)}
            onReopened={() => { setTmReopenOpen(false); refreshAfterTm(); }}
          />
        </ErrorBoundary>
      )}

      {/* SO Email History Modal */}
      <SOEmailHistoryModal
        show={showEmailHistory}
        salesOrderId={selectedId}
        apiBase={API_BASE}
        onClose={() => setShowEmailHistory(false)}
      />

      {/* Catalog inspector — opens whenever a clickable product_id or assembly
          name anywhere on the page (Line Items, Tasks, Defects, Shipments) is
          clicked. Parts, purchased components and assemblies all land here.
          The plan card owns its own instance for its internal click sites. */}
      {inspectedItem && (
        <CatalogItemDetailModal
          apiBase={API_BASE}
          item={inspectedItem}
          onClose={() => setInspectedItem(null)}
        />
      )}
    </div>
  );
}